// Auto-generated from en.json - DO NOT EDIT DIRECTLY
// Edit src/assets/i18n/en.json instead, then run: npm run i18n:sync
// Or let the build watch mode auto-sync on changes.

import type { BaseTranslation } from '../i18n-types';

const en = {
	meals: {
		breakfast: 'Breakfast',
		lunch: 'Lunch',
		dinner: 'Dinner',
		supper: 'Supper',
		snack: 'Snack',
		snackNumbered: 'Snack {number:number}',
	},
	editWeighIn: {
		title: 'Log Weight',
		skinfolds: {
			suprailiac: 'Suprailiac',
			umbilical: 'Umbilical',
			thigh: 'Thigh',
			chest: 'Chest',
			tricep: 'Tricep',
		},
		validation: {
			weight: 'Please state your current weight.',
			skinfolds: 'Measure your skinfolds 3-5x at each site and we\'ll calculate the average.',
		},
		labels: {
			bodyWeight: 'Body Weight',
			bodyFat: 'Body Fat',
			skinfolds: 'Skinfolds',
			waist: 'Waist',
		},
		notes: {
			bodyFatTracking: 'Enable the method(s) you would like to use to track body fat.',
			logWeightInstructions: 'Log your weight each morning before breakfast and after going to the bathroom.',
			measureSkinfolds: 'Measure your skinfolds 3–5 times at each site, and we\'ll calculate your average.',
			measureWaist: 'Measure your waist before breakfast and after going to the bathroom. Exhale fully, flex your abs, and wrap the tape flat around your belly button with consistent tightness. For best results, use a self-tensioning tape like the AccuFitness MyoTape.',
		},
		buttons: {
			save: 'Save',
		},
		warnings: {
			weightNotMeasured: 'It\'s been {days} days since your last weigh-in. Try to get in the habit of weighing yourself daily to improve adherence and tracking.',
			weightNotMeasuredWeeks: 'It\'s been {weeks} weeks since your last weigh-in. Try to get in the habit of weighing yourself daily to improve adherence and tracking.',
			weightNotMeasuredLongTime: 'Try to get in the habit of weighing yourself daily to improve adherence and tracking.',
			bodyFatNotMeasured: 'It\'s been {days} days since you measured your skinfolds or waist. For best results, measure them every {frequency} days.',
			bodyFatNotMeasuredWeeks: 'It\'s been {weeks} weeks since you measured your skinfolds or waist. For best results, measure them every {frequency} days.',
			bodyFatNotMeasuredLongTime: 'It\'s been a while since you measured your skinfolds or waist. For best results, measure them every {frequency} days.',
		},
		info: {
			futureWeighIn: 'You can\'t enter weights for dates in the future.',
			neverMeasuredBodyFat: 'You should measure your body fat composition every {frequency} days. Choose which methods you want to use. Calipers are more accurate, but tape measures are easier.',
			bodyFatNeededForProgressCheck: 'Your progress check needs a few more skinfold or waist measurements to judge your trend. Measuring today helps.',
			bodyFatCooldownTomorrow: 'You can perform your skinfold or waist measurement again tomorrow.',
			bodyFatCooldownDays: 'You can perform your skinfold or waist measurement again in {days} days on {date}.',
		},
		links: {
			howToMeasureSkinfolds: 'How to measure skinfolds?',
		},
	},
	weeklyCheckup: {
		dailyAvgWithDelta: '{avg} kcal ({sign}{diff})',
		loading: {
			processingData: 'Processing data. Please wait...',
			finishingCheckup: 'Finishing weekly checkup, please wait...',
		},
		errors: {
			couldNotProcess: 'We couldn\'t update your plan right now. Please try again, or contact support if it keeps happening.',
			progressCheckFailed: 'We couldn\'t work out how your progress is going right now, so we can\'t run your check-up. Please try again later, or contact support if it keeps happening.',
		},
		bodyfatEstimation: {
			timeForEstimate: 'It has been a while since we last estimated your body fat percentage, so let\'s do it now.',
			bestEstimate: 'What is the best estimate of your body fat percentage that you can obtain (you only need one)?',
			skipOk: 'It\'s OK to skip this: we\'ll then keep using the last estimate.',
		},
		nutritionLogging: {
			nextSection: 'Next, we’ll review your calorie intake from the past week to update your program for the coming week.',
			deselectIncomplete: 'Deselect the days that look incomplete or partially logged and we\'ll exclude them from our calculations.',
		},
		calorieBaseline: {
			beforeAdjust: 'Before we adjust your calorie intake for the next week, we need to get an accurate idea of your current energy intake.',
			whichOption: 'Which of these options best reflects your average calorie intake for the last week?',
			logged: 'Logged',
			loggedNote: 'This is the average logged calories per day over the previous week, excluding any days you marked as incomplete.',
			target: 'Target',
			targetNote: 'This is your average recommended target calorie intake per day in the previous week.',
			custom: 'Custom',
			customNote: 'If you consumed a different number of calories than the above options, please enter it here.',
		},
		programUpdate: {
			basedOnProgress: 'Based on your progress data, we recommend the following updates to your program:',
			progressOnTrack: 'Your progress is on track! No adjustments are needed at this time.',
			notEnoughData: 'Not enough progress data is available yet. No changes are recommended at this point. Continue logging your weigh-ins and body measurements so we can track your progress.',
			notEnoughDataCompleted: 'Not enough progress data was available at the time of this checkup. No changes were recommended.',
			undeterminedMeasureMoreOften: 'We need more measurements to judge your progress, so no changes are recommended this week. Keep logging your weight every day, and add skinfold or waist measurements when the weigh-in screen asks for them.',
			undeterminedBodyFatTrendUnclear: 'Your body-fat trend is not clear yet, so no changes are recommended this week. Add a few more skinfold or waist measurements over the next days. The weigh-in screen will ask for them.',
			undeterminedDataErratic: 'Your measurements are fluctuating a lot, so no clear trend can be found and no changes are recommended this week. Try to measure under the same conditions each time.',
			undeterminedWeightTooFlat: 'Your last few weight readings were exactly the same, so no changes are recommended this week. Please check that your scale is working properly.',
			atMinimum: 'Your progress suggests eating a little less, but your daily calorie target is already at the lowest the app sets for your body weight. It can\'t go any lower, so no changes are made.',
			atMinimumCompleted: 'Progress suggested eating less, but the daily calorie target was already at the lowest the app sets for your body weight, so no changes were made.',
			lowestTarget: 'This is the lowest daily calorie target the app sets for your body weight.',
			changeTooSmall: 'Based on your progress data, your daily calorie target would barely change. The difference is too small to matter, so no changes are made.',
			changeTooSmallCompleted: 'The recommended calorie change was too small to matter, so no changes were made.',
		},
		bfMethods: {
			calipers: 'Calipers',
			tape: 'Tape',
			visual: 'Visual',
			unknown: 'Unknown Method',
		},
		headers: {
			bodyfatEstimation: 'Body fat Estimation',
			programUpdate: 'Program Update',
			checkupStatus: 'Checkup Status',
		},
		descriptions: {
			intro: 'To keep your program optimal and up to date, we\'ll now review your progress from the past week and make updates if needed.',
			futureCheckup: 'This weekly checkup is scheduled for a future date. Please wait until the scheduled date to start the checkup.',
			outdatedCheckup: 'This checkup is outdated and can no longer be completed. Please complete your current week\'s checkup instead.',
			weightTrendingWell: 'Your weight and body measurements are trending in the right direction with a good pace. Keep it up, good work!',
			weightNotEnoughData: 'We don\'t have enough data to determine if your weight and body measurements are trending in the right direction. Please log more weigh-ins and body measurements.',
			noWorkoutsLoggedLastWeek: 'In the last week, you did not log any workouts.',
			workoutProgressionSummary: 'In the last week, you progressed in exercises for {progressions:number} {{time|times}} and regressed for {regressions:number} {{time|times}}.',
		},
		charts: {
			weightTrend: 'Weight Trend',
			skinfoldTrend: 'Skinfold Trend',
			waistTrend: 'Waist Trend',
			exerciseProgressions: 'Exercise Progress',
		},
		tooltips: {
			weightTrend: 'Daily weight measurements with their estimated trend line over the last 7 days.',
			skinfoldTrend: 'Daily skinfold measurements with their estimated trend line over the last 7 days.',
			waistTrend: 'Daily waist measurements with their estimated trend line over the last 7 days.',
			exerciseProgressions: 'Number of exercises that progressed per workout over the last 7 days.',
		},
		labels: {
			estimationMethod: 'Estimation method:',
			estimatedBodyfat: 'Estimated body fat:',
			completionStatus: 'Completion status:',
		},
		buttons: {
			start: 'Start',
			decline: 'Decline',
			accept: 'Accept',
		},
		ownTarget: {
			nothingToChange: 'That is the target the app recommends, so there is nothing to change.',
			completedOwn: 'You set your own target. The app recommended {kcal} kcal.',
			completedCoach: 'Your coach set this target. The app recommended {kcal} kcal.',
		},
		step: 'Step {current} of {total}',
		steps: {
			intro: 'Weekly Checkup',
			progressReview: 'Progress Review',
			bodyfatEstimation: 'Body Fat Estimation',
			nutritionLogging: 'Nutrition Logging',
			calorieBaseline: 'Calorie Baseline',
			programUpdate: 'Program Update',
		},
		completionActions: {
			accepted: 'Adjustments Accepted',
			declined: 'Adjustments Declined',
			finished: 'Completed',
		},
		validation: {
			selectOption: 'Please select one of the options.',
			customCalorieIntake: 'Please enter a custom calorie intake value.',
			unknownField: 'Unknown field',
		},
		finalChoice: {
			acceptApp: 'Accept the New Target',
			acceptAppHelp: 'The app\'s recommendation, worked out from your progress.',
			keepAppTarget: 'Keep the App\'s Target',
			keepAppTargetHelp: 'You are on track, so your target stays as it is.',
			setOwn: 'Set My Own Target',
			setOwnHelp: 'Choose a number yourself, in kcal or as a change in percent.',
			keepCurrent: 'Keep My Current Target',
			keepCurrentHelp: 'Nothing in your plan changes this week.',
		},
	},
	nutrientList: {
		logged: 'Logged',
		targets: 'Targets',
		nutrients: 'Nutrients',
		food: 'Item',
		meal: 'Meal',
		day: 'Day',
		total: 'Total',
		left: 'Left',
		protein: 'Protein',
		carbs: 'Carbs',
		fat: 'Fat',
		fiber: 'Fiber',
		cholesterol: 'Cholesterol',
		short: {
			protein: 'Pro',
			carbs: 'Cho',
			fat: 'Fat',
			fiber: 'Fib',
		},
		oneLetter: {
			protein: 'P',
			carbs: 'C',
			fat: 'F',
			fiber: 'Fib',
		},
	},
	common: {
		reconnecting: 'Reconnecting...',
		offlineBadge: 'Offline',
		options: {
			yes: 'Yes',
			no: 'No',
		},
		ui: {
			recommended: 'Recommended',
			default: 'Default',
		},
		states: {
			skipped: 'Skipped',
		},
		messages: {
			backOnline: 'You\'re back online.',
			imageQueuedOffline: 'Photo saved on this device. It will upload automatically when you\'re back online.',
			scanAppCapacityReached: 'The app has reached its scanning limit for today. Please try again tomorrow.',
		},
		toast: {
			dontShowAgain: 'Never show',
		},
		words: {
			or: 'or',
			and: 'and',
		},
		measureNames: {
			portion: 'portion',
			piece: 'piece',
			slice: 'slice',
			whole: 'whole',
			prepared: 'serving',
			large: 'large',
			medium: 'medium',
			small: 'small',
			can: 'can',
			bottle: 'bottle',
			quart: 'quart',
			pint: 'pint',
			cup: 'cup',
			tbsp: 'tbsp',
			tablespoon: 'tablespoon',
			tsp: 'tsp',
			teaspoon: 'teaspoon',
		},
		measureUnits: {
			descriptions: {
				none: 'none',
				genericNumber: 'Generic number',
				repetitions: 'Repetitions',
				sets: 'Sets',
				pairs: 'Pairs',
				plates: 'Plates',
				percent: 'Percent',
				millimeters: 'millimeters',
				centimeters: 'centimeters',
				feetAndInches: 'feet and inches',
				inches: 'inches',
				kilograms: 'kilograms',
				pounds: 'pounds',
				micrograms: 'micrograms',
				milligrams: 'milligrams',
				grams: 'grams',
				ounces: 'ounces',
				stonesAndPounds: 'stones and pounds',
				kilocalories: 'kilocalories',
				kilojoules: 'kilojoules',
				internationalUnits: 'international units',
				milliliters: 'milliliters',
				fluidOunces: 'fluid ounces',
				quarts: 'quarts',
				pints: 'pints',
				cups: 'cups',
				tablespoons: 'tablespoons',
				teaspoons: 'teaspoons',
			},
		},
		pickerTitles: {
			servingSize: 'Serving Size',
			selectUnit: 'Select Unit',
			timeFormat: 'Time Format',
		},
		scanModels: {
			allModels: 'All Models',
			costEstimate: '{displayName} (~${cost}/1k)',
		},
		errors: {
			unknownField: 'Unknown field',
			generic: 'Something went wrong. Please try again.',
			tooManyAttempts: 'Too many attempts. Please wait a while and try again.',
		},
		offTrack: {
			tooMuchWeightLoss: {
				cutting: {
					summary: 'Your weight is dropping faster than expected.',
					action: 'We need to adjust your nutrition plan to preserve muscle while losing fat.',
				},
				bulking: {
					summary: 'You\'re losing weight when you should be gaining it.',
					action: 'We need to adjust your nutrition plan to support muscle growth.',
				},
			},
			tooMuchFatGain: {
				cutting: {
					summary: 'You\'re gaining body fat when you should be losing it.',
					action: 'We need to adjust your nutrition plan to get your fat loss back on track.',
				},
				bulking: {
					summary: 'Your body fat measurements are trending up faster than expected.',
					action: 'We need to adjust your nutrition plan to prevent excessive fat gain.',
				},
			},
			notEnoughFatLoss: {
				cutting: {
					summary: 'Your body fat measurements are not decreasing as expected.',
					action: 'We need to adjust your nutrition plan to help you lose fat.',
				},
			},
			notEnoughWeightGain: {
				bulking: {
					summary: 'Your weight\'s not increasing as expected.',
					action: 'We need to adjust your nutrition plan to help you gain lean mass.',
				},
			},
			generic: {
				summary: 'You\'ve missed some workouts or volume goals. Stay consistent to keep progressing.',
			},
		},
		buttons: {
			save: 'Save',
			select: 'Select',
			cancel: 'Cancel',
			delete: 'Delete',
			ok: 'OK',
			today: 'Today',
			yes: 'Yes',
			accept: 'Accept',
			no: 'No',
			back: 'Back',
			next: 'Next',
			edit: 'Edit',
			remove: 'Remove',
			report: 'Report',
			dismiss: 'Dismiss',
			ignore: 'Ignore',
			learnMore: 'Learn more',
			discard: 'Discard',
			optimize: 'Optimize',
			saveAsNew: 'Save as new',
			split: 'Split',
			addPlates: 'Add Plates',
			cleanUp: 'Clean Up',
			finish: 'Finish',
			skip: 'Skip',
			restoreTask: 'Restore Task',
			autoTune: 'Auto-Tune',
			uncacheFoods: 'Uncache Foods',
			testSurvey: 'Test Survey',
			apply: 'Apply',
			log: 'Log',
			undo: 'Undo',
			keep: 'Keep',
			saving: 'Saving...',
			tryAgain: 'Try Again',
			continue: 'Continue',
			confirm: 'Confirm',
		},
		weekdays: {
			monday: 'Monday',
			tuesday: 'Tuesday',
			wednesday: 'Wednesday',
			thursday: 'Thursday',
			friday: 'Friday',
			saturday: 'Saturday',
			sunday: 'Sunday',
		},
		weekdayInitials: {
			monday: 'M',
			tuesday: 'T',
			wednesday: 'W',
			thursday: 'T',
			friday: 'F',
			saturday: 'S',
			sunday: 'S',
		},
		placeholders: {
			enterFoodName: 'Enter food name',
			selectMeasure: 'Select measure...',
			day: 'DD',
			month: 'MM',
			year: 'YYYY',
			reps: 'Reps',
			enterText: 'Enter text',
			select: 'Select...',
			selectAnOption: 'Select an option',
		},
		measurements: {
			weight: 'Weight',
			waist: 'Waist',
			skinfold: 'Skinfold',
			skinfolds: 'Skinfolds',
		},
		labelStyles: {
			us: 'US',
			eu: 'EU',
		},
		programTypes: {
			intensified: 'Intensified',
			dup: 'DUP',
			tup: 'TUP',
		},
		loading: {
			pleaseWait: 'Please wait...',
			sendingReport: 'Sending report...',
			registeringPayment: 'Registering the payment...',
			signingIn: 'Signing in...',
			loggingInWithGoogle: 'Logging in with Google...',
			loggingInWithFacebook: 'Logging in with Facebook...',
			loggingInWithApple: 'Signing In With Apple...',
		},
		relativeDay: {
			today: 'today',
			yesterday: 'yesterday',
			tomorrow: 'tomorrow',
		},
	},
	navigation: {
		home: 'Home',
		nutrition: 'Nutrition',
		settings: 'Settings',
		training: 'Training',
	},
	workouts: {
		restTimer: {
			title: 'Rest Timer',
			defaultLabel: 'Default Rest',
			exerciseOverrideTitle: 'Rest Timer Override',
			adjustMinus: '-15s',
			adjustPlus: '+15s',
			skip: 'Skip',
			done: 'Done!',
			rollerLabel: 'REST',
			notificationBody: 'Rest over',
			autoStartLabel: 'Start Automatically',
			infoNative: 'The rest timer counts down between sets of the same exercise and only runs in the installed mobile app (iOS or Android). With Start Automatically on, it starts when you log a set. Otherwise, start it with the Start Rest Timer button under the first set. Settings are available on every device.',
			infoOverride: 'Applies only in the installed mobile app (iOS or Android).',
		},
		detail: {
			weightExplanations: {
				addedWeightExplanation: 'This is the total <i>added</i> weight, <i>without</i> your own bodyweight.',
				totalWeightExplanation: 'This is the <i>total</i> weight (including the bar weight).',
			},
			buttons: {
				yesAgree: 'Yes, I agree.',
				noKeepIntensity: 'No, let me keep this intensity for now.',
				noKeepExercise: 'No, let me keep this exercise for now.',
				noKeepProgram: 'No, let me keep this program for now.',
				decideLater: 'I will decide later.',
			},
			resolve: 'Resolve',
			workoutComplete: 'Great work! You\'ve completed your workout.',
			workoutCompleteFirstTest: 'Test workout done! Next workout will autoregulate your rep targets.',
			workoutCompleteSecondTest: 'Starting weights and rep targets set! Next workout: progressive overload.',
			plan: 'Plan',
			skipped: 'Skipped',
			setOrdinal: 'Set {number}',
			setInstructionsToggle: 'Set instructions',
			collapseSet: 'Collapse set',
			repsLabel: 'Reps',
			bwHelpText: 'Enter only added weight. Enter 0 if you used bodyweight (no added weight).',
			myoReps: 'Myo-reps',
			lastWorkout: 'Last Workout',
			lastWorkoutSameIntensity: 'Showing last workout with same intensity level.',
			selectEquipmentHere: 'Select equipment here.',
			equipmentWarning: {
				title: 'Improve Your Equipment',
				messagePart1: 'Your selected equipment doesn\'t support the weight you have chosen ({error}). ',
				messageLink: 'Update your equipment',
				messagePart2: ' to ensure accurate progression suggestions.',
				mobileMessage: 'Your selected equipment doesn\'t support the weight you have chosen ({error}). Update your equipment to ensure accurate progression suggestions.',
				button: 'Update Equipment',
			},
			equipmentFloor: {
				hintEmptyBar: 'Empty bar · your target is {target}',
				hintLightestFixed: 'Your lightest weight · your target is {target}',
				hintLightestStackPlate: 'Lightest plate on this machine · your target is {target}',
				hintLightestSetting: 'Lightest setting · your target is {target}',
				title: 'Why {floor}?',
				bodyEmptyBar: 'Your plan\'s target for this exercise is {target}. On a barbell, the weight you enter is the total, including the bar. An empty bar already weighs {floor}, so that is the lightest this equipment can go. Meant {target}? Pick lighter equipment. Training at {floor} on purpose? That\'s fine. Your next target will be worked out from what you log here.',
				bodyOther: 'Your plan\'s target for this exercise is {target}. This equipment can\'t go lighter than {floor}. Meant {target}? Pick lighter equipment. Training at {floor} on purpose? That\'s fine. Your next target will be worked out from what you log here.',
				lighterEmptyBar: 'Empty bar · can\'t go lighter',
				lighterLightestFixed: 'Your lightest weight · can\'t go lighter',
				lighterLightestStackPlate: 'Lightest plate on this machine · can\'t go lighter',
				lighterLightestSetting: 'Lightest setting · can\'t go lighter',
				lighterBodyEmptyBar: 'This set should be lighter: about {wanted}. On a barbell, the weight you enter is the total, including the bar. An empty bar already weighs {floor}, so that is the lightest this equipment can go. Want it lighter? Pick lighter equipment, then enter about {wanted}. Staying on the bar? Do the reps at {floor}.',
				lighterBodyOther: 'This set should be lighter: about {wanted}. This equipment can\'t go lighter than {floor}. Want it lighter? Pick lighter equipment, then enter about {wanted}. Staying with this equipment? Do the reps at {floor}.',
				button: 'Change Equipment',
			},
			badges: {
				myoReps: 'Myo-Reps',
				strengthTest: 'Strength Test',
				repTest: 'Rep Test',
				myoRepsInfo: 'Time-saving method with activation set and mini-sets with short rest.',
				strengthTestInfo: 'Find the maximum weight you can only lift for 1-5 reps. Keep adding weight intuitively until you can only perform 1-5 reps.',
				repTestInfo: 'Perform as many reps as you can with the prescribed weight. This number will become your rep target. This method autoregulates your training volume for each muscle group individually based on its muscle fiber type composition, capillary density and other factors that make it stronger in certain rep ranges.',
				tempSwap: 'Temporary',
				tempSwapInfo: 'This exercise is a temporary replacement for this workout only. Your training program is not affected.',
				bodyweight: 'Bodyweight',
				bodyweightInfo: 'This exercise uses your bodyweight as part of the load.',
			},
			superset: 'Combo Set',
			units: {
				lb: 'Lb',
				kg: 'Kg',
			},
			firstWorkoutTestExplainer: 'Test workout to customize your program',
			pendingWorkoutWarning: 'You have an unfinished workout from',
			pendingWorkoutExplanation: 'You can go back and finish it or start this workout which will auto-complete it.',
			workoutChangedInfo: 'Any changes to this workout will be saved once you complete at least one set.',
			coachChanged: {
				title: 'Your coach changed this exercise.',
				detail: '{date}, {name}: {summary}',
			},
			skipRemaining: 'Skip Remaining',
			skipRemainingConfirm: 'Are you sure you want to skip all remaining sets and exercises?',
			lastAttempt: 'Last attempt, {days} days ago:',
			equipment: {
				stack: 'Stack: ',
				fixed: 'Fixed: ',
				bar: 'Bar: ',
				platesEachSide: 'Plates (each side)',
				micro: 'Micro',
				plates: 'Plates',
				notConfigured: '(not configured)',
				automatic: 'Automatic',
				inferredStep: 'inferred step: ~{step}',
				copiedFromOtherUnit: 'Using your {name} for this exercise. It is the same machine, now shown in the unit you picked.',
				chooseDifferentEquipment: 'Choose Equipment',
				chooseEquipmentLabel: 'Choose equipment',
			},
			setInstructions: {
				nonRMTestRetry: 'Rest a few minutes. Consult the [instructions](@help:workout.strength-test-failed) and try again.',
				nonRMTest: 'Find the maximum weight you can lift for the suggested number of reps. [Learn more](@help:workout.strength-test)',
				rmTestRetry: 'Rest a few minutes. Consult the [instructions](@help:workout.RM-strength-test-failed) and try again.',
				rmTest: 'Find your 1-5RM. [Learn more](@help:workout.RM-strength-test)',
				rmTestFollow: 'Do as many reps as you can with good form. [Learn more](@help:workout.RM-test_follow)',
				repTest: 'Find your rep target for this exercise. [Learn more](@help:workout.rep-test)',
				regular: 'Perform as many reps as possible with the given weight.',
				followup: 'Do as many reps as you can with good form.',
				dropset: 'Do as many reps as you can with good form. [Learn more](@help:workout.dropset)',
				dropsetEccentric: 'If practical, try doing just the eccentric phase (the downward motion) of the exercise, with no added load, for as many reps as you can control. [Learn more](@help:workout.dropset)',
				dropsetReduce: 'Reduce the weight as shown. Do as many reps as you can with good form. [Learn more](@help:workout.dropset)',
				deload: 'Do only 3-5 **explosive** reps with the reduced load. [Learn more](@help:workout.deload)',
				deloadAtFloor: 'Do only 3-5 **explosive** reps at this weight. [Learn more](@help:workout.deload)',
			},
			setProgress: {
				lastSession: 'Last session',
				lastMarker: 'last · {reps:number}',
				repsRangePlaceholder: '{min:number}–{max:number}',
				progressedTitle: 'You progressed!',
				targetHitTitle: 'Rep target hit!',
				setLoggedTitle: 'Set logged',
				repsAtWeight: '{reps:number} {{rep|reps}} at {weight}',
				overLastTime: '+{count:number} over last time',
				nextSessionAimFor: 'Next session: aim for {reps:number}+.',
				lastTimeYouDid: 'Last time you did {reps:number}.',
				lastTimeLabel: 'Last time',
				todayLabel: 'Today',
				startRestTimer: 'Start Rest Timer · {duration}',
				outcomeTargetHit: 'Target hit',
				lastShort: 'Last {value}',
				targetLabel: 'Target',
				overLastTimeShort: '+{count:number} {{rep|reps}} (vs. last time)',
				editSet: 'Edit Set',
				restTimer: 'Rest Timer · {duration}',
				outcomeSame: 'Same as last time',
				outcomeFewer: 'Fewer reps',
				loggedAtLabel: 'Logged at',
				nextSessionLabel: 'Next session',
				repsOrMore: '{reps:number}+',
				targetRangeReps: '{floor:number}–{target:number} reps',
				targetReps: '{target:number} {{rep|reps}}',
			},
			summary: {
				title: 'Workout complete',
				setsCount: '{count:number} {{set|sets}}',
				minutes: '{minutes:number} min',
				hoursMinutes: '{hours:number} h {minutes} min',
				recordsPart: '{count:number} personal {{record|records}}',
				progressedPart: '{count:number} {{exercise|exercises}} progressed',
				and: 'and',
				personalRecord: 'Personal record',
			},
			finishedExercise: {
				moreRepsVsLastTime: '+{count:number} {{rep|reps}} vs last time',
				fewerRepsVsLastTime: '−{count:number} {{rep|reps}} vs last time',
				sameAsLastTime: 'Same as last time',
				lastWorkoutLine: 'Last workout: {sets}',
				nextTargetLine: 'Next target: aim for {reps:number}+ reps on Set 1',
			},
			exerciseHeader: {
				set: 'set',
				sets: 'sets',
				skipped: 'skipped',
				highRep: 'high-rep; ',
				heavy: 'heavy; ',
				medium: 'medium; ',
			},
		},
		buttons: {
			description: 'Description',
			instructions: 'Instructions',
			history: 'History',
			stats: 'Stats',
			settings: 'Settings',
			skipToday: 'Skip Today',
			changeExercise: 'Change Exercise',
			resetProgress: 'Reset Progress',
			logSet: 'Log Set',
			completeSet: 'Complete Set',
			saveSet: 'Save Set',
			logShort: 'Log',
			saveShort: 'Save',
			nextDay: 'Next Day',
			close: 'Close',
			yesAgree: 'Yes, I agree',
			noDisagree: 'No, I don\'t agree',
			yesCorrect: 'Yes, it\'s correct',
			oopsLetMeChange: 'Oops, let me change that.',
			addNote: 'Add Note',
			setNote: 'Set Note',
		},
		alerts: {
			youChangedWeight: 'You changed the weight?',
			weightEstimateWrong: 'Our app estimates the ideal load for each of your working sets. Why do you feel we got it wrong?',
			agreeWithDeload: 'Do you agree with a reactive deload?',
			agreeWithUpgrade: 'Do you agree with a training program upgrade?',
			skipExercise: 'Skip exercise...',
			skipRemainingSets: 'Do you want to skip the rest of the sets for this exercise in this workout?',
			skipThisExercise: 'Do you want to skip this exercise in this workout?',
			isThisCorrect: 'Is this correct?',
			notesForSet: 'Notes for this set',
			notesForPreviousWorkouts: 'Notes for previous workouts are under More > Stats and Notes.',
			switchType: 'Change Exercise',
			switchTypeMessage: 'Would you like to replace this exercise permanently in your program, or just for this session?',
			thisSessionOnly: 'This Session Only',
			permanently: 'Permanently',
			resetProgress: 'Reset progress',
			resetProgressConfirm: 'Are you sure you want to reset the current cycle for this exercise? We\'ll have to test your strength in this exercise. Your previous PRs will be preserved in the "Progress" section.',
			switchExerciseResetWarning: 'Your completed sets stay with this exercise, and the new exercise starts at set 1. Do you want to continue?',
			exercisesSkipped: 'Some exercises were skipped due to an application malfunction.\n({exercises})',
			weightTooHeavy: 'The weight you\'re recommending for me is way too heavy.',
			weightTooLight: 'The weight you\'re recommending for me is way too light.',
			closestWeight: 'My chosen weight is the closest I can get to what you\'re telling me to use.',
			recommendDeload: 'Reactive deload recommended for this exercise. You don\'t seem to have recovered yet.',
			plateau: {
				exerciseChanged: 'We changed {name} to this exercise.',
				exerciseWillChange: 'This exercise will be changed in the next session.',
				intensityChangeTitle: 'Do you agree with an intensity change for {name}?',
				intensityChangeMessage: 'We think you have reached a plateau with the current exercise intensity. Therefore, we recommend retesting you for a different intensity to ensure optimal progression.',
				exerciseChangeTitle: 'Do you agree to change {name} to a different exercise?',
				exerciseChangeMessage: 'We recommend replacing this exercise at this point.',
				planUpgradeMessage: 'Congratulations! Your recent progress indicates that you\'re ready to take on a more advanced workout program.',
				retestThis: 'We\'ll redetermine your rep target in this session.',
				retestNext: 'We\'ll redetermine your rep target next session.',
				switchToTUPThis: 'Exercise will switch to Triple Undulating Periodization in this session.',
				switchToTUPNext: 'Exercise will switch to Triple Undulating Periodization in the next session.',
				switchToDUPThis: 'Exercise will switch to Dual Undulating Periodization in this session.',
				switchToDUPNext: 'Exercise will switch to Dual Undulating Periodization in the next session.',
				planUpgradeConfirm: 'We\'ll upgrade your training program to a more advanced level after this session.',
			},
			repCeiling: {
				title: 'You\'ve reached 30 reps',
				switchMessage: 'More than 30 reps no longer builds muscle well, and the next weight on your equipment is too big a jump. Switch to another exercise from the next workout?',
				yes: 'Yes',
				noKeep: 'No, Keep 30 Reps',
			},
			unusualSet: '{setStatText} for {exerciseName} seems unusual compared to your past entries.',
			unusualSetRepTest: '{setStatText} for repetition test of {exerciseName} seems unusual compared to your past entries.',
			viewOnly: {
				futureWorkoutMessage: 'This workout is scheduled for a future date. Please complete and log workouts on their planned day, ideally in the gym.',
				previousWorkoutMessage: 'This is a previous workout. Feel free to make corrections.',
				templateMessage: 'This is a workout template from your plan. You\'ll see the specific workout with more details on its scheduled day. Feel free to change exercises in advance here if needed.',
				loggingMessage: 'You should log the workout while you\'re in the gym. That way we can give you the best possible guidance on each following set.',
			},
		},
		status: {
			exerciseNotOptimal: 'This exercise is not optimal for you at this time and will be replaced in the next workout.',
			goodJobProgression: 'Good job! Now we can guide your progression from here.',
			strengthLevelCalculated: 'Good job! Strength level calculated.',
			strengthCalculatedLighterSets: 'Good job! Strength level calculated. The rest of the sets are lighter to assess your strength-endurance.',
			tooManyRepsRetry: 'Rest a few minutes and use a heavier weight to get closer to the target reps.',
			tooManyRepsNextWorkout: 'We\'ll retry this in a future workout with a heavier weight to get closer to the target reps.',
			tooFewRepsRetry: 'Rest a few minutes and use a lighter weight to get closer to the target reps.',
			tooFewRepsNextWorkout: 'We\'ll retry this in a future workout with a lighter weight to get closer to the target reps.',
			repRangeEstablished: 'Excellent! We\'ve established your target rep range.',
			repRangeEstablishedContinue: 'Excellent! We\'ve established your target rep range. In the following sets, you don\'t have to match it. Just do as many reps as you can.',
			strengthRetestNextTooHeavy: 'Since you noted the suggested load is too heavy for you, we\'ll let you retest your strength next time with a different method. Please review the testing instructions. For today, you can keep using the weight you\'ve chosen.',
			strengthRetestNextTooLight: 'Since you noted the suggested load is too light for you, we\'ll let you retest your strength next time with a different method. Please review the testing instructions. For today, you can keep using the weight you\'ve chosen.',
			differentWeightEquipment: 'Since you chose a different weight than suggested, consider updating the equipment for this exercise so we can get it right next time.',
			progressionDeload: 'Didn\'t hit the planned progression. Deloading...',
			progressionDeloadLastSet: 'Didn\'t hit the planned progression. Your target weight will be one step lower next workout.',
			repTargetUpdatedRegular: 'We\'ve updated the rep target for this exercise based on your current progress.',
			repTargetUpdatedHighRep: 'We\'ve updated the rep target for high-rep session in this exercise based on your current progress.',
			repTargetUpdatedHeavy: 'We\'ve updated the rep target for heavy session in this exercise based on your current progress.',
			lastSetNotNeeded: 'The last set is not needed. Skipping it.',
			lowerThanRecommended: 'This weight is lower than the recommended weight of {plannedWeight}. If you have changed the gym or equipment, you can update the equipment so we know what\'s available.',
			immediateProgression: 'You\'re doing great! Let\'s increase the weight right away and see if you can reach the target reps with that.',
			immediateProgressionReps: 'You\'re doing great! Let\'s increase the rep target and see if you can hit it with this weight.',
			maxedOutEquipment: 'Seems like we can\'t increase your target weight with this equipment anymore, so next time we\'ll switch to another exercise. If you can in fact add more weight, please update your available equipment.',
			plateauResolved: 'You have reached a plateau. {message}',
			plateauDetected: 'Your progress has stalled at this weight for more than one session.',
			plateauDismissed: 'Plateau dismissed by user.',
			volumeAutoregulationIncrease: 'Volume autoregulation applied. Your training volume will be increased by 1 set in the next workout based on your fatigue index.',
			volumeAutoregulationDecrease: 'Volume autoregulation applied. Your training volume will be decreased by 1 set in the next workout based on your fatigue index.',
			volumeAutoregulationPending: 'Based on your performance, we recommend adjusting your set volume.',
			strengthTestFollowupDone: 'Good set. Your strength test already updated your progression for this exercise, so keep this weight for the rest of today.',
			repCeilingSwitch: 'You\'ve reached 30 reps and the next weight is too big a jump, so next time we\'ll switch to another exercise.',
			repCeilingSwitchSameEquipment: 'You\'ve reached 30 reps and the next weight is too big a jump, so next time we\'ll switch to another exercise. Every alternative for this exercise uses the same equipment, so consider adding smaller weight steps to your equipment settings.',
			repCeilingHeld: 'You\'ve reached 30 reps. Your rep target stays at 30, and we\'ll ask again next time.',
			dropSet: 'Dropset! Reduce the weight for the following sets.',
			dropSetAtFloor: 'Dropset! This equipment can\'t go lighter, so keep this weight for the following sets.',
			completeProgressionReset: 'To complete the reset, perform the strength test now. If you exit the workout without logging anything for the strength test, the reset will be canceled and you will return to the old values.',
			adjustedTargetWeight: 'It seems you didn\'t perform this exercise for more than a week. Therefore, we\'ll adjust your target weight accordingly.',
			adjustedTargetReps: 'It seems you didn\'t perform this exercise for more than a week. Therefore, we\'ll adjust your target reps accordingly.',
			cappedToRecentPerformance: 'Based on your recent sets, the planned weight looked too heavy for this many reps. We\'ve lowered it to match what you can do right now.',
			deloadedTargetWeight: 'Your target weight is one step lower because of your last deload. This is a normal working set: aim for your rep target.',
			retestRequired: 'It seems you didn\'t perform this exercise for a few weeks. Therefore, we need you to retest this exercise again.',
		},
		instructions: {
			fillInReps: 'Fill in your repetition count for the set.',
			fillInWeight: 'Fill in your lifted weight.',
			fillInWeightAndReps: 'Fill in your lifted weight and repetition count for the set.',
		},
		errors: {
			unableToLoad: 'Unable to load workout',
			unableToLoadMessage: 'The workout cannot be loaded at this time, sorry. Please try again. If the issue persists, please contact support.',
		},
	},
	foods: {
		title: 'Foods',
		addFoods: 'Add Foods',
		searchPlaceholder: 'Search...',
		filtersAndSettings: 'Filters & Settings',
		showFoodValuesBy: 'SHOW FOOD VALUES BY',
		servingSize: 'Serving Size',
		per100g: '100g',
		filters: {
			all: 'All',
			foods: 'Foods',
			recipes: 'Recipes',
			meals: 'Meals',
			favorites: 'Favorites',
			myFoods: 'My Foods',
			loggedBefore: 'Logged Before',
			hideUnverified: 'Hide Unverified',
			hideUnverifiedHint: 'Hides foods other members added and Open Food Facts',
			type: 'Type',
			showOnly: 'Show only',
			source: 'Source',
			removeFilter: 'Remove {filter}',
			reset: 'Reset',
		},
		buttons: {
			createRecipe: 'Create Recipe',
			createFood: 'Create Food',
			addMacros: 'Add Macros',
			new: 'New',
			filters: 'Filters',
			retrySearch: 'Try Again',
			clearFilters: 'Clear Filters',
			searchCorrection: 'Search {phrase}',
			searchTypedInstead: 'Search {phrase} instead',
			removeFromHistory: 'Remove from History',
		},
		badges: {
			recipe: 'Recipe',
			meal: 'Meal',
			locked: 'Locked',
			usedBefore: 'Logged Before',
			liked: 'Liked',
			disliked: 'Disliked',
			community: 'Community',
			openFoodFacts: 'Open Food Facts',
			staple: 'Staple Food',
			noNutritionData: 'No Nutrition Data',
			checkValues: 'Check Values',
			checkValuesInfo: 'The calories listed for this food do not match its protein, fat and carbs, which work out to about {expectedCalories:number} kcal per 100 g. This entry may be wrong.',
		},
		messages: {
			useSearchBar: 'Use the search bar above to find foods.',
			removedFromHistory: 'Removed from your recent foods.',
			nothingMatchesFilters: 'No foods match your filters.',
			clearFiltersOrSearch: 'Clear the filters, or use the search bar above to find foods.',
			clearFiltersOrCreate: 'Clear the filters, or use the + New button to create a new food or recipe.',
			noFoodByBarcode: 'No food found for that barcode. Use the button below to create it.',
			noFoodByBarcodeScanLabel: 'No food found for that barcode.',
			noFoodByBarcodeScanLabelHint: 'Snap a few photos of the packaging and we\'ll create a new food for you!',
			notFoundWhat: 'Couldn\'t find what you\'re looking for?',
			searchConnectionTitle: 'Couldn\'t load search results',
			searchConnectionHint: 'There seems to be a connection problem. Check your internet and try again.',
			offlineShowingLocalFoods: 'You\'re offline, so we can\'t search the food database. Showing staple foods and foods you\'ve eaten recently, both saved on this device.',
			searchUnreachableShowingLocalFoods: 'We couldn\'t reach the food database. Showing staple foods and foods you\'ve eaten recently, both saved on this device.',
			offlineShowingLocalFoodsBarcode: 'You\'re offline, so we can\'t search the food database. Showing only foods you\'ve eaten recently, saved on this device.',
			searchUnreachableShowingLocalFoodsBarcode: 'We couldn\'t reach the food database. Showing only foods you\'ve eaten recently, saved on this device.',
			searchFailedTitle: 'Couldn\'t load foods',
			searchFailedHint: 'Something went wrong while loading your foods. Please try again.',
			wrongFoodScanLabel: 'Not the right food?',
			wrongFoodScanLabelHint: 'Snap a few photos of the packaging and we\'ll create a new food for you!',
			useButtonsBelow: 'Use the + New button to create a new food or recipe.',
			copiedToClipboard: 'Copied to clipboard',
			didYouMeanLabel: 'Did you mean:',
			showingResultsForLabel: 'These are results for',
			searchInsteadForLabel: 'Search instead for',
		},
		suggestions: {
			listLabel: 'Suggested searches',
			loading: 'Looking for suggestions',
			rowLabel: '{phrase}, {count} {{food|foods}}',
			stapleRowLabel: '{name}, staple food',
		},
	},
	nutrition: {
		labels: {
			nutrient: 'Nutrient',
			target: 'Target',
			actual: 'Actual',
		},
		targets: {
			remaining: 'Remaining',
			logged: 'Logged',
		},
		macrosLogged: 'Macros Logged',
		macrosRemaining: 'Macros Remaining',
		nutritionInfo: 'Nutrition Info',
		flexibleNutritionLink: 'Flexible Nutrition',
		calorieCyclingLink: 'Calorie Cycling',
		settings: {
			meals: 'Meals',
		},
		planMyWeek: 'Plan My Week',
		charts: {
			loggedCalories: 'Logged Calories',
			targetCalories: 'Target Calories',
		},
		enabledForMealsBoth: 'are enabled for your meals.',
		enabledForMeals: 'is enabled for your meals.',
	},
	errors: {
		userFacing: {
			authFailed: 'Failed to log in after coming online. Staying offline.',
			foodNotExist: 'Food doesn\'t exist',
			foodLoadFailedTitle: 'Couldn\'t Load Food',
			foodLoadFailed: 'Couldn\'t load this food right now. Please check your connection and try again.',
			foodDeleteFailed: 'Couldn\'t delete this food. Please try again.',
			ingredientListLoadFailed: 'Couldn\'t load all the foods in this meal. Please check your connection and try again.',
			noMealOnDayTitle: 'No Meal to Add To',
			noMealOnDay: 'There\'s no meal on this day you can add food to. Please pick a different day.',
			splitIntoFoodsFailed: 'Couldn\'t split this into foods. One of its ingredients may no longer be available. Please try again.',
			splitTuningFailed: 'Your foods were split, but Auto-Tune ran into a problem and couldn\'t adjust their amounts. Please adjust them yourself, or try again.',
			confirmRecentMealFailed: 'Couldn\'t add this meal\'s foods. Please try again.',
			recentMealCouldNotBeOpened: 'Couldn\'t open this meal, so nothing was added. It may have been deleted, or it may not have finished syncing yet. Please try again.',
			couldNotOpenPage: 'This couldn\'t be opened. It may have been deleted, or it isn\'t available right now.',
			couldNotLeavePage: 'This screen couldn\'t be closed. Please try again.',
			exportFailed: 'Couldn\'t export the file. Please try again.',
			unsupportedBrowser: 'Your browser may not be fully supported',
			noticeUnusual: 'Notice anything unusual?',
			communityAuthFailed: 'Cannot authenticate for community access',
			equipmentCannotBeUsed: 'This equipment cannot be used',
			unsubscribeFailed: 'Failed to unsubscribe',
			paymentPending: 'Your payment is pending verification, but you can continue using the app',
			resubscribeFailed: 'Failed to resubscribe',
			planSwitchFailed: 'Cannot switch the plan',
			sendLoginEmailFailed: 'Failed to send the sign-in email. Please contact support.',
			updateWorkoutFailed: 'Couldn\'t update your workout. Please try again.',
			paddlePortalFailed: 'Couldn\'t open the subscription portal. Please try again.',
			imageReadFailed: 'Failed to read selected image.',
			imageDecodeFailed: 'Failed to load selected image.',
			imageTooSmall: 'Selected image is too small. Minimum image size is {minSize}px.',
			imageUploadFailed: 'Couldn\'t upload the image. Please check your connection and try again.',
		},
		syncRetry: {
			retrying: 'Connecting to your data — just a moment...',
			stillRetrying: 'Still working on it — hang tight!',
		},
	},
	auth: {
		login: 'Sign In',
		signup: 'Sign Up',
		forgotPassword: 'Forgot Password',
		email: 'Email',
		enterYourEmail: 'Enter your email',
		password: 'Password',
		enterYourPassword: 'Enter your password',
		signInReadyTapAgain: 'Sign-in is ready now. Please tap the button again.',
		enterYourName: 'Enter your name',
		chooseYourPassword: 'Choose your password',
		loginToAccount: 'Sign in to Your Account',
		welcomeToMhPhysique: 'Welcome to MH Physique',
		dontHaveAccountYet: 'Don\'t have an account yet?',
		dontHaveAccount: 'Don\'t have an account?',
		dontHaveAccountButton: 'Don\'t have an account?',
		learnMore: 'Learn More',
		processing: 'Processing',
		loginWithLink: 'Get the link',
		signInByEmail: 'Email me a sign-in link',
		signInWithPassword: 'Sign in with password',
		createAccount: 'Create an Account',
		alreadyHaveAccount: 'Already have an account?',
		signUpWithEmail: 'Sign Up With Email',
		orSignUpWith: 'Or Sign Up With',
		or: 'OR',
		signIn: 'Sign In',
		signInWithGoogle: 'Sign in with Google',
		signInWithApple: 'Sign In With Apple',
		google: 'Google',
		apple: 'Apple',
		firstLastName: 'First & Last Name',
		choosePassword: 'Choose Password',
		subscriptionSuccess: 'Subscription Successful!',
		subscriptionSuccessCheckEmail: 'We sent a sign-in link to the email you used during checkout. Check your inbox to get started.',
		emailLinkSent: 'Email Link Sent',
		emailLinkSentMessage: 'We sent you an email with a sign-in link. If you don\'t see the email soon, please check your spam folder.',
		emailLinkContact: 'If it is not there, contact',
		bySigningIn: 'By signing in, you agree to MH Physique',
		bySubscribing: 'By subscribing, you agree to MH Physique',
		termsAndConditions: 'Terms and Conditions',
		and: 'and',
		privacyPolicy: 'Privacy Policy',
		pricing: 'Pricing',
		refunds: 'Refunds',
		eula: 'EULA',
		claim: {
			alreadyHaveAccount: 'I Already Have an Account',
			newToMhPhysique: 'I\'m New Here',
			createAccount: 'Create Account',
		},
		switchAccount: {
			title: 'Switch Account',
			message: 'You are signed in as {currentEmail}. Switch to {newEmail} to access your subscription?',
			switchButton: 'Switch',
			expiredTitle: 'Link Expired',
			expiredMessage: 'Your sign-in link has expired. We\'ve sent a new one to {email}.',
			expiredResendFailedMessage: 'Your sign-in link has expired, and we could not send a new one to {email} just now. Please check your connection and try again.',
			unfinishedTitle: 'Switch Not Finished',
			unfinishedMessage: 'Switching to {newEmail} is taking too long, so you are still signed in to your current account. If the switch completes, the app will restart. Otherwise, please open the link again.',
		},
		errors: {
			otlExpired: 'The one-time link has expired. Please enter your email address to receive a new one.',
			otlRecent: 'Please check your inbox. We sent you a link to sign in recently. Can\'t find it? We can send another one.',
			otlAmbiguousAccount: 'We could not send your sign-in link, because there is more than one account on this email address and we cannot tell which one is yours. Please contact us at {email} and we will sort it out.',
			signInTokenCheck: 'Error during sign-in token check. Please try again or use a different method.',
			useAlternativeTitle: 'Use {provider}?',
			useAlternativeMessage: 'This account is already registered using {provider} sign-in. Do you want to use {provider} to sign in?',
			badEmailFormat: 'Bad email address format.',
			passwordResetSent: 'Check your inbox for password reset instructions. Then come back here to sign in.',
			popupClosed: 'The pop-up was closed. Please try again.',
			firebase: {
				invalidEmail: 'Please enter a valid email address.',
				missingEmail: 'Please enter your email address.',
				userNotFound: 'No account found with this email. Please check your email or create a new account.',
				emailAlreadyInUse: 'An account with this email already exists. Please sign in instead, or use "Forgot password" if you\'ve forgotten it.',
				accountExistsWithDifferentCredential: 'An account with this email already exists, but it uses a different sign-in method. Please sign in the way you did before, for example with your email and password.',
				wrongPassword: 'Incorrect password. Please try again.',
				invalidCredential: 'Incorrect email or password. Please try again.',
				weakPassword: 'Your password is too weak. Please choose a stronger one.',
				tooManyRequests: 'Too many attempts. Please wait a few minutes and try again.',
				networkRequestFailed: 'Unable to connect. Please check your internet connection and try again.',
				userDisabled: 'This account has been disabled. Please contact support at {email} for assistance.',
				operationNotAllowed: 'This sign-in method is not available right now. Please try a different method.',
				popupBlocked: 'The sign-in pop-up was blocked by your browser. Please allow pop-ups for this site and try again.',
				requiresRecentLogin: 'For security reasons, please sign in again.',
				invalidActionCode: 'This link is invalid or has already been used.',
				expiredActionCode: 'This link has expired. Please request a new one.',
				genericError: 'Something went wrong. Please try again or contact support at {email}.',
			},
		},
		newAccountMade: {
			title: 'New Account Created',
			message: 'Signing in this way created a brand new account, so it is empty. If you already subscribed using a different email address, enter that address and we will send you a sign-in link.',
			placeholder: 'you@example.com',
			continueAsNew: 'I\'m New Here',
			findMyAccount: 'Find My Account',
			checking: 'Checking that address...',
			linkSent: 'We\'ve sent a sign-in link to {email}. Open it on this device to get back to your account.',
			sameAccount: 'That is the address of the new account you are already signed in to. If you subscribed using a different address, try that one instead.',
			noAccount: 'We could not find an account with that email address. Check the spelling, or continue with the new account you just created.',
			severalAccounts: 'Several accounts use that email address, so we cannot tell which one is yours. Please contact support and we will sort it out for you.',
			couldNotCheck: 'We could not check that email address just now. Please check your connection and try again.',
			couldNotSend: 'We could not send the sign-in link just now. Please check your connection and try again.',
		},
		accountHint: {
			title: 'Different Account',
			message: 'The link you opened was sent to {hintEmail}, but you are signed in as {currentEmail}. Do you want us to email a sign-in link to {hintEmail}?',
			stayButton: 'Stay Signed In',
			sendButton: 'Send Link',
			sending: 'Sending sign-in link...',
		},
	},
	settings: {
		sections: {
			training: 'Training',
			nutrition: 'Nutrition',
			general: 'General',
			support: 'Support',
			regional: 'Regional & Display Preferences',
			accessibility: 'Accessibility',
			experimental: 'Experimental',
		},
		intakePendingCard: {
			title: 'Finish Setting Up Your Profile',
			body: 'Most options here adjust your fitness plan, so they\'ll be ready once it\'s generated.',
			linkText: 'Continue the Intake',
		},
		items: {
			trainingSettings: 'Training Settings',
			workouts: 'Workouts',
			editEquipment: 'Edit Equipment',
			nutritionSettings: 'Nutrition Settings',
			mealPlanner: 'Meal Planner',
			foods: 'Foods',
			appSettings: 'App Settings',
			manageSubscription: 'Manage Subscription',
			coaching: 'Coaching',
			privacyChoices: 'Privacy Choices',
			admin: 'Admin',
			help: 'Help',
			feedback: 'Feedback',
			messageCoach: 'Message Coach',
			community: 'Community',
			whatsNew: 'What\'s New',
			rateTheApp: 'Rate the App',
		},
		installApp: 'Install App',
		language: 'Language',
		applyingLanguage: 'Applying language…',
		downloadingLanguage: 'Downloading language…',
		languageDownloadFailed: {
			title: 'Couldn\'t switch language',
			message: 'We couldn\'t download {language} for offline use. Please check your internet connection and try again.',
		},
		clockMode: 'Clock mode',
		hour24: '24 hr',
		ampm: 'AM/PM',
		automatic: 'Automatic',
		decimalSeparator: 'Decimal Separator',
		point: 'Point (.)',
		comma: 'Comma (,)',
		dateFormat: 'Date Format',
		firstDayOfWeek: 'First Day of Week',
		monday: 'Monday',
		saturday: 'Saturday',
		sunday: 'Sunday',
		nutritionLabelStyle: 'Nutrition Label Style',
		foods: {
			uncaching: 'Uncaching foods...',
		},
		allowCardAnimations: 'Allow Card Animations',
		headerHideBehavior: 'Header Hide on Scroll',
		footerHideBehavior: 'Footer Hide on Scroll',
		scrollHide: {
			always: 'Always',
			never: 'Never',
		},
		descriptions: {
			language: 'Choose the language for the app\'s interface. Any text that isn\'t translated yet falls back to English.',
			clockMode: 'We have detected {mode} as the most likely clock mode based on your current country. If that doesn\'t match your preferences, please set a manual override here.',
			dateFormat: 'Choose how dates are displayed throughout the app. Automatic uses the locale detected from your current country.',
			firstDayOfWeek: 'Choose which day displays first in weekly views. Automatic uses the locale detected from your current country.',
			decimalSeparator: 'Choose which symbol is used for decimals (e.g., "." or ","). Based on your regional setting.',
			nutritionLabelStyle: 'US-style labels show total carbohydrates with fiber included. EU-style labels show net carbohydrates without fiber.\n\nThis applies only when editing an individual food. We always show net carbs (without fiber) in other places in the app, because it makes more metabolic sense.',
			cardAnimations: 'Disable the animations if the motion bothers you, or if your device cannot handle it well.',
			headerHideBehavior: 'Control whether the header hides when scrolling down. \'Auto\' only hides headers with calendars.',
			footerHideBehavior: 'Control whether the bottom navigation hides when scrolling down. \'Auto\' only hides the main navigation bar.',
			showSlotName: 'Show the muscle group name (slot name) under exercise names in workout views and exercise configuration.',
		},
		notifications: {
			sectionTitle: 'Notifications',
			description: 'Get reminders for scheduled activities. Notifications fire 10 minutes before the scheduled time.',
			workout: 'Workouts',
			workoutDescription: 'Before each scheduled workout',
			meals: 'Meals',
			mealsDescription: 'Before each scheduled meal',
			weighIn: 'Weigh-Ins',
			weighInDescription: 'On your scheduled weigh-in days',
			weeklyCheckup: 'Weekly Check-Up',
			weeklyCheckupDescription: 'When your weekly check-up is due',
			sleep: 'Sleep',
			sleepDescription: 'Before your scheduled wind-down time',
			deviceSettings: 'Device Notification Settings',
			deviceSettingsDescription: 'Manage notification permissions in your device settings',
			content: {
				workout: 'Time to train',
				meal: 'Time to eat',
				weighIn: 'Time to weigh in',
				weeklyCheckup: 'Time for your weekly check-up',
				sleep: 'Time to wind down',
			},
		},
		testSettings: {
			header: 'Test settings',
			testMode: 'Test mode',
			locale: 'Locale',
			localeDecimalSeparator: 'Locale Decimal Separator',
			unitSettings: 'Unit settings',
			hintsSeen: 'Hints seen',
			helpSeen: 'Help seen',
			dismissedToasts: 'Dismissed toasts',
			showAllHints: 'Show all hints always',
			showAllTutorials: 'Show all tutorials always',
			debugFoodSearch: 'Debug food search',
			debugRestTimerOnWeb: 'Show rest timer on web (debug)',
			debugLog: 'Debug Log',
			debugLogPlaceholder: 'No log recorded',
			clearLog: 'Clear Log',
			accountDatabase: 'Account database',
			cachedFoods: 'Currently cached foods: {count}',
			uncacheFoodsNote: 'For any foods in userdb that exist in globaldb, clear the item from userdb (will cause recache on next use).',
			testSurveyNote: 'This will pop up the survey called "Test survey".',
			testNotificationNote: 'Sends a push notification to all mobile devices logged into this account. Arrives within the next minute.',
			testNotification: 'Test Push Notification',
			resetHiddenMessagesNote: 'Brings back every message you hid by tapping "Never show again". Without this there is no way to undo that.',
			resetHiddenMessages: 'Reset Hidden Messages',
			hiddenMessagesRestored: '{count} hidden message{{s}} will show again.',
			noHiddenMessages: 'You haven\'t hidden any messages.',
		},
		experimental: {
			showSlotName: 'Show Slot Name',
		},
	},
	dbSnapshots: {
		title: 'Database Snapshots',
		createSnapshot: 'Create Snapshot',
		rename: 'Rename',
		download: 'Download',
		noSnapshots: 'No snapshots yet. Create one to save the current database state.',
		renameTitle: 'Rename Snapshot',
		renamePlaceholder: 'Snapshot name',
		deleteConfirmTitle: 'Delete Snapshot?',
		deleteConfirmMessage: 'This will permanently delete the snapshot "{name}".',
		creating: 'Creating snapshot...',
		snapshotInfo: '{date} · {count} docs',
		restore: 'Restore',
		restoreConfirmTitle: 'Restore Snapshot?',
		restoreConfirmMessage: 'This will replace all current data with the snapshot "{name}". This cannot be undone.',
		restoring: 'Restoring snapshot...',
		restoreComplete: 'Snapshot "{name}" has been restored successfully.',
		restoreFailed: 'The snapshot could not be restored. The connection dropped part-way through, so your data may be a mix of old and restored documents until syncing finishes. The app will now reload. Check your connection and try again.',
		restoreFailedTitle: 'Restore Failed',
		restoreCleanupFailed: 'The snapshot was restored on the server, but this device\'s old local copy could not be cleared. Do NOT keep using the app on this device yet: clear the app\'s storage (or reinstall) and open it again, so the restored data is downloaded fresh.',
	},
	dbRestoreJson: {
		title: 'Restore Database from JSON',
		description: 'Upload a JSON file or paste JSON data to restore a user database.',
		chooseFile: 'Choose JSON File',
		orPaste: 'Or Paste JSON',
		pasteHint: 'Paste an array of documents as JSON',
		parseJson: 'Parse JSON',
		restore: 'Restore',
		restoring: 'Restoring database...',
		invalidJson: 'Invalid JSON. Must be an array of documents, each with an _id field.',
		confirmTitle: 'Restore Database?',
		confirmMessage: 'This will replace all current user data with {count} documents. This cannot be undone.',
		fileLoaded: 'Loaded {count} documents from file.',
		restoreCompleteTitle: 'Database Restored',
		restoreCompleteMessage: 'Database has been restored from JSON ({count} documents).',
		restoreFailed: 'The database could not be restored. The connection dropped part-way through, so your data may be a mix of old and restored documents until syncing finishes. The app will now reload. Check your connection and try again.',
		restoreFailedTitle: 'Restore Failed',
		restoreCleanupFailed: 'Your data was restored on the server, but this device\'s old local copy could not be cleared. Do NOT keep using the app on this device yet: clear the app\'s storage (or reinstall) and open it again, so the restored data is downloaded fresh.',
	},
	debug: {
		nextDay: 'Next Day',
		more: 'More...',
	},
	app: {
		title: 'MH Physique',
		updated: 'Updated to {version}',
		whatsNew: 'What\'s New?',
		updateAvailable: {
			message: 'A new version is available.',
			action: 'Update',
			downloading: 'Downloading update…',
			ready: 'Update downloaded.',
			restart: 'Restart',
			upToDate: 'You\'re up to date',
			settingsRow: 'Check for updates',
		},
		updateRequired: {
			title: 'Please update',
			message: 'This version of the app is no longer supported. Update now to keep getting fixes and new features.',
			update: 'Update',
			later: 'Later',
			cardMessage: 'This version is no longer supported. Please update.',
		},
		starting: 'Starting...',
		loading: 'Loading...',
		databaseRecoveryLoading: 'Restoring your data...',
		downloadingExistingData: 'Downloading existing data...',
		openingCommunity: 'Opening Community, please wait...',
		communityOffline: 'The community needs an internet connection. Connect to the internet and try again.',
		videoOffline: 'Videos need an internet connection.',
		offlineFirstLoadMessage: 'You\'re offline. Connect to the internet to load your account for the first time on this device.',
		offlineFirstLoadChecking: 'Checking your connection...',
		offlineFirstLoadStillOffline: 'Still offline. Connect to the internet, then try again.',
		offlineFirstLoadServerUnreachable: 'Your connection is back, but we couldn\'t reach your account. Please try again in a moment.',
		accountLoadFailedMessage: 'We couldn\'t load your account. Check your connection and try again.',
		splashStuckMessage: 'Taking longer than usual. Check your connection and try again.',
		splashStuckOfflineMessage: 'You appear to be offline. Reconnect to the internet, then try again.',
		retry: 'Retry',
		communityTitle: 'Community',
		communityFirstTime: 'If you\'re opening the Community for the first time, it may take some time to create an account for you. Thank you in advance for your patience.',
		screenshotHelp: 'Use this button to take the screenshot. You can drag it around, if it gets in your way.',
		screenshotCaptureFailed: 'We could not take the screenshot. Your text is still there, so you can send it without one.',
		darkModeConflictTitle: 'Samsung Dark Mode Conflict',
		darkModeConflictMessage: 'This app is always in Dark Mode. Samsung Internet is forcing its own Dark Mode on top, which is making the app unreadable. Please open the browser menu and tap either "Light theme sites", or "Settings > Labs > Use website dark theme".',
		darkModeConflictButton: 'Ok, I did that',
		serverConnectionDifficulty: 'Having difficulty connecting to the server...',
		serverBehindAccountVersion: 'This server is running an older version than your account needs. Please try again in a few minutes.',
	},
	appInstallSheet: {
		title: 'Get the MH Physique app',
		body: 'A faster, smoother experience — workouts, meal logging, and progress tracking right on your phone.',
		openInAppCta: 'Open in MH Physique',
		openInAppLink: 'Already have the app? Open it',
		continueInBrowser: 'Continue in browser',
		dontShowAgain: 'Don\'t Show Again',
		notRightNow: 'Not right now?',
	},
	progress: {
		title: 'Progress',
		filters: 'Filters',
		measurements: 'Measurements',
		exportAsCsv: 'Export as CSV',
		noData: 'No data',
		lostPerWeek: 'lost per week',
		gainedPerWeek: 'gained per week',
		filterHeaders: {
			bodyComposition: 'Body Composition',
			nutrition: 'Nutrition',
			chartDisplaySettings: 'Chart Display Settings',
		},
		chartTitles: {
			bodyComposition: 'Body Composition Progress',
			nutrition: 'Nutrition Progress',
		},
		filterOptions: {
			showAverage: 'Show Average',
			showAverageDescription: 'Averages last 7 days of data points to smooth daily fluctuations',
			showPoints: 'Show Points',
			showPointsDescription: 'Display data points as circles on the chart lines',
		},
		axisTitles: {
			weight: 'Weight ({unit})',
			waist: 'Waist ({unit})',
			caloriesKcal: 'Calories (kcal)',
			day: 'Day',
			month: 'Month',
		},
		trendLegend: {
			falling: 'Falling',
			rising: 'Rising',
			flat: 'Flat',
			uncertain: 'Unclear',
			mayChange: 'May still change',
		},
	},
	intro: {
		start: 'Start',
		next: 'Next',
		back: 'Back',
		skip: 'Skip Intro',
		slide1: {
			title: 'Your all-in-one fitness coach in your pocket',
			text: 'Adaptive training and nutrition come together in one powerful app. Your program evolves with your progress, making sure you build muscle, gain strength, and lose fat.',
		},
		slide2: {
			title: 'Log your workouts and see your progress grow',
			text: 'Your program adjusts after every session to match your performance. If it\'s too easy, we\'ll challenge you. If it\'s too much, we\'ll scale back to help you recover.',
		},
		slide3: {
			title: 'Effortless nutrition for your goals',
			text: 'Your nutrition plan is continuously updated.\nExpert-designed meals take the guesswork out of hitting your targets.',
		},
		slide4: {
			title: 'Always Adapting to You',
			text: 'Your plan evolves as you grow stronger, adjusting seamlessly to keep you on track.\nPrefer another exercise or want your own meal?\nTweak your plans anytime.',
		},
	},
	customizeHomepage: {
		title: 'Customize Homepage',
		sections: {
			dailyLog: 'Daily Log',
			progress: 'Progress',
		},
		options: {
			wakeUp: 'Wake Up',
			weighIn: 'Weigh In',
			workout: 'Workout',
			meals: 'Meals',
			sleep: 'Sleep',
			goal: 'Goal',
			bodyComposition: 'Body Composition',
			nutrition: 'Nutrition',
			coachingTips: 'Coaching Tips',
		},
	},
	home: {
		tabs: {
			dailyLog: 'Daily Log',
			progress: 'Progress',
		},
		buttons: {
			logActivity: 'Log Activity',
			logMeal: 'Log Meal',
			logWorkout: 'Log Workout',
			weighIn: 'Weigh In',
			barcodeScan: 'Barcode Scan',
			labelScan: 'Label Scan',
		},
		hints: {
			nutritionTargetsWaiting: 'Nutrition targets will be available after your photos are evaluated. This shouldn\'t take more than 48 h.',
			offlineNow: 'You\'re offline. Syncing with the cloud will resume when you reconnect.',
		},
		messages: {
			noScheduleForDay: 'There\'s no schedule for this day.',
		},
		emptyState: {
			allHidden: {
				title: 'All Items Hidden',
				subtext: 'You\'ve hidden all items in this section. Use the button below to customize what appears here.',
			},
			customizeButton: 'Customize Homepage',
		},
		alerts: {
			workoutTimeLabel: 'Workout time',
		},
		settingsMenu: {
			customizeDashboard: 'Customize Dashboard',
		},
		progress: {
			goalCard: {
				title: 'Goal',
				target: 'Target',
				progress: 'Progress',
				fatLoss: 'Fat Loss',
				weightLoss: 'Weight Loss',
				weightGain: 'Weight Gain',
				estimatedValueTooltip: 'Current value is algorithmically estimated from your last two weeks of measurements. The change shown is the difference since you set this goal.',
				growthMindset: 'Growth Mindset',
				growthMindsetInfo: 'Growth mindset focuses on consistency and training progress. The goal is building strength and muscle.',
				start: 'Start',
				goal: 'Goal',
				onTrack: 'On Track',
				offTrack: 'Off Track',
				undetermined: 'Undetermined',
				notEnoughData: 'We don\'t have enough measurements yet to assess your progress. Keep logging your weigh-ins and body fat measurements.',
				measureMoreOften: 'We need more body fat measurements (waist tape or calipers) to determine if you\'re on track or not.',
				dataErratic: 'Your measurements are fluctuating a lot, making it hard to determine a clear trend. Try to measure under consistent conditions.',
				weightTooFlat: 'Your weight readings have been suspiciously identical. Please check that your scale is working properly.',
				insufficientFatnessConfidence: 'We\'re not yet confident in your body-fat trend. Add a few more measurements (waist tape or calipers) before we adjust your plan.',
				nutritionAdjusted: 'Nutrition adjusted {N1} {{day|days}} ago. Keep logging — assessment resumes in about {N2} {{day|days}}.',
				nutritionAdjustedToday: 'Nutrition adjusted today. Keep logging — assessment resumes in about {N2} {{day|days}}.',
				planJustStarted: 'Plan started {N1} {{day|days}} ago. Keep logging — assessment begins in about {N2} {{day|days}}.',
				planJustStartedToday: 'Plan started today. Keep logging — assessment begins in about {N2} {{day|days}}.',
			},
			bodyComposition: {
				title: 'Body Composition',
			},
			nutrition: {
				title: 'Nutrition',
			},
			coachingTips: {
				title: 'Coaching Tips',
				noTips: 'You\'re doing great! Keep up the good work.',
			},
		},
	},
	editTrainingGoal: {
		title: 'Edit Training Goals',
		primaryGoal: 'Primary Goal',
		method: 'Method',
		targetUnit: 'Target Unit',
		targetUnitDescription: 'Select how you would like to track your progress',
		goalWeight: 'Goal Weight',
		goalBodyFat: 'Goal Body Fat',
		growthMindset: 'Growth Mindset',
		growthMindsetDescription: 'Growth mindset focuses on consistency and training progress. The goal is building strength and muscle.',
		fixedTarget: 'Fixed Target',
		fixedTargetDescription: 'Have the app track your progress against a target weight or body fat percentage.',
		weight: 'Weight',
		bodyFatPercent: 'Body Fat %',
		fatLoss: 'Fat Loss',
		muscleGrowth: 'Muscle Growth',
		update: 'Update',
		updateAlert: {
			title: 'Update Training Goal',
			message: 'Changing your goal may require your training program to be entirely rebuilt and restarted.',
			updateGoal: 'Update Goal',
		},
	},
	consent: {
		cookies: {
			header: 'Zero-calorie cookies, the best kind of cookies.',
			message: 'By clicking "Accept All Cookies", you agree that MH Physique can store cookies on your device and disclose information in accordance with our ',
			privacyPolicy: 'Privacy Policy',
			customize: 'Customize',
			customizeHeader: 'Customize Cookies',
			saveSettings: 'Save Settings',
			acceptAll: 'Accept All Cookies',
			rejectAll: 'Reject All',
			required: 'Required',
			types: {
				functionality: {
					name: 'Functionality',
					description: 'Enables storage that supports the functionality necessary for proper functioning of the website or app, like language settings.',
				},
				security: {
					name: 'Security',
					description: 'Enables storage related to security, such as authentication functionality, fraud prevention, and other user protection.',
				},
				personalization: {
					name: 'Personalization',
					description: 'Enables storage related to personalization, like video recommendations.',
				},
				advertising: {
					name: 'Advertising',
					description: 'Helps MH Physique grow: lets our advertising partners know how you use the app, so our ads reach the right people and we can see which ones work.',
					gpcNotice: 'Advertising is off because your browser sent a Global Privacy Control signal.',
				},
				performance: {
					name: 'Performance',
					description: 'Enables storage, such as cookies, related to performance of the website and the application, like which options are used more often. We use this to improve the app.',
				},
			},
			chooseEach: 'Choose on or off for each option to save your settings.',
		},
		user: {
			header: 'Terms & Agreements',
			subtitle: 'Open the links below and read the terms carefully. By proceeding, you acknowledge that you have read and agree to the following terms.',
			updatedHeader: 'We\'ve updated our Terms and Privacy Policy',
			updatedSubtitle: 'We\'ve made some updates to our terms. Please review them and accept to continue.',
			later: 'Later',
			updatedCard: 'We\'ve updated our Terms and Privacy Policy.',
			updatedCardAction: 'Review',
			reviewOffline: 'You\'re offline. Connect to the internet to review the updated policy.',
			additionalOptions: 'Additional options:',
			acceptAll: 'Accept All',
			tosLabel: 'Terms of Service',
			healthLabel: 'Health Disclaimer',
			privacyLabel: 'Privacy Policy',
			researchLabel: 'Help improve the app by sharing anonymized fitness data for analysis.',
			marketingLabel: 'Stay updated with app tips, feature news, and occasional offers.',
			advertisingLabel: 'Help MH Physique grow: let our advertising partners know how I use the app, so our ads reach the right people and we can see which ones work.',
			continue: 'Continue',
			details: 'Details',
			requiredError: 'You must agree to the Terms of Service, Privacy Policy, and Health Disclaimer to continue.',
			persistFailed: 'We couldn\'t save your agreement. Please check your connection and try again.',
		},
	},
	profile: {
		title: 'User Profile',
		signOut: 'Sign Out',
		profileSection: 'Profile',
		subscriptionSection: 'Subscription',
		subscription: 'Subscription',
		subscriptionType: 'Type',
		subscriptionTypeMonthly: 'Monthly',
		subscriptionTypeYearly: 'Yearly',
		renewalDate: 'Renewal Date',
		expiresOn: 'Expires On',
		renewalPrice: 'Renewal Price',
		emailSupport: 'Email Support',
		switchPlanPromo: 'Your current plan is "{current}". Switch to "{other}" for {price} and save {saving}!',
		perpetualLicense: '{type} license',
		refreshingStatus: 'Refreshing subscription status...',
		subscriptionInfoError: 'Could not load subscription details. Re-open this page to retry.',
		coachCoversMembership: 'Your coach\'s plan covers your Premium membership right now. You are also paying for a subscription of your own.',
		boostYourGains: 'Boost Your Gains!',
		upgradeToPremium: 'Upgrade to Premium and unlock all features including advanced training programs, detailed progress tracking, and personalized nutrition plans.',
		upgradeNow: 'Upgrade Now',
		trialEndsOn: 'Your trial ends on {date}',
		trialCancelInfo: 'If you cancel now, you can continue using Premium features until your trial ends.',
		cancelSubscription: 'Cancel Subscription',
		trialCanceled: 'Your trial is canceled and will end on {date}',
		trialPaused: 'Your trial will pause on {date}',
		trialResumeInfo: 'You can resume your subscription anytime before the trial ends.',
		resumeSubscription: 'Resume Subscription',
		currentPlanWithSaving: 'Your current plan is "{current}". You can switch to "{other}" for {price} and save {saving}!',
		currentPlan: 'Your current plan is "{current}". You can switch to "{other}" for {price}.',
		switchToPlan: 'Switch to {plan}',
		paymentFailing: 'Your last payment did not go through. We will keep trying, and your membership stays active in the meantime. If the payment keeps failing, your membership will end on {date}.',
		paymentProcessing: 'Your payment is being processed.',
		paymentProcessingHelp: 'This usually takes just a few minutes. Please check back shortly.',
		subscriptionEnds: 'Your subscription ends on {date}',
		resubscribeInfo: 'You can resume your subscription anytime to continue enjoying Premium features.',
		support: 'Support',
		reportIssueInfo: 'Did the app seem off, or are you unsure what to do next? Maybe the answer you\'re looking for is here. Otherwise, feel free to discuss it with others in our <a href="https://community.mh-physique.app/" target="_blank">Community</a>.',
		reportIssueInfoFeedback: 'If you are unable to submit your feedback with the above buttons, you can also reach out to us via email.',
		termsAndConditions: 'Terms & Conditions',
		privacyPolicy: 'Privacy Policy',
		healthDisclaimer: 'Health Disclaimer',
		refundPolicy: 'Refund Policy',
		menu: {
			logout: 'Sign Out',
			deletePrivateData: 'Delete Private Data',
		},
		deleteData: {
			title: 'Are you sure?',
			message: 'This will delete all your private data from the servers and this device. Your account and subscription will remain, but all your workout and nutrition history will be lost forever. Note that some data may still remain on other devices if you have logged into them before.',
			deleteButton: 'DELETE',
			deleting: 'Deleting all user data...',
			error: 'Cannot purge the account. Please try again later.',
		},
		signOutOfflineWarning: {
			title: 'Sign Out Without Uploading?',
			message: 'Some of your recent changes have not been uploaded to the cloud yet. If you sign out now, those changes will be deleted from this device and cannot be recovered. To keep them, stay signed in, make sure you have a working internet connection, and give the app a moment to finish uploading before you sign out.',
			signOutAnyway: 'Sign Out Anyway',
		},
		reportProblemEmail: {
			subject: 'Issue-name-goes-here',
		},
		contactSupport: {
			title: 'Contact Support',
			message: 'Reach us by email at {email}. Copy the address below, or open your email app to start a message.',
			copyAddress: 'Copy Address',
			openMailApp: 'Open Email App',
			close: 'Close',
			copied: 'Support email address copied',
			copyFailed: 'Could not copy. Please copy the address shown above by hand.',
		},
		resubscribe: {
			confirm: 'Do you want to resume your subscription?',
			success: 'You have successfully resubscribed. We need to restart the application to apply the changes.',
			confirmPaused: 'Resume your subscription? This starts a new billing period and charges your saved payment method now, unless you still have paid time left.',
		},
		manageSubscription: {
			title: 'Manage Subscription',
			emailSent: 'We sent a log-in link to your email. Use it to access PayPro Global’s account portal to manage your subscription, view invoices, and update payment methods.',
		},
		googlePlayCancel: {
			confirm: 'Cancel your subscription? You\'ll keep access until the end of your current billing period.',
			confirmButton: 'Cancel Subscription',
			success: 'Your subscription has been canceled. You\'ll retain access until {date}.',
			error: 'Failed to cancel subscription. Please try again.',
		},
		paddleStaleState: 'Your subscription changed somewhere else, so this action no longer applies. Reopen this page to see its current state.',
		paddleCancel: {
			confirm: 'Cancel your subscription? You\'ll keep access until the end of your current billing period.',
			confirmImmediate: 'Cancel your subscription? Your subscription is paused, so canceling ends it right away.',
			confirmButton: 'Cancel Subscription',
			successImmediate: 'Your subscription has been canceled.',
			error: 'Failed to cancel subscription. Please try again.',
			successScheduled: 'Your subscription has been canceled. You will keep access until the end of your current billing period.',
			alreadyScheduled: 'Your subscription is already scheduled to be canceled, so there\'s nothing more you need to do. You will keep access until the end of your current billing period.',
		},
		subscriptionPausesOn: 'Your subscription pauses on {date}',
		subscriptionPaused: 'Your subscription is paused. Resume it any time to get your access back.',
		appleCancel: {
			confirm: 'To cancel your subscription, we\'ll open Apple\'s subscription management, where you can cancel it.',
			confirmButton: 'Manage Subscription',
			success: 'If you canceled in Apple\'s subscription management, your access will continue until the end of your current billing period.',
			error: 'Couldn\'t open subscription management. You can manage your subscription in your device\'s Settings, under your Apple Account.',
		},
		appleResume: {
			confirm: 'To resume your subscription, we\'ll open Apple\'s subscription management, where you can turn auto-renew back on.',
			confirmButton: 'Manage Subscription',
			success: 'If you turned auto-renew back on in Apple\'s subscription management, your subscription will continue to renew automatically.',
			error: 'Couldn\'t open subscription management. You can manage your subscription in your device\'s Settings, under your Apple Account.',
		},
		subscribedViaOtherPlatform: 'Your subscription is managed via {provider}. Open that platform to manage your subscription.',
		subscribedViaWeb: 'Your membership is managed on the web. Sign in on our website to make changes.',
		webMembershipProblem: 'We could not load the details of your membership, so we cannot change it from here right now. Your membership is billed on our website — sign in there to manage it, or contact support and we will help.',
		switchPlan: {
			confirm: 'Do you want to switch your plan to {plan} for {price}?',
			confirmNoPrice: 'Do you want to switch your plan to {plan}? The price difference for the rest of your current period will be charged or credited right away, using your saved payment method.',
			success: 'You have successfully switched the plan. We need to restart the application to apply the changes.',
			scheduledConfirm: 'Your plan will switch to "{plan}" when your current subscription cycle ends on {date}.',
			error: 'Failed to switch the plan.',
			trialNotSupported: 'Plan switching is available after your trial ends. You can switch plans once your first payment goes through, or cancel now and purchase a different plan when the trial expires.',
			grantedPriceWouldBeLost: 'You\'re on a special price we agreed with you, and that price only applies to your current plan. Switching would put you back on the standard price, so please contact support and we\'ll switch you over and keep your price.',
			notYetConfirmed: 'Your plan change went through at the store. We couldn\'t finish confirming it just now, but it should be applied by itself within a few minutes. There\'s no need to try again.',
		},
		pendingPlanChange: 'Your plan is scheduled to switch to "{plan}" on {date}.',
		pausesOn: 'Pauses On',
	},
	intake: {
		title: {
			updateProgram: 'Update Training Program',
		},
		steps: {
			basicInfo: 'Basic Info',
			bodyComposition: 'Body Composition',
			strength: 'Strength',
			lifestyle: 'Lifestyle',
			advancedTraining: 'Advanced Training',
			schedule: 'Schedule',
			nutritionOptions: 'Nutrition Options',
			goals: 'Goals',
			trainingVolume: 'Training Volume',
			energyBalance: 'Energy Balance',
			programOverview: 'Program Overview',
			firstWorkoutHandoff: 'Get Started',
		},
		step: 'Step {current} of {total}',
		validation: {
			selectOption: 'Please select an option',
			checkInputs: 'Please check your inputs and make sure all required fields are filled in correctly.',
			gender: 'Please state your gender. Nutrition and workout planning is significantly determined by gender characteristics.',
			age: 'We need to know your age to optimize the program taking that into account.',
			ageMinimum: 'You have to be 18 years or older to use this application.',
			ageTooOld: 'You can\'t possibly be that old.',
			birthdayFuture: 'Oh, hello, dear visitor from the future. Try another one. :)',
			height: 'Please enter your height',
			bodyMeasurements: 'Finger, wrist and ankle measurements are important for various genetic indicators. Hit the info button above, if you want to know more.',
			weight: 'Please state your current weight.',
			chooseOption: 'Please choose one of the options.',
			weekdaySelection: 'Please select yes or no for each of the weekdays.',
			trainingDaysAtLeastOne: 'Select the days you will train on most of the time.',
			bothTimes: 'Please enter both times. Doesn\'t have to be exact.',
			unknownField: 'Unknown field',
			internalError: 'Internal error [{code}]',
			strengthTooGoodToBeTrue: 'That sounds too good to be true. Can you please double-check those numbers?',
			strengthTestsNotLoaded: 'Tap Try Again, or tap Skip to continue without lifting stats.',
		},
		strength: {
			liftingStatsOptional: 'Lifting Stats (Optional)',
			liftingStatsNote1: 'Fill in the maximum weight x reps you estimate you could currently lift or recent personal records.',
			liftingStatsNoteHighlight: 'The closer to a one rep max (1RM), the better.',
			liftingStatsNote2: 'If you have no idea, leave that exercise blank.',
			addedWeightOnly: 'Enter only added weight beyond body weight. If none, enter 0.',
			skipExerciseReason: 'Why do you want to skip this exercise?',
			neverTrained: 'I never trained this before.',
			injuryOrEquipment: 'I have to avoid it due to an injury, or lack of equipment.',
			skipEstimate: 'Skip estimate',
			selectExercisePlaceholder: 'Select exercise',
			loadFailed: 'The strength tests could not be loaded. Check your internet connection and tap Try Again, or tap Skip to continue without lifting stats.',
		},
		nutrition: {
			morningAppetite: 'Morning appetite',
			morningAppetiteQuestion: 'Are you naturally hungry in the morning?',
			mealFrequency: 'Meal Frequency',
			mealFrequencyDescription: 'Select how many meals you plan to eat each day. (Tip: For best results, eat at consistent times and avoid snacking between meals.)',
		},
		nutritionOptions: {
			header: 'Nutrition Options',
			macroTargets: 'Macro Targets',
			enabled: 'Enabled',
			enabledNote: 'I want the app to prescribe macro and calorie targets, and update the targets based on my progress.',
			disabled: 'Disabled',
			disabledNote: 'I don\'t want the app to determine my macro targets.',
			macroTracking: 'Macro Tracking',
			trackInApp: 'Track In-App',
			trackInAppNote: 'I will log my meals in this app.',
			trackElsewhere: 'Track Elsewhere',
			trackElsewhereNote: 'I will log my meals on paper or in another app (or not at all).',
			trackingType: 'Tracking Type',
			flexibleNutrition: 'Flexible Nutrition',
			flexibleNutritionNote: 'You\'ll work with flexible ranges for your macros. The exact macro split often doesn\'t significantly affect your results.',
			fixedTargets: 'Fixed Targets',
			fixedTargetsNote: 'The app sets exact macronutrient targets for every meal.',
		},
		goals: {
			physiqueGoal: 'Physique goal',
			physiqueGoalQuestion: 'What is your primary fitness goal?',
			bareMinimum: 'Bare minimum',
			bareMinimumNote: 'Train with minimal volume to maintain current muscle mass.',
			balanced: 'Balanced full-body development',
			balancedNote: 'This is our default recommendation. It applies to most recreational trainees, athletes and bodybuilders.',
			lowerBodyNote: 'This option is popular among women who want to emphasize their lower bodies, including Bikini Fitness and Wellness competitors.',
			lowerBodyFocus: 'Lower body focus',
			lowerBodyFocusNote: 'This option is popular among women who want to emphasize their lower bodies, including Bikini Fitness and Wellness competitors.',
			custom: 'Custom',
			customNote: 'Let me decide for each muscle group how to customize it.',
			customizationNote: 'You can customize your training focus for each muscle group.',
			bodyPartCustomization: 'Body part customization',
			lowerBody: 'Lower Body',
			upperBody: 'Upper Body',
			primaryGoal: 'Primary Goal',
			trainingVolume: 'Training volume',
			maximize: 'Maximize',
			maintain: 'Maintain',
			shrink: 'Shrink',
			decideForMe: 'Decide for me',
			decideForMeDescription: 'Let the app determine the best approach based on your current state',
			fatLoss: 'Fat loss',
			fatLossDescription: 'Focus on losing body fat while maintaining muscle',
			muscleGrowth: 'Muscle growth',
			muscleGrowthDescription: 'Focus on building muscle mass',
		},
		training: {
			selectDaysNote: 'To get started, select the days that will represent your typical schedule. You can move them around later if needed.',
			volumeDescription: 'This refers to the total weekly training volume for the muscle groups you aim to grow. Muscle groups not prioritized for growth will have lower weekly volume.',
			currentPlanVolume: 'Current plan: {volume} sets/week',
			setsPerWeek: 'Sets per week:',
			recommendSwitchToMuscleGrowth: 'Your body fat is now low enough to focus on building muscle. We recommend switching your goal to Muscle growth.',
			recommendSwitchToFatLoss: 'Your body fat is now high enough that losing fat should come first. We recommend switching your goal to Fat loss.',
			slowAndSteady: 'Slow and steady',
			slowAndSteadyDescription: 'Sustainability is most important to me. As long as I\'m moving in the right direction, I don\'t care about the rate of progress.',
			balanced: 'Balanced',
			balancedDescription: 'I want to strike a reasonable balance between good results in proportion to effort and sustainability.',
			maximumEffort: 'Maximum effort',
			maximumEffortDescription: 'I will do whatever it takes to achieve maximum results (without compromising my health).',
			overreachingWarning: 'Warning: poor risk-reward at such high volumes. There\'s a high risk of overreaching and injuries for comparatively little additional progress.',
			calorie: 'Calorie',
			calorieDeficit: 'Calorie deficit:',
			calorieSurplus: 'Calorie surplus:',
		},
		advancedLabels: {
			progressionTesting: 'Progression testing',
			slot: 'Slot',
			sets: 'Sets',
			level: 'Level',
			otherSlotsLevel: 'Other slots level',
			intensityPeriodization: 'Intensity/Periodization',
			slotIntensitiesJSON: 'Slot Intensities JSON',
		},
		labels: {
			gender: 'Gender',
			male: 'Male',
			female: 'Female',
			birthdate: 'Birthdate',
			height: 'Height',
			weight: 'Weight',
			bodyfat: 'Body Fat Percentage',
		},
		alerts: {
			progressSaved: 'Your progress is saved as you go. You can leave and resume the form at any time.',
			recalculateNeeded: 'You made changes that impact other settings, you\'ll need to recalculate before you can access some tabs and update your program.',
		},
		updateOptions: {
			title: 'Update Your Program',
			intro: 'You changed some settings. Choose what to update.',
			workoutsTitle: 'Your Workouts',
			workoutsRebuild: 'Rebuild My Program',
			workoutsRebuildNote: 'Builds new workouts from your new settings.',
			workoutsRebuildNoteWithCustomizations: 'Builds new workouts from your new settings. Exercises you swapped in yourself are replaced.',
			workoutsVolumesOnly: 'Update Only My Set Volumes',
			workoutsVolumesOnlyNote: 'Keeps your exercises. Adjusts how many sets you do of each.',
			workoutsKeep: 'Keep My Current Workouts',
			workoutsKeepNote: 'Nothing about your training changes.',
			nutritionTitle: 'Your Nutrition',
			nutritionUpdate: 'Update My Calorie Target',
			nutritionUpdateNote: 'Works out your daily calories again from your new settings.',
			nutritionKeep: 'Keep My Calorie Target',
			nutritionKeepNote: 'Your daily calories stay at {target}.',
			nutritionKeepNoteNoTarget: 'Your daily calories stay where they are.',
			warnKeepEnergyBalance: 'Your new deficit or surplus setting will not change your daily calories, because you are keeping your current target.',
			warnKeepGoal: 'You changed your goal. Keeping your target leaves you eating the amount that was worked out for your previous goal.',
			warnKeepBodyData: 'Your calorie target stays worked out from your previous weight and body fat.',
			scrollToMessages: 'Scroll down to read what changes',
			warnKeepActivityLevel: 'You changed how active you are, but keeping your target leaves your daily calories worked out for your previous activity level.',
			warnUpdateReplacesTarget: 'Your target of {target} will be replaced by {recommended}. You can set it again on the Daily Nutrition Goal screen.',
			warnUpdateReplacesTargetUnknown: 'Your target of {target} will be replaced by a newly calculated one. You can set it again on the Daily Nutrition Goal screen.',
		},
		buttons: {
			next: 'Next',
			back: 'Back',
			skip: 'Skip',
			saveAndUpdate: 'Save & Update',
			recalculate: 'Recalculate',
		},
		lifestyle: {
			activityLevel: 'Activity Level, Including Cardio and Sports',
			sedentary: 'Sedentary',
			sedentaryNote: 'Example: you have an office job with standard life chores.',
			somewhatActive: 'Somewhat Active',
			somewhatActiveNote: 'Example: you have a long commute by bicycle, or occasionally go for a jog or play some sports, but are otherwise mostly sedentary.',
			active: 'Active',
			activeNote: 'Example: you\'re a full-time Personal Trainer, literally on your feet most of the day, or you do a lot of sports apart from lifting weights.',
			veryActive: 'Very Active',
			veryActiveNote: 'Example: you have a manual labor job and/or do lots of physically demanding activities apart from lifting weights.',
			stressLevel: 'Stress Level',
			mildStress: 'Only Occasional/Mild Stress',
			mildStressNote: 'Example: student not during exam period.',
			averageStress: 'Average Stress',
			averageStressNote: 'Example: full-time work with deadlines and commuting.',
			highStress: 'High Stress',
			highStressNote: 'Example: very high-paced work environment with great responsibility.',
		},
		advanced: {
			myoReps: 'Myo-reps',
			myoRepsPrefer: 'Prefer (Time-Saver)',
			myoRepsPreferNote: 'Your workouts will be as short as possible, while taking your goals and individual progression into account. This includes the use of Myo-reps whenever warranted.',
			myoRepsAvoid: 'Avoid (Classic Training)',
			myoRepsAvoidNote: 'Use only classical straight sets for all exercises. Myo-reps will not be applied automatically. However, you can always enable it for suitable exercises at any later point.',
			automatic: 'Automatic',
			myoRepsAutomaticNote: 'Myo-reps will be implemented for you whenever they are specifically beneficial.',
			rmTesting: 'Muscle Specific Hypertrophy Method (RM testing)',
			rmTestingPrefer: 'Prefer',
			rmTestingPreferNote: 'You\'ll perform a strength test for most exercises, and together we\'ll determine your personalized rep targets.',
			rmTestingAvoid: 'Avoid',
			rmTestingAvoidNote: 'We\'ll give you a repetition target for each exercise, and you\'ll find the appropriate weight during your first workouts. You can later still enable autoregulated rep targets.',
			calorieCycling: 'Calorie cycling',
			calorieCyclingOn: 'On',
			calorieCyclingOnNote: 'For more advanced trainees very familiar with tracking their macros, the app can distribute more nutrients in your anabolic windows (the meals after your workouts) and fewer nutrients outside of the anabolic windows. This improves nutrient partitioning - the ratio of muscle to fat you gain - according to some research.',
			calorieCyclingOff: 'Off (simpler)',
			calorieCyclingOffNote: 'This will distribute your macros equally across the week without nutrient timing, if-it-fits-your-macros style.',
			myoRepsTooltip: 'A time-efficient technique using an activation set followed by short rest-pause mini-sets to maximize muscle stimulus.',
			rmTestingTooltip: 'Strength tests to autoregulate your rep targets (ideal for advanced trainees).',
			calorieCyclingTooltip: 'Distributes more nutrients around your workouts and fewer outside the anabolic windows to improve nutrient partitioning.',
		},
		schedule: {
			trainingFrequency: 'Training Frequency',
			daysPerWeek: 'Days per week:',
			mealsPerDay: 'Meals per day:',
			hoursPerNight: 'Hours per night:',
			sleep: 'Sleep',
			sleepDescription: 'How many hours of sleep do you get on average per night? Be realistic!',
		},
		firstWorkout: {
			heading: 'Your plan is ready.',
			subheading: 'Here is what happens next:',
			step1: 'A short strength test sets your starting weights.',
			step2: 'Then the app updates your training and nutrition every week, from how you perform.',
			startButton: 'Start Your First Workout',
			finishLaterButton: 'Finish, I\'ll Start Later',
		},
		planOverview: {
			successAlert: 'Your personalized program is ready, optimized for your training and nutrition goals.',
			keepingProgramAlert: 'Your current training program will be kept unchanged. Your other settings have been updated.',
			nutritionPlan: 'Nutrition Plan',
			nutritionGoal: 'Your primary goal is currently set to',
			nutritionGoalSuffix: 'The following nutrition targets are designed to help you reach that goal:',
			dailyAverageCalories: 'Daily Average Calories',
			dailyAverageTooltip: 'Your average calorie intake across multiple days, not a daily target.',
			mealCalories: 'Meal Calories',
			mealCaloriesTooltip: 'Target calories per average meal. Weekly totals matter more than per-meal accuracy.',
			cutMealCalories: 'Cut Meal Calories',
			cutMealTooltip: 'Target calories for meals outside your anabolic window.',
			bulkMealCalories: 'Bulk Meal Calories',
			bulkMealTooltip: 'Target calories for meals after resistance training.',
			macroTabs: {
				day: 'Day',
				bulkMeal: 'Bulk Meal',
				cutMeal: 'Cut Meal',
				perDay: 'Per Day',
				perMeal: 'Per Meal',
			},
			macroTitles: {
				estimatedDailyAverage: 'Estimated Daily Average Macros',
				bulkMeal: 'Bulk Meal Macros',
				cutMeal: 'Cut Meal Macros',
				daily: 'Daily Macros',
				meal: 'Meal Macros',
			},
			macroMin: '(min)',
			macroMax: '(max)',
			protein: 'Protein',
			carbs: 'Carbs',
			fat: 'Fat',
			fiber: 'Fiber',
			workouts: 'Workouts ({count})',
			workoutsDescription: 'The following workouts have been designed for you, you will rotate through them each week.',
			learnMoreProgramming: 'Learn more about the reasoning behind the programming',
			edit: 'Edit',
			gainWeightQuicker: 'You will gain weight quicker at higher percentages, but going too high means gaining fat.',
			loseWeightQuicker: 'You will lose weight quicker at higher percentages, but losing weight too fast risks muscle loss.',
			currentPlan: 'Current plan: {percent}%',
			muscleLossWarning: 'Loss of muscle mass is likely with your current selection.',
			fatGainWarning: 'Excessive fat gain is likely with your current selection.',
		},
		dedication: {
			slowAndSteady: 'Slow and steady',
			slowAndSteadyNote: 'Sustainability is most important to me. As long as I\'m moving in the right direction, I don\'t care about the rate of progress.',
			balanced: 'Balanced',
			balancedNote: 'I want to strike a reasonable balance between good results in proportion to effort and sustainability.',
			maximumEffort: 'Maximum effort',
			maximumEffortNote: 'I will do whatever it takes to achieve maximum results (without compromising my health).',
		},
		trainingSplit: {
			title: 'Workout split',
			optimal: 'Optimal',
			optimalNote: 'The app optimizes your training frequency per muscle group without being limited by any template.',
			pushPullLegs: 'Push / Pull / Legs',
			pushPullLegsNote: 'You rotate between workouts of pushing exercises, then pulling exercises, then lower body.',
			upperLower: 'Upper / Lower',
			upperLowerNote: 'You alternate between upper-body and lower-body workouts.',
			fullBody: 'Full-body',
			fullBodyNote: 'You train every major muscle group in every workout.',
			availableAtFrequency: 'Available at {minimum:number}+ workouts/week',
		},
		testMode: {
			options: 'Test mode options',
			novice: 'Novice',
			intermediate: 'Intermediate',
			advanced: 'Advanced',
			initial: 'Initial',
		},
		loading: {
			analyzingFitness: 'Analyzing fitness goals and level.\nThis may take a minute or two.',
			generatingProgram: 'Generating customized training program.\nThis may take a minute or two.',
			finishingPlan: 'Finalizing your plan and schedule...',
		},
		errors: {
			internalError: 'Internal error [{code}]. Please refresh the page and try again.',
			couldntGeneratePlan: 'Couldn\'t generate fitness plan.',
			subscriptionRequired: 'Your subscription is not active, possibly because the payment didn\'t go through. Please try subscribing again to continue.',
		},
		notes: {
			bodyfatEstimate: 'What is the best estimate of your body fat percentage that you can obtain?',
			immutableStats: 'The information in this section cannot be edited once you complete the intake form. If you need to change this information later, you have to clear your data and recreate your account in Settings.',
		},
	},
	fatalError: {
		title: {
			whoops: 'Whoops...',
			testError: 'Test Fatal Error',
			storageNotResponding: 'Your device\'s storage isn\'t responding',
		},
		messages: {
			cantAccessData: 'Can\'t access local offline data.',
			testError: 'Test fatal error occurred.',
			appFailedToStart: 'App failed to start.',
			lockdownMode: 'Your device has lockdown mode enabled.',
			serverError: 'Unable to connect to the server.',
			localStorageWedged: 'The app can\'t reach the offline copy of your data on this device. Your account and everything already synced are safe on our servers.',
			resetLocalDataOffer: 'If that doesn\'t help, you can start this device\'s copy from scratch. Everything already synced is downloaded again; anything logged on this device that hadn\'t synced yet will be lost.',
		},
		help: {
			tryAgainLater: 'Please try again later.',
			retryOrLogout: 'If retrying doesn\'t help, try signing out and back in.',
			testError: 'Just testing, please ignore.',
			retryOrRelogin: 'Please try again, or try to sign out and sign in again.',
			disableLockdown: 'Please disable lockdown mode on your device and then try again, or contact support if the problem persists after.',
			storageForceQuitIos: 'Closing the tab won\'t fix this. Please fully close the app (swipe it away from the app switcher) and open it again.',
			storageRestartApp: 'Please fully close the app and open it again.',
			storageRestartBrowser: 'Please close and reopen your browser, then open the app again.',
		},
		buttons: {
			retry: 'Retry',
			logout: 'Sign Out',
			resetLocalData: 'Start This Device Over',
		},
		support: {
			prefix: 'If the above doesn\'t help, and the problem persists, please contact',
		},
		alerts: {
			resetLocalDataTitle: 'Start this device over?',
			resetLocalDataMessage: 'Your account is safe, and everything already synced will be downloaded again. Anything logged on this device that hadn\'t synced yet will be lost.',
		},
	},
	calorieChange: {
		decrease: 'Decrease',
		increase: 'Increase',
		self: {
			less: '{percent}% less than the {base} kcal you reported is {kcal} kcal.',
			more: '{percent}% more than the {base} kcal you reported is {kcal} kcal.',
			same: 'The same as the {base} kcal you reported.',
		},
		coach: {
			less: '{percent}% less than the reported {base} kcal is {kcal} kcal.',
			more: '{percent}% more than the reported {base} kcal is {kcal} kcal.',
			same: 'The same as the reported {base} kcal.',
		},
		belowMinimum: '{kcal} kcal is below {minimum} kcal, the lowest target the app would set. Your number is used as typed. The app may raise it to {minimum} kcal the next time it recalculates the plan.',
	},
	nextIntake: {
		title: 'Next week\'s intake',
		now: 'Now {kcal} kcal/day',
		sourceLogged: 'Average daily intake last week (logged)',
		sourceTargetSelf: 'Your calorie target, reported as last week\'s intake',
		sourceTargetCoach: 'Their calorie target, reported as last week\'s intake',
		sourceCustom: 'Typed in as last week\'s intake',
		recommendedChange: 'Recommended: {change} kcal/day ({percent}%) → {kcal} kcal/day',
		recommendedChangeNoPercent: 'Recommended: {change} kcal/day → {kcal} kcal/day',
		recommendedKeep: 'Recommended: keep {kcal} kcal/day',
	},
	bodyCompOverview: {
		progressCheck: 'Progress check',
		statusOnTrack: 'On track',
		statusOffTrack: 'Off track',
		statusUndetermined: 'No verdict yet',
		statusInfoHome: {
			onTrack: 'Judged on the last 14 days: your progress fits your goal, so your plan needs no change.',
			offTrack: 'Judged on the last 14 days: your progress does not fit your goal, so your plan needs a change.',
			undetermined: 'The app cannot judge the last 14 days yet, for example after a plan change or with too few readings.',
		},
		statusInfoCheckup: {
			onTrack: 'Judged on the 14 days before this check-up: your plan needed no change.',
			offTrack: 'Judged on the 14 days before this check-up: your plan needed a change.',
			undetermined: 'This check-up could not judge the 14 days before it, for example after a plan change or with too few readings.',
		},
		fatTrendDown: 'Body fat is going down',
		fatTrendUp: 'Body fat is going up',
		fatTrendUnclear: 'Body fat has no clear direction',
		notEnoughFatReadings: 'Not enough skinfold or waist readings for a trend',
		notEnoughWeighIns: 'Not enough weigh-ins for a trend',
		readFromSkinfolds: 'read from your skinfolds',
		readFromWaist: 'read from your waist',
		trendWithSource: '{trend}, {source}.',
		skinfoldsWord: 'skinfolds',
		waistWord: 'waist',
		perWeek: '{value} {unit}/wk',
		ofBodyWeightPerWeek: 'of body weight per week',
		ofBodyWeightGainedPerWeek: 'of body weight gained per week',
		ofBodyWeightLostPerWeek: 'of body weight lost per week',
		countedFrom: 'The check counted readings from {date}.',
		judgedOnLast14Days: 'The on-track verdict is judged on the last 14 days. This card shows the trend over the range you picked.',
		periodTrendOnly: 'Trend over the period you picked.',
		storedAtCheckup: 'As judged when this check-up was completed.',
		weightResponse: 'Weight response',
		atKcal: 'At {kcal} kcal/day',
		baselineLogged: 'Logged average',
		baselineTarget: 'Calorie target',
		baselineCustom: 'Typed in',
		intakeReportedOn: '{how}, reported at the check-up on {date}.',
		fillsAfterFirstCheckup: 'Fills in after your first weekly check-up.',
		weightNow: 'Last weigh-in {value} {unit}',
		energyIntake: 'Energy intake',
		avgKcalPerDay: '{kcal} kcal/day avg',
		daysCounted: '{counted} of {total} days',
		checkupWeek: 'Check-up week {from} - {to}',
		legendLogged: 'Logged calories',
		legendTarget: 'Target calories',
		mean: 'Mean',
		valueWithUnit: '{value} {unit}',
		readingsInRange: '{count} readings in this range',
		adherence: 'Adherence',
		adherenceCalories: 'Calories',
		adherenceTrainingDays: 'Training days',
		adherenceDaysLogged: 'Days logged',
		ofTargetKcal: '/ {target} kcal',
		percentOfTarget: '{percent}% of target',
		kcalPerDay: 'kcal/day',
		ofTotal: 'of {total}',
		noWorkoutsPlanned: 'No workouts planned that week.',
		noWeighIns: 'No weigh-ins in this range.',
		noFatReadings: 'No skinfold or waist readings yet.',
		loadFailed: 'The overview could not be loaded.',
		retry: 'Retry',
	},
	trainingOverview: {
		title: 'Training Overview',
		notAvailable: 'This page is not available.',
		deletedExercise: 'Deleted exercise',
		otherSessions: 'Other sessions',
		noLongerInWorkout: 'No longer in this workout',
		columnExercise: 'Exercise',
		columnSets: 'Sets',
		columnLastSession: 'Last session',
		showingSessions: 'Showing {shown} of {total} {{session|sessions}}',
		showEarlier: 'Show Earlier',
		showLater: 'Show Later',
		phoneNote: 'Last session {date} · tap an exercise for its history',
		skipped: 'skipped',
		noWorkouts: 'No logged workouts in the last {weeks} weeks.',
		noSessions: 'No sessions yet.',
		olderDownloading: 'Older workouts are still downloading.',
		loadFailed: 'The training overview could not be loaded.',
		retry: 'Retry',
		marks: {
			same: 'Same',
			down: 'Down',
		},
		markInfo: {
			same: 'Same weight and reps on the first set as last time',
			down: 'Lower weight, or the same weight with fewer reps, on the first set than last time',
		},
		tags: {
			heavy: 'Heavy',
			medium: 'Medium',
			light: 'Light',
			test: 'Test',
			deload: 'Deload',
			break: 'Break',
			cap: 'Capped',
		},
		tagInfo: {
			heavy: 'Heavy day',
			medium: 'Medium day',
			light: 'Light day',
			test: 'Strength or rep test',
			deload: 'Lighter after a deload',
			break: 'Lighter after a training break',
			cap: 'The app held the weight down to what you can lift right now',
		},
		menu: {
			exerciseSettings: 'Exercise Settings',
			history: 'History',
			editWorkouts: 'Edit Workouts',
		},
		programChanged: 'The program changed while you were editing. Nothing was saved.',
		saveFailed: 'The exercise settings could not be saved. Please try again.',
		openOverview: 'Open Training Overview',
		upToToday: 'Training up to today',
		tabTraining: 'Training',
		checkupCount: '{same} the same, {down} down',
	},
	dateRange: {
		week: 'Week',
		month: 'Month',
		quarter: 'Quarter',
		year: 'Year',
		all: 'All',
		twoWeeks: '2w',
		fourWeeks: '4w',
		eightWeeks: '8w',
		twelveWeeks: '12w',
	},
	dayItems: {
		titles: {
			weighIn: 'Weigh-in',
			weeklyCheckup: 'Weekly Checkup',
			wakeUp: 'Wake-up',
			sleep: 'Sleep',
			restDay: 'Rest Day',
			meal: 'Meal {number}',
			workout: 'Workout',
			workoutNamed: 'Workout {name}',
		},
		text: {
			reviewYourWeek: 'Review your week',
		},
		alerts: {
			rescheduleWorkout: 'You currently don\'t have a scheduled workout for tomorrow. Do you want to reschedule this workout to tomorrow?',
		},
		warnings: {
			daysSinceLastWeighIn: 'It\'s been {days} {{day|days}} since your last weigh-in.',
			weeksSinceLastWeighIn: 'It\'s been {weeks} {{week|weeks}} since your last weigh-in.',
			longTimeSinceLastWeighIn: 'It\'s been a long time since your last weigh-in.',
			logWeightLink: 'Log Weight',
			planUpdateNeeded: 'Your progress indicates your training program needs to be updated to match your current abilities.',
			updateProgramLink: 'Update Program',
			pendingWorkout: 'You have an unfinished workout from',
			unfinishedWeeklyCheckup: 'You have an unfinished weekly checkup from',
			firstWorkoutReminder: 'Your first workout is on {day}. Don\'t forget to log back in!',
			coachLapseManageInApp: 'Manage Subscription',
			coachLapseManageInStore: 'To keep it running, update this subscription in your app store\'s subscription settings.',
			coachPaymentFailedTitle: 'Your coaching payment did not go through',
			coachPaymentFailed: 'We could not take payment for your coaching subscription. We will keep trying, but if it does not go through, your access ends on {date}.',
			coachPaymentFailedNoDate: 'We could not take payment for your coaching subscription. We will keep trying, but if it does not go through, your access will end.',
			coachNotRenewingTitle: 'Your coaching plan is not renewing',
			coachNotRenewing: 'Your coaching subscription is no longer set to renew. Your access continues until {date}.',
			coachNotRenewingNoDate: 'Your coaching subscription is no longer set to renew.',
			coachPlanStoppedTitle: 'Your coaching plan is not active',
			coachPlanStopped: 'Your coaching subscription is not active. Nothing has been deleted — everything comes back as soon as the subscription is active again.',
			coachClientsWillLoseAccess: 'Your {count} {{client|clients}} will lose the Premium access it covers too.',
			coachClientsLostAccess: 'Your {count} {{client|clients}} no longer have the Premium access it covered.',
			memberPaymentFailedTitle: 'Your last payment did not go through',
			memberPaymentFailed: 'We could not take your payment. We will keep trying, and your membership stays active in the meantime. If the payment keeps failing, your membership will end on {date}.',
			memberPaymentFailedNoDate: 'We could not take your payment. We will keep trying, and your membership stays active in the meantime. If the payment keeps failing, your membership will end.',
			memberPaymentFailedViewMembership: 'View Membership',
		},
		success: {
			weightChange: 'Your estimated weight change is {direction} {amount}{unit} / week.',
			viewProgress: 'View Progress',
		},
		info: {
			notEnoughWeightData: 'We need a few more weigh-ins to estimate your weekly trend.',
		},
		badges: {
			mealPlanner: 'Meal Planner',
			mealPlannerInfo: 'This meal was generated using the Meal Planner, designed to match your macro targets. Nutrients will be close to your goals, though slight variations allow for flexibility and food variety.',
			macrosMatch: 'On Target',
			macrosMatchInfo: 'This meal\'s macros are within 5% of your targets. Great job!',
		},
		buttons: {
			logMacrosManually: 'Log Macros Manually',
			reschedule: 'Reschedule',
			markAsComplete: 'Mark as Complete',
			addFood: 'Add Food',
			logWeight: 'Log Weight',
			resume: 'Resume',
			start: 'Start',
			done: 'Done',
		},
		skippedWorkout: {
			message: 'Workout moved to {date}.',
			moveToToday: 'Move to Today',
			moveToTodayButton: 'Move To Today',
			restore: 'Restore',
		},
		noWorkout: {
			nextWorkout: 'Next workout is on {day}.',
			message: 'Train today if you like.',
		},
	},
	unsavedChanges: {
		title: 'Apply changes?',
		alerts: {
			incompleteData: 'The data is incomplete and cannot be saved. What do you want to do?',
			applyChanges: 'You have unsaved changes. Do you want to apply those before leaving?',
		},
		errors: {
			saveFailed: 'Couldn\'t save your changes. Your work is still here - please try again.',
		},
		buttons: {
			keepEditing: 'Keep Editing',
		},
		discard: {
			feedbackTitle: 'Discard your feedback?',
			feedbackMessage: 'You haven\'t sent this yet. If you leave now, what you wrote is gone.',
			coachMessageTitle: 'Discard your message?',
			coachMessageMessage: 'You haven\'t sent this yet. If you leave now, what you wrote is gone.',
			noteTitle: 'Discard your note?',
			noteMessage: 'You haven\'t saved this note yet. If you leave now, what you wrote is gone.',
			brandingTitle: 'Discard your branding changes?',
			brandingMessage: 'You haven\'t saved these yet. If you leave now, your logo, color and name go back to what they were.',
			checkupTitle: 'Leave the check-up?',
			checkupMessage: 'You haven\'t finished it yet. If you leave now, the answers you have entered are gone.',
			coachTargetTitle: 'Discard your calorie target?',
			coachTargetMessage: 'You haven\'t applied it yet. If you leave now, the number you typed is gone.',
			nutritionPlanTitle: 'Discard your calorie and macro changes?',
			nutritionPlanMessage: 'You haven\'t saved these yet. If you leave now, the numbers go back to what they were.',
			ingredientTitle: 'Discard your changes to this ingredient?',
			ingredientMessage: 'You haven\'t saved them yet. If you leave now, the amount goes back to what it was.',
		},
	},
	training: {
		startWorkout: 'Start Workout',
		resumeWorkout: 'Resume Workout',
		openWorkout: 'Open Workout',
		skipWorkout: 'Skip Today',
		editWorkout: 'Edit Workout {letter}',
		editWorkoutShort: 'Edit Workout',
		changeWorkout: {
			title: 'Different Workout',
			description: 'Select which workout you would like to perform on this date.',
			menuOption: 'Different Workout',
		},
		workoutChangedInfo: 'This change will only be saved when you start the workout and complete at least one set. Otherwise, it will reset after refreshing the app.',
		noWorkout: 'No workout scheduled for this day',
		noWorkoutScheduled: 'No Workout Scheduled',
		nextWorkoutOn: 'Your next workout is on {day}.',
		trainAnyway: 'Would you like to train today?',
		trainToday: 'Train Today',
		workout: 'Workout',
		trainingProgram: {
			muscleVolume: 'Muscle Volume',
			workoutLetter: 'Workout {letter}',
			sets: 'Sets',
			myoReps: 'Myo-Reps',
			rmTesting: 'RM Testing',
			set: 'Set',
			notCountedTitle: 'Not Counted Yet',
			notCountedExplanation: 'These exercises have no "Most Similar Exercise" set, so we can\'t tell which muscles they train. Tap one to set it and include it in the volume above.',
			notCountedUntrackedTitle: 'Not Measured by This Program',
			notCountedUntrackedExplanation: 'These exercises train a body area this program doesn\'t measure, so they can\'t be added to the totals above. You still train and log them as usual.',
			notCountedSummary: '{count} not counted',
		},
		goals: {
			muscleGrowth: 'Muscle growth',
			bodyRecomposition: 'Body recomposition',
		},
		emptyProgramDay: {
			message: 'This workout has no exercises yet.',
			editLink: 'Add Exercises',
		},
	},
	schedule: {
		warnings: {
			mealBeforeWorkout: 'You can train fasted, but we recommend having at least one meal before workout.',
			mealAfterWorkout: 'At least one meal after a workout helps maximize muscle growth.',
			minTimeBetweenMeals: 'If there\'s less than 3 hours between two meals, you can consider combining them into one.',
			oneMealCloseToWo: 'Having a meal within 1 hour before or after workout is beneficial.',
			prePostWoMealDistance: 'We suggest sandwiching your workout between two meals by having those meals no more than 5 hours apart.',
			shortFeedingWindow: 'Intermittent Fasting is fine, but we suggest having your total daily feeding window (the time between the first and last meal) be at least 8 hours long.',
			workoutTimingIrregular: 'Train whenever you can, but we suggest training at roughly the same time every (training) day.',
			mealTimingIrregular: 'You can eat whenever you want, but we recommend having meals at roughly the same times every day.',
			sleepTimingIrregular: 'If you can, we recommend consistent going to sleep and getting out of bed at roughly the same times each day.',
			shortSleep: 'If you can sleep more, you probably should. At least 8 hours every night is highly recommended.',
		},
		tips: 'Schedule Tips',
	},
	editFood: {
		labelScanLowConfidence: 'Low confidence — verify nutrients manually',
		labelScanJsonRepaired: 'AI response was truncated — some nutrients may be missing',
		placeholders: {
			enterName: 'Enter name',
		},
		labels: {
			servingSize: 'Serving size',
			servingSizes: 'Measures',
			measureName: 'Measure name',
			valuesPer: 'Per',
			instructions: 'Instructions',
			labelStyle: 'Label Style',
			details: 'Details',
			source: 'Source:',
			barcode: 'Barcode',
			noBarcode: 'No barcode',
			macros: 'Macros',
			nutrients: 'Nutrients',
			usLabel: 'US label',
			euLabel: 'EU label',
			serving: 'Serving',
			portion: 'portion',
			perPortionOfWeight: '{per} {portion} of {weight}',
			foodName: 'Food Name',
			image: 'Image',
			nutritionFacts: 'Nutrition Facts',
			nutritionInformation: 'Nutrition Information',
			hideAdditionalNutrients: 'Hide additional nutrients',
			showAdditionalNutrients: 'Show additional nutrients',
			description: 'Description',
		},
		buttons: {
			addServingSize: 'Add Measure',
			addImage: 'Add Image',
			scanBarcode: 'Scan barcode',
			log: 'Log',
			duplicate: 'Duplicate',
			delete: 'Delete',
			editFood: 'Edit Food',
			scanRecipe: 'Scan Recipe',
			useScannedBarcode: 'The One I Scanned',
			useBarcodeOnLabel: 'The One On The Label',
			useAnyway: 'Use anyway',
			doubleCheck: 'Let me double check',
			share: 'Share Recipe',
			unshare: 'Stop Sharing',
			importRecipe: 'Import to My Recipes',
			recipeImported: 'Recipe Imported',
			linkCopied: 'Link Copied',
			updateAvailable: 'Update Available',
			refreshFromOriginal: 'Refresh',
			originalUnavailable: 'Original Recipe No Longer Available',
			importedRecipe: 'Imported Recipe',
			recipeRefreshed: 'Recipe Updated',
			sharedRecipeInfo: 'This recipe was shared with you.',
			shareFailed: 'Could not share the recipe. Please try again.',
			unshareFailed: 'Could not stop sharing the recipe. Please try again.',
			shareIngredientsFailed: 'Some ingredients could not be published. The shared recipe may not work correctly for others.',
		},
		titles: {
			newRecipe: 'New recipe',
			newFood: 'New food',
		},
		tabs: {
			overview: 'Overview',
			ingredients: 'Ingredients',
		},
		nutrients: {
			calories: 'Calories',
			totalFat: 'Total Fat',
			saturatedFat: 'Saturated Fat',
			transFat: 'Trans Fat',
			cholesterol: 'Cholesterol',
			sodium: 'Sodium',
			totalCarbohydrate: 'Total Carbohydrate',
			dietaryFiber: 'Dietary Fiber',
			totalSugars: 'Total Sugars',
			includesAddedSugars: 'Includes Added Sugars',
			protein: 'Protein',
			vitaminD: 'Vitamin D',
			calcium: 'Calcium',
			iron: 'Iron',
			potassium: 'Potassium',
			energy: 'Energy',
			fat: 'Fat',
			saturates: 'saturates',
			carbohydrate: 'Carbohydrate',
			sugars: 'sugars',
			fiber: 'Fiber',
			salt: 'Salt',
		},
		alerts: {
			inconsistentValues: 'The values seem inconsistent!',
			confirmDelete: 'Are you sure you want to delete this {type}?',
			removeBarcode: 'Remove barcode',
			removeBarcodeMessage: 'Do you want to remove the barcode {barcode} from this food?',
			confirmScannedBarcode: 'Is this the same product?',
			confirmScannedBarcodeMessage: 'You scanned the barcode {barcode}, but no barcode was found on this label. Should this new food get that barcode?',
			barcodeMismatch: 'Two different barcodes',
			barcodeMismatchMessage: 'You scanned {scanned}, but the label shows {onLabel}. Which one belongs to this food?',
			foodSourceBody: '{attribution}\n\n{trustNote}',
		},
		errors: {
			caloriesInconsistent: 'Calories are inconsistent with macronutrient values! Expected around {expectedCalories} kcal.',
			nameRequired: 'Food name is required.',
			ingredientRequired: 'At least one ingredient needs to be added.',
			portionRequired: 'Portion size is required.',
			nutrientMissing: 'Missing key nutrient.',
			measureWeightRequired: 'Enter weight in grams',
		},
		messages: {
			barcodeExists: 'Barcode already exists in this food.',
		},
		types: {
			recipe: 'recipe',
			food: 'food',
		},
		foodSources: {
			appRecipe: {
				name: 'MH Physique Recipe',
				shortName: 'Recipe',
				description: 'Recipe provided and verified by the MH Physique app team.',
			},
			community: {
				name: 'Community-Created Food',
				shortName: 'Community',
				description: 'Food created by the MH Physique app community.',
			},
			staple: {
				name: 'MH Physique Staple Food',
				shortName: 'Staple',
				description: 'A common food checked by the MH Physique app team, built from trusted food databases.',
			},
		},
		sourceFoods: {
			header: 'Built From These Foods',
			row: '{name} ({database})',
			nameUnavailable: 'Name Not Available',
		},
		trustLevel: {
			official: 'This is a **highly accurate** food info sourced from verified databases.',
			crowdsource: 'This food info is sourced from a publicly editable database. Errors are often possible. **Please verify the values yourself before using!**',
			community: 'This food info is provided by the MH Physique app community. This is usually of high quality, but errors are possible. **Please verify the values yourself before using!**',
		},
	},
	editMeal: {
		basedOn: 'Based on {name}, {day}',
		recipeInstructions: '{name} instructions',
		macrosLogged: 'Macros Logged',
		macrosRemaining: 'Macros Remaining',
		dayMacrosLogged: 'Day Macros Logged',
		dayMacrosRemaining: 'Day Macros Remaining',
		instructions: 'Instructions',
		skippedNotice: 'You skipped this meal, so its food doesn\'t count toward the day\'s totals.',
		buttons: {
			clear: 'Clear',
			saveAsRecipe: 'Save as Recipe',
			copyFrom: 'Copy From a Day',
			scanMeal: 'Scan Meal',
		},
		addToMeal: {
			title: 'Add to this meal',
			addFoodsDescription: 'Search foods, recipes and meals',
			scanBarcode: 'Scan Barcode',
			scanBarcodeDescription: 'A packaged product',
			scanLabel: 'Scan Label',
			scanLabelDescription: 'Photograph the nutrition panel',
			logMacrosManuallyDescription: 'Type the numbers straight in',
			scanMealDescription: 'Photograph your plate',
			autoSuggestionsDescription: 'Fills the meal to your remaining targets',
			copyFromDescription: 'The same meal on another day',
			short: {
				addFoods: 'Add Foods',
				scanBarcode: 'Scan Barcode',
				scanLabel: 'Scan Label',
				logMacrosManually: 'Macros',
				scanMeal: 'Scan Meal',
				autoSuggestions: 'Suggestions',
				copyFrom: 'Copy Day',
			},
		},
		placeholders: {
			recipeName: 'Enter recipe name',
		},
		labels: {
			recipeName: 'Recipe name',
		},
		alerts: {
			clearMealTitle: 'Clear {mealName}?',
			deleteMealTitle: 'Delete {mealName}?',
			clearMealMessage: 'Are you sure you want to clear contents of this meal?',
			deleteMealMessage: 'Are you sure you want to delete this meal?',
			cannotTuneTitle: 'Cannot tune',
			cannotTuneMessage: 'This meal cannot be tuned to fit your targets.\n\nAuto-Tune works by changing how much of each food is in the meal, so it needs enough to work with. Try adding another food, or one from a different food group.',
			splitRecipesTitle: 'Split recipes?',
			splitRecipesMessage: 'To tune a recipe, it needs to be split into individual ingredients. Do you want to do that now?',
			splitAndTune: 'Split & Tune',
			tuneSuccessTitle: 'Tuning tips',
			tuneSuccessMessage: 'The meal was tuned as close as possible, but cannot fully match targets with these ingredients.\n\nAuto-tuning works best with a complete meal containing a reasonable mix of protein, fat, carbs, and ideally fiber sources.',
			mealOnTarget: 'The meal is now on target',
			copyFromNoMatchTitle: 'Nothing to copy',
			copyFromNoMatchMessage: 'There is no {mealName} logged on the day you picked. Pick a different day, or add the foods to this meal yourself.',
			cannotTuneLockedTitle: 'Nothing left to tune',
			cannotTuneLockedMessage: 'Some of this meal is locked or was entered as macros by hand, and Auto-Tune can change neither. What is left is not enough for it to work with.\n\nUnlock a food, or replace a hand-typed entry with a real food, and try again.',
		},
		toasts: {
			mealTuned: 'The meal was tuned.',
			undoTuneFailed: 'The meal could not be put back the way it was.',
			mealAlreadyOptimal: 'This meal is already as close to your targets as Auto-Tune can get it.',
		},
		errors: {
			couldNotSizeSuggestions: 'The meal suggestions could not be sized to your targets just now. Please try again.',
			autoTuneFailed: 'Auto-Tune ran into a problem and could not tune this meal. Please try again.',
		},
	},
	mealPlan: {
		title: 'Meal Planner',
		dailyMacroTargets: 'Daily Macro Targets',
		emptyState: {
			selectDatesTitle: 'Select dates to begin',
			selectDatesSubtext: 'Meal Planner auto-generates meals that match your daily macro targets. Select the days you want to generate meals for.',
		},
		labels: {
			startDate: 'Start Date',
			numberOfDays: 'Number of Days',
		},
		duration: {
			days7: '7 days',
			days14: '14 days',
			untilSunday: 'Until next Sunday',
			untilSaturday: 'Until next Saturday',
		},
		buttons: {
			generateMeals: 'Generate Meals',
			favorite: 'Favorite',
			dontSuggest: 'Don\'t Suggest Again',
			copyMeals: 'Copy Meals',
			acceptAll: 'Accept All',
			editMeal: 'Edit Meal',
			viewDetails: 'View Details',
		},
		exitDialog: {
			title: 'Exit Meal Planner',
			message: 'You haven\'t planned all your meals for your selected date range. How would you like to proceed?',
			saveAndExit: 'Save my current meal selections.',
			keepPlanning: 'Keep planning the rest of my meals.',
			discardAndExit: 'Exit & don\'t save my changes.',
		},
		errors: {
			saveFailed: 'Couldn\'t save your meal plan. Your planned days are still here — please try again.',
			dayCouldNotBeOpened: 'Couldn\'t open {day} for planning, so it was skipped. Your other days are not affected.',
		},
		copyDialog: {
			title: 'Copy Meal to Other Days',
			message: 'Select the days you would like to copy {meals} to.',
			selectAll: 'Select All',
			copyButton: 'Copy Meals',
			multipleMeals: '{count} meals',
		},
	},
	mealSuggestions: {
		title: 'Meal Suggestions',
		replaceMealTitle: 'Replace Meal',
		match: 'Match',
		targets: 'Targets',
		buttons: {
			viewRecipe: 'View Recipe',
			addMeal: 'Add Meal',
			replace: 'Replace',
		},
		errors: {
			serviceUnavailable: 'Can\'t reach the meal planning service, please try again later.',
			recipeLibraryNotReady: 'Setting up your recipe library — please try again in a minute.',
		},
	},
	editNutritionPlan: {
		title: 'Edit Nutrition Plan',
		description: 'You should only edit your nutrition plan if you know how many calories and macros you should consume based on your own tracking.',
		caloriesLabel: 'Calories',
		macronutrients: 'Macronutrients',
		autoAdjustLabel: 'Auto-adjust macros',
		autoAdjustTooltip: 'When enabled, macronutrient targets will automatically recalculate to maintain optimal ratios when you change your calorie target. When disabled, macro targets remain fixed regardless of calorie changes.',
		proteinLabel: 'Protein',
		carbsLabel: 'Carbohydrates',
		fatLabel: 'Fat',
		fiberLabel: 'Fiber',
		recommendedHint: 'Recommended: {value}',
		carbFatRatioLabel: 'Carb/Fat Calorie Ratio',
		resetRecommended: 'Reset Values',
		calorieOvershoot: 'Macro targets exceed your calorie goal',
		couldNotComputeTargets: 'We could not work out your nutrition targets right now, so this screen cannot be opened. Please try again in a moment.',
	},
	addMacros: {
		title: 'Log macros manually',
		defaultFoodName: 'My custom food',
		labels: {
			foodName: 'Food Name',
		},
		placeholders: {
			enterFoodName: 'Enter Food Name',
		},
		warnings: {
			caloriesMismatch: 'Calories don\'t match defined macros',
		},
	},
	reschedule: {
		labels: {
			date: 'Date',
			weighIn: 'Weigh-In',
			wakeUp: 'Wake-Up',
			time: 'Time',
			item: 'Item',
			meal: 'Meal',
		},
		weighInHelpText: 'Weigh yourself fasted in the morning after having gone to the bathroom.',
		weekdaySelect: {
			placeholder: 'Select weekday',
			currentBadge: 'Current',
		},
		options: {
			thisDayOnly: {
				title: 'Apply only to this day',
				description: 'Only apply to this {weekday}.',
			},
			allDays: {
				title: 'Apply to all days',
				descriptionWakeUp: 'Make this my wake-up time for all days.',
				descriptionWorkout: 'Make this my training time for all workouts.',
				description: 'Make this my {itemName} time for all days.',
			},
			thisWeekday: {
				title: 'Apply to this weekday',
				descriptionWorkout: 'Permanently move this workout to this time on {weekday}.',
				descriptionWakeUp: 'Make this my wake-up time on {weekday}s.',
				description: 'Make this my {itemName} time on {weekday}s.',
			},
		},
		title: 'Reschedule {item}',
		titleWakeUpWeighIn: 'Reschedule Wake-Up & Weigh-In',
		errors: {
			timeAlreadyUsed: 'This time is already used by Meal {mealNumber}. Pick another time.',
		},
	},
	pinnedNote: {
		viewTitle: 'Pinned Notes',
		description: 'Pinned notes stay visible every time you do this exercise.',
		placeholder: 'Ex. Write equipment tips or reminders you always want on hand.',
		title: 'Create Pinned Note',
		titleEdit: 'Edit Pinned Note',
	},
	setNote: {
		viewTitle: 'Set Note',
		title: 'Set Note',
		titleEdit: 'Edit Set Note',
		description: 'A Set Note is for 1 specific set of a workout. You can view it in the workout page and exercise history stats.',
		placeholder: 'Ex. Notes about this specific set...',
	},
	editIngredient: {
		title: 'Edit Ingredient',
		logMacrosTitle: 'Log Macros Manually',
		viewDetails: 'View Details',
		details: 'Details',
		date: 'Date',
		meal: 'Meal',
		foodName: 'Food Name',
		amount: 'Amount',
		foods: 'Foods',
		splitTitle: 'Split Into Individual Foods',
		splitDescription: 'This recipe is currently logged as a single item. Would you like to split it into its separate ingredients so you can adjust the amounts?',
		splitIntoFoods: 'Split Into Foods',
		splitAndAutoTune: 'Split & Auto-Tune Foods',
		splitMealDescription: 'Split this meal into individual foods and auto-tune their amounts to fit your targets.',
		splitMealDescriptionNoTargets: 'Split this meal into individual foods so you can adjust their amounts.',
		splitTuneNoSolutionToast: 'Your foods were split, but Auto-Tune couldn\'t find amounts that fit your targets. Try adding another food, or adjust the amounts yourself.',
		splitTuneNoSolutionLockedToast: 'Your foods were split, but some of this meal is locked or was entered as macros by hand, and Auto-Tune can change neither. Unlock a food or replace a hand-typed entry, then try again.',
		caloriesMismatchWarning: 'Based on the amount of macronutrients, this food should have about {expectedCalories:number} calories.',
		foodAddedToast: '{food} {amount} added to {meal}',
		foodAddedToastNoDestination: '{food} {amount} added',
		undoFailedToast: 'That food could not be removed again — the meal has changed since you added it.',
		inconsistentNutrientsWarning: 'The calories listed for this food do not match its protein, fat and carbs, which work out to about {expectedCalories:number} kcal per 100 g. Check the numbers before logging it, or pick a different entry.',
		refuseHint: {
			withParts: 'Weigh it without {parts}. About {percent:number}% of the weight as bought is not eaten.',
			percentOnly: 'Values are for the part you eat. About {percent:number}% of the weight as bought is not eaten.',
			partsOnly: 'Weigh it without {parts}.',
			withPartsRough: 'Weigh it without {parts} for an accurate amount. If you weigh it before, about {percent:number}% is usually removed, but this varies a lot.',
			percentOnlyRough: 'Values are for the part you eat. Weigh it after preparing it for an accurate amount: about {percent:number}% is usually removed, but this varies a lot.',
		},
		refuseParts: {
			shell: 'shell',
			tail: 'tail',
			bone: 'bone',
			skin: 'skin',
			fat: 'fat',
			peel: 'peel',
			pit: 'pit',
			seeds: 'seeds',
			core: 'core',
			stem: 'stem',
			leaves: 'outer leaves',
			rind: 'rind',
			pod: 'pod',
			husk: 'husk',
			cob: 'cob',
			top: 'top',
			root: 'root',
			trimmings: 'trimmings',
		},
		weighedWith: {
			withParts: 'Weighed with {parts}',
			asBought: 'Weighed as bought',
		},
		weighedWithNote: {
			typical: 'Counted as {weight} eaten: {percent:number}% of the weight is taken off.',
			rough: 'Counted as about {weight} eaten: usually about {percent:number}% is removed, but this varies a lot.',
		},
	},
	ingredientSearch: {
		addFood: 'Add Food',
		customFood: 'Custom food',
		emptyState: {
			title: 'Select Foods For Your Meal',
			titleRecipe: 'No Foods Added Yet',
		},
		buttons: {
			clearMeal: 'Clear Meal',
			lock: 'Lock',
			unlock: 'Unlock',
			viewAll: 'View All',
			addFoods: 'Add Foods',
			logMacrosManually: 'Log Macros Manually',
			autoSuggestions: 'Auto-Suggestions',
			editServingSize: 'Edit Serving Size',
			viewFoodDetails: 'View Food Details',
			splitIntoFoods: 'Split Into Foods',
			lockFood: 'Lock Food',
			unlockFood: 'Unlock Food',
			removeFood: 'Remove Food',
			reorder: 'Reorder',
			done: 'Done',
		},
		tooltips: {
			lockFood: 'Locking a food will keep it from being modified when auto-tuning the meal.',
		},
		alerts: {
			removeTitle: 'Remove ingredient?',
			removeMessage: 'Are you sure you want to remove {name}?',
		},
		errors: {
			foodDoesNotExist: 'Food doesn\'t exist',
		},
		titleMeal: 'Foods',
		repeatMeal: {
			sectionTitle: 'Repeat a Recent Meal',
			lastTimeIn: 'Last time in {meal}',
			add: 'Add Again',
			addedToast: '{count} {{food|foods}} added to {meal}',
			addedToastNoDestination: '{count} {{food|foods}} added',
			undoFailedToast: 'Those foods could not be removed again — the meal has changed since you added them.',
			couldNotRepeatToast: 'That meal could not be added — it no longer holds any foods.',
		},
		splitToast: 'Split into {count} {{food|foods}}',
		splitUndoFailedToast: 'The split could not be undone — the meal has changed since.',
	},
	editExercise: {
		titles: {
			createExercise: 'Create Exercise',
			editExercise: 'Edit {name}',
			viewExercise: '{name}',
			relativeStrengthStandard: 'Relative Strength Standard',
			absoluteStrengthStandard: 'Absolute Strength Standard ({gender})',
		},
		labels: {
			name: 'Name',
			id: 'ID',
			shortName: 'Short name',
			advancedSettings: 'Advanced Settings',
			instructions: 'Instructions',
			videoUrl: 'Video URL',
			equipmentTemplate: 'Equipment',
			bodyweightPercent: 'Bodyweight %',
			myoRep: 'Myo-rep exercise?',
			rmTesting: 'Use RM testing?',
			referenceExercise: 'Reference Exercise',
			oneRMFactor: '1RM Factor %',
			bodyWeight: 'Body Weight',
			similarExercise: 'Most Similar Exercise',
		},
		placeholders: {
			exerciseName: 'Enter exercise name',
			shortName: 'Enter short name',
			instructions: 'Enter exercise instructions',
			videoUrl: 'Paste YouTube or Vimeo link',
			selectTemplate: 'Select...',
			selectExercise: 'Select exercise',
		},
		hints: {
			bodyweightPercent: 'The percentage of your lifted bodyweight for the movement.',
			similarExercise: 'This determines the target musculature.',
		},
		buttons: {
			addImage: 'Add Image',
			previewInstructions: 'Preview Instructions',
			addAbsoluteStandard: 'Add Absolute Standard',
			addRelativeStandard: 'Add Relative Standard',
			addWeightCategory: 'Add Weight Category',
			importExercise: 'Import',
			refreshFromOriginal: 'Update',
			revertToOriginal: 'Revert to Original',
			checkForUpdate: 'Check for Update',
		},
		messages: {
			exerciseNotFound: 'This exercise is no longer available.',
			exerciseUnreadable: 'We couldn\'t load this exercise just now. Go back and try again in a moment.',
			sharedWithYou: 'This exercise was shared with you.',
			youOwnThisExercise: 'You are the author of this exercise.',
			updateAvailable: 'An updated version of this exercise is available.',
			originalUnavailable: 'The original shared exercise is no longer available.',
			importedExercise: 'Imported Exercise',
			basedOn: 'Based on: {name}',
		},
		toasts: {
			exerciseImported: 'Exercise Imported',
			importFailed: 'Failed to import exercise',
			exerciseRefreshed: 'Exercise Updated',
			refreshFailed: 'Failed to update exercise',
			exerciseCustomized: 'Exercise customized',
			revertedToOriginal: 'Reverted to original.',
			originalNoLongerAvailable: 'Original is no longer available.',
			couldNotReachExercises: 'We couldn\'t reach your exercises just now. Please try again in a moment.',
			checkForUpdateOffline: 'Could not check for updates — you appear to be offline.',
			partialUpdateApplied: '{count} change(s) applied from source.',
			saveFailed: 'Failed to save exercise',
		},
		alerts: {
			saveAndExit: {
				title: 'Save and exit?',
				message: 'Are you sure you want to save your edits and exit the page?',
			},
			revertToOriginal: {
				title: 'Revert to original?',
				message: 'Discard your changes and restore the original?',
			},
			updateAvailable: {
				title: 'Update Available',
				acceptButton: 'Accept Changes',
				ignoreButton: 'Ignore',
				cancelButton: 'Cancel',
				updateRevisionButton: 'Update Revision',
				noVisibleChangesBody: 'No user-visible changes.',
				alreadyUpToDateToast: 'Already up to date',
				pickerApplyButton: 'Apply ({count})',
				pickerIgnoreButton: 'Ignore',
				pickerCheckboxHint: 'Select the changes you want to take from the source.',
				pickerCurrentDescription: 'Current description',
				pickerUpdatedDescription: 'Updated description',
				toggledOn: 'Toggled on',
				toggledOff: 'Toggled off',
				descriptionChanged: 'Description changed',
				standardChanged: 'Strength standard changed',
				empty: '(empty)',
				fieldDiffLabels: {
					name: 'Name',
					shortname: 'Short name',
					description: 'Description',
					videoUrl: 'Video URL',
					bw_pc: 'Bodyweight %',
					allowMyoReps: 'Myo-reps allowed',
					avoidRMTesting: 'Avoid RM testing',
					standard: 'Strength standard',
					equipmentTemplateId: 'Equipment',
					similarExerciseId: 'Similar exercise',
				},
			},
		},
		errors: {
			invalidName: 'Invalid name',
			unknownButton: 'Unknown equipment edit button {button}',
		},
	},
	exerciseSwitch: {
		titles: {
			changeExercise: 'Change Exercise',
			addExercise: 'Add Exercise',
			pickExercise: 'Pick Exercise',
		},
		placeholders: {
			searchExercise: 'Type an exercise name...',
		},
		filters: {
			all: 'All',
			recommended: 'Recommended',
			myExercises: 'My Exercises',
			suggested: 'Suggested',
			filterByCategory: 'Filter by category',
		},
		messages: {
			recommendationInfo: 'We recommend the following exercises as a replacement, from most to least recommended.',
		},
		buttons: {
			statsAndNotes: 'Stats And Notes',
			description: 'Description',
			modify: 'Modify',
			delete: 'Delete',
			share: 'Share',
			checkForUpdate: 'Check for Update',
			update: 'Update',
			createExercise: 'Create Exercise',
			save: 'Save',
			add: 'Add',
		},
		indicators: {
			updateAvailable: 'Update available',
		},
		emptyState: {
			noResultsTitle: 'No results found',
			noResultsSubtext: 'Try a different search term',
		},
		alerts: {
			deleteExercise: {
				title: 'Delete Exercise',
				message: 'Are you sure you want to delete "{name}"? {extra}',
				neverUsed: 'It was never used in a workout, so it will be deleted forever.',
				wasUsed: 'It was used in a workout. It will be marked as deleted and will not appear in the list of exercises, but you can restore it later.',
			},
		},
		removeExercise: {
			title: 'Remove Exercise',
			message: 'Are you sure you want to remove {exercise} from Workout {workout}?',
		},
		linkWorkouts: {
			title: 'Share Progression?',
			message: '{exercise} is already in {workouts}. If you use it here too, both workouts share one progression: the same weights and reps, and a later change to this exercise in one workout also changes it in the other.',
			workoutName: 'Workout {workout}',
			share: 'Share Progression',
		},
		alreadyInWorkout: {
			title: 'Already in This Workout',
			message: '{exercise} is already in Workout {workout}. Pick a different exercise, or remove the one you\'re changing instead.',
		},
		errors: {
			workoutNotFound: 'Workout {workoutId} not found',
			noSelectedItem: 'No selected item when accepting exercise switch',
		},
		coachBadge: {
			label: 'Coach',
			info: 'From your coach {coachName}',
			infoNoName: 'From your coach',
		},
	},
	editTrainingProgram: {
		labels: {
			sets: '{count} {{set|sets}}',
		},
	},
	editWorkouts: {
		title: 'Edit Workouts',
		setsCount: '{count} Sets',
		rmTesting: 'RM Testing',
		myoReps: 'Myo-Reps',
		repsCount: '{count} reps',
		suggestedRepsCount: '~{count} reps',
		dupRepsCount: '{heavy}/{light} reps',
		suggestedDupRepsCount: '~{heavy}/{light} reps',
		tupRepsCount: '{heavy}/{medium}/{light} reps',
		suggestedTupRepsCount: '~{heavy}/{medium}/{light} reps',
		buttons: {
			save: 'Save',
			revert: 'Revert',
			deleteWorkout: 'Delete Workout {name}',
		},
		menu: {
			volumeOptimization: 'Volume Optimization',
			scanWorkout: 'Scan Workout',
			removeExercise: 'Remove Exercise',
			moveExercise: 'Move Exercise',
			moveExerciseTo: 'Move Exercise to...',
			workoutName: 'Workout {name}',
		},
		deleteWorkout: {
			confirmTitle: 'Delete Workout',
			confirmMessage: 'Are you sure you want to delete Workout {name}? All exercises in this workout will be removed.',
			cannotDeleteTitle: 'Cannot Delete',
			cannotDeleteMessage: 'You must have at least one workout.',
		},
		removeExercise: {
			cannotRemoveLastTitle: 'Cannot Remove',
			cannotRemoveLastMessage: 'Your plan needs at least one exercise, otherwise every workout would be empty. Add another exercise first, or swap this one for a different exercise instead.',
		},
		revert: {
			title: 'Revert Changes',
			message: 'Choose how you want to revert your changes:',
			undoMessage: 'This will undo all changes made in this session.',
			undoButton: 'Undo',
			undoSession: 'Undo Session Changes',
			restoreOriginal: 'Restore Original Plan',
			noChangesTitle: 'No Changes',
			noChangesMessage: 'You haven\'t made any changes in this session.',
			notAvailableTitle: 'Not Available',
			notAvailableMessage: 'Original plan backup is not available for plans created before this feature was added.',
			confirmRestoreTitle: 'Restore Original Plan',
			confirmRestoreMessage: 'This will restore your workouts to the plan from intake or last program update. All changes made after that will be lost.',
			restoreButton: 'Restore',
		},
		toast: {
			exerciseMoved: 'Exercise moved to Workout {workout}. ',
			view: 'View',
		},
		alerts: {
			reoptimizeConfirm: {
				message: 'Do you want the app to update the set volumes for your exercises to match your target weekly volume per muscle group? Your exercise order and workout split will not be affected. Only your sets per exercise will change.',
				yes: 'Yes, Reoptimize My Volume',
				no: 'No, Don\'t Update My Exercise Volumes',
			},
			reoptimizeResult: {
				title: 'Volume Reoptimized',
				noChanges: 'Your set volumes already match your targets.',
			},
			reoptimizeLoading: 'Reoptimizing volume...',
		},
		exercisesLoadFailed: {
			title: 'Couldn\'t Load Your Exercises',
			message: 'Your exercises could not be loaded just now. Please check your connection and tap Try Again.',
			saveRefused: 'Your exercises could not be loaded, so nothing was saved yet. Your changes are kept. Please check your connection and try again.',
		},
		emptyWorkout: {
			title: 'Empty Workout',
			message: 'Workout {name} has no exercises. Add an exercise to it, or delete it. An empty workout would show up as a blank day in your training.',
			messageSeveral: 'Workouts {names} have no exercises. Add exercises to them, or delete them. An empty workout would show up as a blank day in your training.',
			deleteSeveral: 'Delete Empty Workouts',
		},
	},
	exerciseConfiguration: {
		loadFailed: {
			title: 'Couldn\'t Load Exercise',
			message: 'This exercise could not be loaded just now. Please check your connection and try again.',
		},
		labels: {
			muscleGroup: 'Muscle Group',
			myoReps: 'Myo-Reps',
			equivalentVolume: 'Equivalent Volume',
			volume: 'Volume',
			myoRepsVolume: 'Myo-Reps Volume',
			rmTesting: 'RM Testing',
			repTarget: 'Rep Target',
			heavyRepTarget: 'Rep Target (Heavy)',
			mediumRepTarget: 'Rep Target (Medium)',
			lightRepTarget: 'Rep Target (Light)',
			toBeDetermined: 'To be determined',
		},
		options: {
			avoid: 'Avoid',
			prefer: 'Prefer',
			auto: 'Automatic',
		},
		buttons: {
			save: 'Save',
		},
		myoRepsVolumeOneSeries: '1 activation set and {minisets} minisets',
		myoRepsVolumeMultipleSeries: '{count} series of activation set + {minisets} mini-sets',
		repTargetInfo: 'The rep target will be determined during your next workout for this exercise.',
		repTargetInfoRMTesting: 'The rep target will be determined after RM testing is complete.',
		repTargetHint: {
			atLeast: 'Must be at least {min} reps',
			atMost: 'Must be at most {max} reps',
			between: 'Must be between {min} and {max} reps',
			dupBanner: 'Heavy and light sessions use different rep targets. Keep them at least {gap} reps apart.',
			tupBanner: 'Heavy, medium, and light sessions use different rep targets. Keep adjacent sessions at least {gap} reps apart.',
		},
		toast: {
			repTargetRequired: 'Rep target cannot be empty.',
			repTargetTooLow: 'Rep target must be at least {min} reps for this exercise.',
			dupLightTooLow: 'Light rep target must be at least {min} reps (heavy + {gap}).',
			dupHeavyTooHigh: 'Heavy rep target must be at most {max} reps (light - {gap}).',
			tupLightTooLow: 'Light rep target must be at least {min} reps (medium + {gap}).',
			tupMediumTooHigh: 'Medium rep target must be at most {max} reps (light - {gap}).',
			tupMediumTooLow: 'Medium rep target must be at least {min} reps (heavy + {gap}).',
			tupHeavyTooHigh: 'Heavy rep target must be at most {max} reps (medium - {gap}).',
		},
		coach: {
			intensity: 'Intensity',
			oneTarget: 'One Target',
			nextSessionOnly: 'Next Session Only',
			nextWeight: 'Next Weight',
			nextMinReps: 'Next Minimum Reps',
			dayHeavy: 'Heavy Day',
			dayMedium: 'Medium Day',
			dayLight: 'Light Day',
			dayOne: 'Every Session',
			comesNext: 'This day comes next, in the next {exercise} session.',
			nextSessionNote: 'The app sets these after every session. A change applies to the next session only.',
			lastSession: 'Last session: {weight} × {reps} on {date}.',
			calculatedFrom: 'Weights worked out from the best recent set, {weight} × {reps} on {date}. You can change them.',
			strengthTestNext: 'Decided by a strength test in the next session.',
			calculateWeights: 'Calculate Weights',
			historyUnreadable: 'The recent workouts could not be read, so the intensity cannot be changed right now. Please try again later.',
			minRepsInvalid: 'Must be at least 1 rep, or empty.',
			strengthAtLastDeload: 'Strength at Last Deload',
			deloadStrength: '{strength}. A plateau is called if a deload comes below {limit}.',
			deloadStrengthNone: 'None yet.',
			plateauWaiting: 'Plateau question waiting since {date}. {client} has not answered yet. Changing the intensity or the exercise answers it.',
			plateauWillAnswer: 'Saving answers the plateau question waiting since {date}. {client} will not be asked.',
			volumeWaiting: 'Volume question waiting since {date}. {client} has not answered yet. Changing the volume answers it.',
			volumeWillAnswer: 'Saving answers the volume question waiting since {date}. {client} will not be asked.',
			setAside: 'Set Aside',
			setAsideNone: 'No',
			setAsidePlateau: '{exercise} was set aside on {date} after a plateau, until {until}.',
			setAsideWeakness: '{exercise} was set aside on {date} after a weakness result, until {until}.',
			bringBackNow: 'Bring Back Now',
			broughtBack: '{exercise} will be available again once you save.',
			changes: 'Changes',
			changesNone: 'No changes yet.',
			changeBy: '{who}: {what}',
			app: 'App',
			event: {
				plateau: 'Plateau detected',
				deload: 'Deload',
				weakness: 'Weakness result',
				volumeUp: 'Volume raised by 1 set',
				volumeDown: 'Volume lowered by 1 set',
			},
			field: {
				scheme: 'Intensity {before} → {after}',
				targetReps: '{day} rep target {before} → {after}',
				targetWeight: '{day} next weight {before} → {after}',
				minReps: '{day} next minimum reps {before} → {after}',
				volume: 'Volume {before} → {after} sets',
				myo: 'Myo-Reps changed',
				rmTesting: 'RM Testing changed',
				setAsideCleared: 'Set-aside exercise brought back',
				plateauAnswered: 'Plateau question answered',
				volumeQuestionAnswered: 'Volume question answered',
				none: '–',
			},
			advanced: 'Advanced',
			plateauWaitingSelf: 'Plateau question waiting since {date}. Changing the intensity or the exercise answers it.',
			plateauWillAnswerSelf: 'Saving answers the plateau question waiting since {date}.',
			volumeWaitingSelf: 'Volume question waiting since {date}. Changing the volume answers it.',
			volumeWillAnswerSelf: 'Saving answers the volume question waiting since {date}.',
		},
	},
	equipment: {
		sections: {
			incrementWeights: 'Increment Weights',
			barbellWeight: 'Barbell Weight',
			stackedWeights: 'Stacked Weights',
			dumbbellWeights: 'Dumbbell Weights',
			plates: 'Plates',
		},
		stack: {
			additionalMicroloading: 'Additional "microloading" (usually magnetic) plates that you can attach to this stack if needed. Make one entry for each plate.',
			additionalMicroloadingWeights: 'Additional "microloading" (usually magnetic) plates that you can attach to the weights if needed. Make one entry for each plate.',
			unsortedWeights: 'The weights are unsorted. They will be reordered when saving. Or you can press Clean Up on the right to do it now.',
		},
		labels: {
			template: '[Template]',
			usedIn: 'Used in: {exercises}',
			minimumWeight: 'Minimum weight',
			weightIncrement: 'Weight increment',
			bar: 'Bar',
			from: 'From:',
			to: 'To:',
			increments: 'Increments:',
			collarWeightOptional: 'Collar Weight (Optional)',
			individualWeight: 'Individual Weight',
			noPlatesYet: 'No plates yet',
			filters: 'Filters',
			equipmentType: 'Equipment Type',
			details: 'Details',
			name: 'Name',
			units: 'Units',
			microPlatesOptional: 'Micro Plates (Optional)',
			platesSummary: '{count} x {weight} plates',
			platesSummaryPairs: '{count} x {weight} pairs',
			numberOfPairs: 'Number of Pairs',
			numberOfPlates: 'Number of Plates',
			myEquipmentName: 'My {name}',
		},
		summary: {
			manual: 'Manually specified with minimum of {min} and {inc} increments',
			bar: '{bar} barbell with {plates} plates (in pairs)',
			barWithCollars: '{bar} barbell with {collars} collars and {plates} plates (in pairs)',
			stack: 'Stack machine selecting from {weights}',
			freePlates: 'Free plates: {plates}',
			withMicroloading: '{base} with microloading {plates}',
			noPlates: 'no plates',
			unknown: 'Unknown equipment',
		},
		templates: {
			barbell: 'Barbell',
			dumbbells: 'Dumbbells',
			plainPlates: 'Plain plates',
			freePlates: 'Free plates',
			kettlebells: 'Kettlebells',
			cableMachine: 'Cable machine',
			customIncrements: 'Custom increments',
		},
		titles: {
			changeEquipment: 'Change Equipment',
			equipment: 'Equipment',
			createEquipment: 'Create Equipment',
		},
		placeholders: {
			search: 'Search',
			enterName: 'Enter name',
			noStackedWeights: 'No stacked weights set. Add one below.',
			noWeightsSet: 'No weights set. Add one below.',
			selectTemplate: 'Select...',
		},
		filters: {
			all: 'All',
			suggested: 'Suggested',
			showTemplates: 'Show Templates',
			showMine: 'Show Mine',
		},
		descriptions: {
			barPlates: 'All pairs of plates that you have available for this bar. Make one entry for each pair.',
			plates: 'Free plates without a bar (like for putting on a machine or hanging on a belt). Plates are used one at a time, not in pairs.',
			stack: 'Stacked weights in a strict order. Usually on a machine. With microloading plates if you have them.',
			manual: 'Use this for weights that you will arrange yourself manually. Must be able to arrange all possible weights in the given increments.',
		},
		notes: {
			barWeight: 'Weight of the bar itself.',
			bothCollars: 'Both collars together weigh {weight}',
			keepEmptyCollars: 'Keep empty if you\'re using very light or no collars (as in most gyms).',
		},
		buttons: {
			delete: 'Delete',
			duplicate: 'Duplicate',
			modify: 'Modify',
			create: 'Create',
			createEquipment: 'Create Equipment',
			addStackedWeight: 'Add Stacked Weight',
			addDumbbells: 'Add Dumbbells',
		},
		messages: {
			wouldYouLike: 'Would you like to',
			noEquipmentDefined: 'You haven\'t defined any equipment yet.',
			showingOnlyCustom: 'Showing only your custom equipment. There\'s {count} items hidden.',
			toCreateEquipment: 'you can use to create your equipment',
		},
		emptyState: {
			noResultsTitle: 'No Results Found',
			noResultsSubtext: 'We couldn\'t find any matches for your search. Try adjusting your keywords or filters and search again.',
			chooseTemplate: {
				title: 'Choose a Template to Begin',
				subtext: 'Select an equipment template above to unlock additional fields and create your new equipment type.',
			},
		},
		links: {
			showTemplates: 'show the templates',
		},
		alerts: {
			deleteEquipment: {
				title: 'Delete equipment?',
				subtitle: 'Are you sure you want to delete {name}?',
				usedBy: 'It is used in: {exercises}',
			},
		},
		errors: {
			unknownMode: 'Unknown equipment search mode {mode}',
			unknownButton: 'Unknown equipment search button {button}',
			attemptToApplyTemplate: 'Attempt to apply template {id} as equipment',
		},
		tutorial: {
			nameEquipment: 'This is your new equipment based on an official template. Give it a name you will recognize.',
			adjustValues: 'These are just example values. Make sure to adjust them to your specific equipment!',
			saveEquipment: 'When you\'re sure the equipment is correct, tap here to save it.',
		},
		validation: {
			invalidName: 'Invalid name',
			incrementHelp: 'Set the "increment" to the smallest amount that you can increase the weight by. E.g. if the smallest plate on a barbell is 0.5 kg, then set the increment to 1 kg - because you will have to add two plates.',
			minimumHelp: 'Set the minimum to the smallest weight you can use for this exercise. E.g. weight of an empty barbell.',
			barWeightRequired: 'Bar weight is required.',
			unknownField: 'Unknown field',
			unknownEquipmentType: 'Unknown type of equipment',
		},
	},
	subscribe: {
		title: 'Subscribe',
		restorePurchases: 'Restore Purchases',
		subscriptionTerms: 'Payment will be charged to your Apple ID account at the end of any free trial. Subscription automatically renews at the listed price unless cancelled at least 24 hours before the end of the current period. Manage or cancel anytime in your Apple ID settings.',
		privacyPolicy: 'Privacy Policy',
		eula: 'EULA',
		titles: {
			selectSubscription: 'Select Subscription',
			tryFree: 'Try Free for {period}',
			payment: 'Payment',
			subscribe: 'Subscribe',
			startFreeTrial: 'Start Free Trial',
		},
		messages: {
			paymentInitiated: 'Looks like you have initiated a payment that you have not fully confirmed yet.',
			confirmPayment: 'Please confirm the payment with your bank and then ',
			thisPage: ' this page.',
			paymentIssues: 'In case of any issues with payments, please feel free to contact ',
			subscriptionInactive: 'Your subscription is inactive. If you wish to renew your subscription, please update your payment information.',
			coachLapsedTitle: 'Your Premium access is paused',
			coachLapsed: '{coachName} was covering your Premium access, and that access is paused for now.',
			coachLapsedNoName: 'Your coach was covering your Premium access, and that access is paused for now.',
			coachLapsedStillConnected: 'You are still connected to your coach. Nothing about your coaching has been removed, and your access comes back automatically as soon as your coach\'s cover is active again.',
			coachLapsedSubscribe: 'You can also start your own subscription below.',
			noExpiration: 'Your subscription has no expiration date, therefore you don\'t need to extend it.',
			ifPersists: 'If this persists, contact ',
			alreadyHaveAccountPrefix: 'Already have an account?',
			signIn: 'Sign In',
			crossPlatform: 'Your subscription is active. To manage it, use the device you subscribed on.',
			membershipPaused: 'Your membership is paused, so it is not active right now. Resume it from your profile instead of buying a new subscription.',
			crossPlatformWeb: 'Your subscription is active. To manage it, sign in on our website.',
			signedInAsPrefix: 'You are signed in as',
			signedInAsSuffix: '.',
			alreadyHaveSubscriptionPrefix: 'Paid with a different email address?',
			alreadyHaveSubscriptionLink: 'Get a sign-in link',
			alreadyHaveSubscriptionSuffix: 'for that account.',
			installAppPromptTitle: 'Your subscription is active',
			installAppPromptBody: 'Want to use MH Physique on your phone too?',
			continueAfterPurchase: 'Continue',
			noStoreProductsTitle: 'Subscription options couldn\'t be loaded',
			noStoreProductsBody: 'We couldn\'t load the subscription options on this device right now. Our team has been notified. Please try again later, or contact us for help getting subscribed.',
		},
		links: {
			reload: 'reload',
		},
		labels: {
			savePrefix: 'Save',
			trialDisclosure: '{period} free trial, then {price}/{recurrence}',
		},
		recurrence: {
			month: 'month',
			year: 'year',
		},
		trialPeriod: {
			days: '{count:number} {{day|days}}',
			weeks: '{count:number} {{week|weeks}}',
			months: '{count:number} {{month|months}}',
			years: '{count:number} {{year|years}}',
		},
		code: {
			hint: 'A friend\'s code, an access code or a sale code',
			hintApp: 'An access code',
			friendApplied: 'Friend\'s code applied: your first month is free.',
			removeCode: 'Remove Code',
			notRecognised: 'We don\'t recognise this code. Check it and try again.',
			newAccountsOnly: 'This offer is for new accounts only.',
			ownCode: 'You can\'t use your own code.',
			alreadyReferred: 'You have already used a friend\'s invite.',
			alreadyUsed: 'This code has already been used.',
			addsNothing: 'This code would not add anything to your access.',
			notValidYet: 'This code can\'t be used yet. Try again in {days:number} {{day|days}}.',
			tryAgain: 'Something went wrong. Please try again.',
			mistyped: 'This code doesn\'t look right. Check it for typos.',
			incomplete: 'This code looks incomplete. Check it for typos.',
			signedOutFriend: 'This looks like a friend\'s invite code. Create an account or sign in to use it.',
			signedOutAccess: 'This looks like an access code. Create an account or sign in to redeem it.',
			appOnly: 'Google Play\'s rules don\'t allow promo codes on in-app purchases.',
			confirmTitle: 'Redeem This Code?',
			confirmMessage: 'This access code will be added to {email}. It gives access without payment and can be used once.',
		},
		promo: {
			haveCode: 'Have a code?',
			codePlaceholder: 'Code',
			applyButton: 'Apply',
			appliedOneNamed: '{name} code {code} applied: {percent:number}% off the first {period} of {plan}',
			appliedOne: 'Code {code} applied: {percent:number}% off the first {period} of {plan}',
			appliedSeveralNamed: '{name} code {code} applied: see the sale prices below',
			appliedSeveral: 'Code {code} applied: see the sale prices below',
			periodYear: 'year',
			periodMonth: 'month',
			planYearly: 'Yearly',
			planMonthly: 'Monthly',
			percentOff: '{percent:number}% off',
			perMonth: '{price} a month',
			thenYear: 'first year, then the regular {plan} price',
			thenMonth: 'first month, then the regular {plan} price',
			planTrial: 'Starts with {period} free',
			moneyBack: 'No trial on the sale price: you pay today and can get your money back within 30 days, no questions asked. The sale ends {date}.',
			expired: 'This code has expired',
			ended: 'The sale has ended',
			checkFailed: 'The code could not be checked. Please try again.',
		},
		notes: {
			billingInfo: 'Billed at the start of every cycle. Plans renew automatically. Change plans or cancel any time.',
			billingInfoTrial: '{period} free, then billed at the start of every cycle. Plans renew automatically. Change plans or cancel any time.',
		},
		alerts: {
			emailMismatch: {
				title: 'Email Mismatch',
				message: 'You cannot pay with a different email address than your account email. Please use your account email or sign out first.',
			},
			accountExists: {
				title: 'Account Already Exists',
				message: 'This email address is already registered to an account. We need to sign you in first...',
			},
			cantVerifyEmail: {
				title: 'Can\'t Verify Email',
				message: 'Unable to contact our registration server. Please try again later or contact [support@mh-physique.app](mailto:support@mh-physique.app).',
			},
			checkEmail: {
				sendLinkTitle: 'Get a Sign-In Link',
				message: 'Enter the email address you paid with, and we\'ll send a sign-in link to it.',
				sendLink: 'Send Link',
				resendFailedTitle: 'Couldn\'t Send the Email',
				resendFailedMessage: 'We couldn\'t send the sign-in email. Please check your connection and try again.',
			},
			pricingFailed: {
				title: 'Failed to Load Pricing',
				message: 'Could not load subscription pricing. Please check your connection and try again.',
			},
			pricingOffline: {
				title: 'You\'re Offline',
				message: 'Subscription plans can\'t load without an internet connection. Connect to the internet and try again.',
				tryAgain: 'Try Again',
			},
			pricingUnavailable: {
				title: 'Couldn\'t Load Plans',
				message: 'Subscription plans couldn\'t load. An ad blocker or network restriction may be blocking them. Try again, or disable any ad blocker for this site.',
			},
			checkoutUnavailable: {
				title: 'Couldn\'t Open Checkout',
				message: 'The payment form couldn\'t be opened. You have not been charged. Please try again. If this keeps happening, contact support@mh-physique.app.',
			},
			purchaseFailed: {
				title: 'Subscription Purchase Failed',
				message: 'Could not complete the subscription purchase. Please try again.',
			},
			purchaseNotYetConfirmed: {
				title: 'Purchase Complete — Activating',
				message: 'Your purchase went through. We couldn\'t finish confirming it just now, but it should activate by itself within a few minutes. There\'s no need to buy again. If it hasn\'t activated, reopen the app or tap Restore Purchases.',
			},
			restoreFailed: {
				title: 'Restore Failed',
				message: 'No existing purchases found. If you believe this is an error, please contact support.',
			},
			restoreNotYetConfirmed: {
				title: 'Restore In Progress',
				message: 'The store found your purchase, but we couldn\'t finish confirming it just now. It should activate by itself within a few minutes. There\'s no need to buy again.',
			},
			alreadyLinked: {
				title: 'Subscription Linked to Another Account',
				message: 'This store subscription is already linked to {email}. To use it, sign out and sign in with that account.',
				signOut: 'Sign Out',
				unknownAccount: 'another account',
			},
			otherAccount: {
				title: 'Purchase Made on Another Account',
				message: 'This purchase was made while signed in to a different MH Physique account. To use it, sign in with that account. If that account no longer exists, contact support and we\'ll help you get access on this one.',
			},
			storeRefused: {
				title: 'Purchase Couldn\'t Be Confirmed',
				message: 'The store didn\'t accept our request to confirm this purchase, so it hasn\'t been added to your account. Please don\'t buy again. If you were charged, contact support@mh-physique.app and we\'ll sort it out.',
			},
			registrationSlow: {
				title: 'Payment Registration Delayed',
				message: 'Your payment appears to have been processed, but registration is taking longer than expected. Please tap Retry to reload. If the issue persists, contact support@mh-physique.app.',
			},
			paymentBeingConfirmed: {
				title: 'Payment Being Confirmed',
				message: 'Your payment is still being confirmed. You\'ll get an email when it\'s processed.',
			},
		},
		temporaryInfoCard: {
			message: 'Payment being confirmed — your payment is in PayPro\'s manual-review queue. You have full access while we wait. Most reviews complete in a few minutes.',
		},
		errors: {
			unexpectedCheckout: 'Unexpected event during checkout.',
			checkoutAwaitingProcessing: 'Your payment is still awaiting processing. Did you confirm the payment with your bank?',
			checkoutDeclined: 'Payment was declined by your bank or payment provider.',
		},
	},
	unsubscribe: {
		title: 'Why are you leaving?',
		reasons: {
			highPrice: 'The price is too high.',
			noLongerNeed: 'I no longer need this product',
			notSatisfied: 'I am not satisfied with the product.',
			switchedProduct: 'I switched to another product',
			notPriority: 'It isn\'t a priority right now.',
			mistake: 'I subscribed by mistake.',
			anotherPlan: 'I am switching to another subscription plan.',
			other: 'Other reasons.',
		},
		placeholders: {
			customReason: 'I wish you would... (Please explain your reason here.)',
		},
		buttons: {
			unsubscribe: 'Unsubscribe',
		},
		alerts: {
			success: 'You have successfully unsubscribed. Your subscription will stop on {date}.',
			alreadyCanceled: 'Your subscription has already been canceled. No further action is needed.',
			unsubscribeFailed: {
				title: 'We couldn\'t cancel right now',
				message: 'Your subscription is still active. Please try again in a moment, or contact support if this keeps happening.',
				retry: 'Retry',
				contactSupport: 'Contact Support',
			},
		},
	},
	redeemCode: {
		title: 'Redeem Code',
		labels: {
			enterCode: 'Enter code:',
		},
		placeholders: {
			codeFormat: 'ABCD-EFGH-1234-56789',
		},
		notes: {
			codeWillBeRedeemed: 'The code you enter will be applied to your account: \'\'{email}\'\'.',
		},
		buttons: {
			redeem: 'Redeem',
		},
		loading: {
			redeemingCode: 'Redeeming code...',
		},
		alerts: {
			successfullyRedeemed: 'Successfully redeemed!',
		},
		errors: {
			loginFailed: 'The key seems valid, but the new sign-in failed for an unknown reason. Please restart the app and try again or contact support.',
			activationFailed: 'The key seems valid, but the activation failed for an unknown reason. Please restart the app and try again or contact support.',
		},
	},
	referral: {
		settings: {
			inviteRow: 'Invite a Friend',
		},
		disclosure: 'The friend who invited you will see when you join, when you start training, and if you subscribe.',
		invite: {
			headerTitle: 'You have been invited',
			checking: 'Checking your invite...',
			title: '{name} gave you a free month',
			subtitle: 'A free month on Menno Henselmans\' app. Start whenever you\'re ready.',
			startButton: 'Start my free month',
			continueButton: 'Continue',
			invalidTitle: 'This invite link isn\'t valid any more.',
			invalidSubtitle: 'Ask your friend for a new link, or create an account without one.',
			invalidButton: 'Create an account',
		},
		hub: {
			title: 'Give a friend a free month',
			subtitle: 'Send someone a free month on the app. If they stick with it, you\'ll know you got them training properly.',
			messageLabel: 'Your message',
			linkLabel: 'Your invite link',
			codeLabel: 'Or give them this code',
			defaultMessage: 'Been using Menno Henselmans\' training app. Here\'s a free month if you want to try it:',
			shareButton: 'Share your invite',
			copiedToClipboard: 'Invite copied to your clipboard.',
			copyFailed: 'We could not copy your invite. You can select the link above and copy it by hand.',
			loadFailed: 'We could not get your invite link. Please check your connection and try again.',
			retryButton: 'Try again',
			wereYouInvited: {
				title: 'Were you invited?',
				subtitle: 'Enter your friend\'s code and we will record who invited you.',
				placeholder: 'Enter code',
				submitButton: 'Apply code',
				accepted: 'Thanks. We have recorded who invited you.',
				refusals: {
					unknownInvite: 'We don\'t recognise that code. Check it with the friend who sent it to you.',
					selfReferral: 'That is your own code. Send it to a friend instead.',
					alreadyReferredByAnother: 'Somebody else is already recorded as the friend who invited you.',
					hasSubscription: 'This offer is for brand-new accounts only.',
					accountTooOld: 'This offer is for brand-new accounts only.',
					inviterGone: 'That code no longer belongs to anyone.',
					unexpectedError: 'Something went wrong. Please try again.',
				},
			},
			friends: {
				title: 'Friends you\'ve brought in',
				count: '{count:number} {{friend|friends}} so far',
				empty: 'Nobody yet. Share your invite and they will show up here.',
				loadFailed: 'We could not load your friends. Please check your connection and try again.',
				noName: 'A friend',
				statusJoined: 'Joined',
				statusTraining: 'Training',
				statusMember: 'Member',
			},
		},
		subscribe: {
			freeMonthApplied: 'Your free month from your friend is applied.',
		},
	},
	bodyfatInput: {
		errors: {
			missingSkinfold: 'Please enter at least one value for the \'{skinfold}\' skinfold.',
			genderRequired: 'Need to specify gender for bodyfat calculations.',
			tapeAllValues: 'Please enter all values for tape measurement.',
			tapeInvalid: 'Invalid data for tape measurement.',
			noMethod: 'No method chosen for bodyfat % calculation.',
			valuesOutOfRange: 'Please double check those values. If that\'s correct, you should seek help of a medical professional.',
		},
		methods: {
			visual: 'Visual',
			caliper: 'Caliper',
			tape: 'Tape',
		},
		descriptions: {
			visual: 'Use the images below to estimate your percentage.',
		},
		labels: {
			bodyFatPercentage: 'Body fat percentage',
			take5Measurements: 'Take 5 measurements of each skinfold site.',
			neckCircumference: 'Neck circumference',
			abdomenCircumference: 'Abdomen circumference',
			waistCircumference: 'Waist circumference',
			hipsCircumference: 'Hips circumference',
		},
		helpText: {
			neckCircumference: 'Measure below the Adam\'s apple at the smallest point.',
			abdomenCircumference: 'Measure around the belly button at the widest point.',
			waistCircumference: 'Measure around or above the belly button at the narrowest point.',
			hipsCircumference: 'Measure around the widest point of the hips/glutes.',
		},
		links: {
			howToMeasure: 'How do I measure skinfolds?',
		},
		warnings: {
			roughEstimate: 'Tape is surprisingly useful to track your body fat level, but if you can get calipers, they\'re even more useful.',
		},
	},
	sendCoachMessage: {
		title: 'Message Coach',
		placeholder: 'Write a message to your coach...',
		addScreenshot: 'Add Screenshot',
		send: 'Send',
		sentDescription: 'Your coach will receive your message by email.',
	},
	sendFeedback: {
		title: 'Send Feedback',
		headers: {
			howCanWeHelp: 'How can we help you today?',
		},
		messages: {
			thankYouForFeedback: 'Thank you for your feedback! We\'ve sent you an email confirmation.',
			couldNotSend: 'We could not send your feedback. Your message is still here — please check your connection and try again.',
			noTitle: 'No title',
		},
		buttons: {
			submit: 'Send Feedback',
			addScreenshot: 'Add Screenshot',
		},
		writeFeedback: {
			title: 'Write Your Feedback',
			subtitle: 'We\'ll contact you at {email} if needed.',
		},
		types: {
			question: {
				title: 'Ask a question',
				description: 'We\'re happy to help you with any questions you may have.',
				placeholder: 'Ask anything about your program, or about the app in general and we\'ll get back to you as soon as possible.',
			},
			like: {
				title: 'I like something',
				description: 'We\'re happy to hear that you like something in our app.',
				placeholder: 'Let us know what you like about the app, or about your program.',
			},
			dislike: {
				title: 'I don\'t like something',
				description: 'If you don\'t like something, please let us know how we can improve it.',
				placeholder: 'Let us know what you don\'t like about the app, or about your program.',
			},
			issue: {
				title: 'Report an issue',
				description: 'Found a bug? Let us know and we\'ll fix it as soon as possible.',
				placeholder: 'Please describe what the issue looks like and how to reproduce it (if possible).',
			},
			suggestion: {
				title: 'Make a suggestion',
				description: 'We\'re always looking for ways to improve our app. Let us know how we can do better.',
				placeholder: 'Let us know what you would like to see in the app, or how we can improve it.',
			},
		},
	},
	flagContent: {
		title: 'Report Content',
		reasons: {
			offensive: 'Offensive or Inappropriate',
			offensiveDesc: 'Contains profanity, hate speech, or explicit material',
			spam: 'Spam',
			spamDesc: 'Advertising, nonsensical, or duplicate content',
			incorrect: 'Incorrect Information',
			incorrectDesc: 'Nutritional data is clearly wrong or misleading',
			other: 'Other',
			otherDesc: 'Something else not listed above',
		},
		submit: 'Submit Report',
		otherTitle: 'Describe the Issue',
		otherPlaceholder: 'Describe the issue...',
		success: 'Report submitted. Thank you!',
		error: 'Failed to submit report. Please try again.',
		menuButton: 'Report',
	},
	helpPage: {
		title: 'Help',
		placeholders: {
			searchKnowledgeBase: 'What do you need help with?',
		},
		messages: {
			noMatchesTitle: 'No Search Results Found',
			noMatchesSubtext: 'Can\'t find what you\'re looking for? Try adjusting your search, or reach out via <a href="mailto:support@mh-physique.app">emailing support</a> or <a href="https://community.mh-physique.app/" target="_blank">joining the community</a> to get help from our team and other users.',
		},
		faq: {
			title: 'Frequently Asked Questions',
		},
	},
	labelScanner: {
		title: 'Scan Label',
		noNutrientsTitle: 'No Nutrition Info Found',
		noNutrients: 'No nutrition info detected. Check under flaps, on the bottom, or on the outer box — then take another photo.',
		noNutrientsBlurry: 'Photo is too blurry or dark to read. Try retaking with better lighting and hold steady.',
		noNutrientsPartial: 'Only partial nutrition info found. The full label may be under a flap, on the bottom, or on the outer box.',
		retakeButton: 'Retake',
		cameraErrorTitle: 'Camera Unavailable',
		error: 'Failed to analyze label. Please try again.',
		quotaTitle: 'Scan Limit Reached',
		cameraPermissionDenied: 'Camera access is required to scan labels.',
		modelsAgree: '{count} model(s) agree',
		acceptResult: 'Accept This Result',
		labelType: 'Label Type',
		confidence: 'Confidence',
		jsonRepaired: 'Response was truncated and auto-repaired — verify nutrients',
		doneButton: 'Done',
		photoHintExtract: 'The more you capture, the better — nutrition, front of package, ingredients.',
		photoHintImage: 'The best photo becomes the food image.',
		providerErrorTitle: 'Service Temporarily Unavailable',
		providerErrorMessage: 'The scan service is temporarily unavailable. Please try again in a moment, or contact support if this keeps repeating.',
		incompleteTitle: 'Scan Didn\'t Finish',
		incompleteMessage: 'The scan didn\'t finish. Please try again — your photos are still here.',
		quotaAccountLocked: 'Label scanning is temporarily disabled for your account.',
		quotaRateLimited: 'Too many scans in a short time. Please wait a moment.',
		quotaDailyCooldown: 'Too many unsuccessful scans today. Please try again tomorrow.',
		quotaQualityWarning: 'Most of your scans are failing — check image quality and contents.',
		retryingDifferentServer: 'Having trouble — trying a different server...',
		offlineTitle: 'You\'re Offline',
		offlineMessage: 'Connect to the internet and try again.',
		statusReadingPackaging: 'Reading packaging...',
		statusExtractingProductInfo: 'Extracting product info...',
		statusAnalyzingNutrients: 'Analyzing nutrients...',
		statusCheckingIngredients: 'Checking ingredients...',
		statusDetectingServingSizes: 'Detecting serving sizes...',
		statusAlmostThere: 'Almost there...',
	},
	mealPhotoScanner: {
		title: 'Scan Meal',
		analyzeButton: 'Analyze Meal',
		analyzing: 'Identifying foods...',
		retryingDifferentServer: 'Retrying with a different model...',
		addToMeal: 'Add {count} Food(s) to Meal',
		replaceInMeal: 'Replace with {count} Food(s)',
		noFoodsDetected: 'No food detected in this photo. Try taking a clearer photo of your meal.',
		noFoodsBlurry: 'Photo is too blurry or dark. Try retaking with better lighting.',
		barcodeDetectedTitle: 'Barcode Detected',
		barcodeDetectedMessage: 'This looks like a product barcode, not a plate of food. Scan the barcode to log this packaged product instead.',
		nutritionLabelDetectedTitle: 'Nutrition Label Detected',
		nutritionLabelDetectedMessage: 'This looks like a product\'s nutrition label, not a plate of food. Scan the label to log this packaged product instead.',
		scanBarcodeButton: 'Scan Barcode',
		scanLabelButton: 'Scan Label',
		retakeButton: 'Retake',
		changeButton: 'Change',
		searchButton: 'Search',
		addFoodButton: 'Add Food',
		errorTitle: 'Analysis Failed',
		error: 'Failed to analyze meal photo. Please try again.',
		estimatedGrams: '{grams}g estimated',
		estimatedToMeasured: '{estimated}g estimated → {measured}g measured',
		addedGrams: '{grams}g added by you',
		quotaTitle: 'Scan Limit Reached',
		quotaAccountLocked: 'Meal scanning is temporarily disabled for your account.',
		quotaRateLimited: 'Too many scans in a short time. Please wait a moment.',
		quotaDailyCooldown: 'Too many unsuccessful scans today. Please try again tomorrow.',
		cameraPermissionDenied: 'Camera access is required to scan meals.',
	},
	equipmentScanner: {
		title: 'Scan Equipment',
		analyzeButton: 'Analyze',
		retakeButton: 'Retake',
		photoHintCapture: 'Capture from multiple angles for the best result.',
		photoHintLabel: 'Make sure printed weight numbers are readable.',
		analyzingReadingPlates: 'Reading weights...',
		analyzingMatchingType: 'Matching equipment type...',
		analyzingFinalising: 'Finalising...',
		retryingDifferentServer: 'Retrying with a different model...',
		cameraPermissionDenied: 'Camera access is required to scan equipment.',
		errorTitle: 'Scan Failed',
		errorCamera: 'The camera failed to start. Please try again.',
		errorNetwork: 'Could not reach the scanner. Check your connection and try again.',
		errorGeneric: 'Failed to analyze the equipment. Please try again.',
		errorIncomplete: 'The scan didn\'t finish. Please try again — your photos are still here.',
		errorBadImage: 'Image was rejected. Make sure photos are clear and not too large.',
		errorUnsupportedType: 'This equipment type cannot be scanned.',
		noEquipmentDetected: 'No equipment detected in the photos. Try clearer angles or better lighting.',
		noEquipmentBlurry: 'Photos are too blurry or dark. Try retaking with better lighting.',
		noEquipmentMixed: 'The photos appear to show different pieces of equipment. Please retake the photos of the SAME equipment from different angles.',
		lowConfidenceTooltip: 'The AI wasn\'t sure about this value — it may have been interpolated or hard to read. Double-check before applying.',
		confidenceTooltip: 'How confident the AI is in this reading. Values below 70% are flagged with a warning.',
		types: {
			barbell: 'Barbell',
			dumbbells: 'Dumbbells',
			plates: 'Free plates',
			kettlebells: 'Kettlebells',
			cableMachine: 'Cable machine',
			unknown: 'Unknown',
		},
		detectedHeader: 'Detected ({count} items)',
		applyModeHeader: 'How to apply',
		applyMode: {
			replace: 'Replace',
			replaceHelp: 'Overwrite all existing values with the checked detected values.',
			merge: 'Merge',
			mergeHelp: 'Add detected values that aren\'t already present (within 0.05 kg).',
			append: 'Append',
			appendHelp: 'Add all checked detected values to the existing list (duplicates allowed).',
		},
		barGroup: 'Bar',
		collarGroup: 'Collar',
		platesGroup: 'Plates',
		microPlatesGroup: 'Micro Plates',
		currentValues: {
			showStack: 'Show resulting stack ({count} increments)',
			headerStack: 'Resulting stack ({count} increments)',
			showPlates: 'Show resulting plates ({count} plates)',
			headerPlates: 'Resulting plates ({count} plates)',
			showBar: 'Show resulting bar setup ({count} plates)',
			headerBar: 'Resulting bar setup ({count} plates)',
			showFixed: 'Show resulting weights ({count})',
			headerFixed: 'Resulting weights ({count})',
		},
		afterApply: 'After apply: {count} item(s)',
		usingChecked: 'Using {checked} of {total} detected',
		typeMismatchBanner: 'Detected {detected}, but this equipment is a {current}.',
		unknownTypeBanner: 'We couldn\'t confidently identify the equipment type in the photos.',
		quotaTitle: 'Scan Limit Reached',
		quotaAccountLocked: 'Equipment scanning is temporarily disabled for your account.',
		quotaRateLimited: 'Too many scans in a short time. Please wait a moment.',
		quotaDailyCooldown: 'Too many unsuccessful scans today. Please try again tomorrow.',
	},
	workoutPlanScanner: {
		title: 'Scan Workout',
		analyzeButton: 'Analyze',
		retakeButton: 'Retake',
		photoHintCapture: 'Photograph or screenshot your workout. One training day per image works best.',
		photoHintBatch: 'Up to 5 images per scan. For longer programs, scan in batches and append.',
		analyzingReadingWorkout: 'Reading the workout...',
		analyzingMatchingExercises: 'Matching exercises...',
		analyzingFinalising: 'Finalising...',
		retryingDifferentServer: 'Retrying with a different model...',
		cameraPermissionDenied: 'Camera access is required to scan a workout. You can also upload screenshots with the + button.',
		errorTitle: 'Scan Failed',
		errorCamera: 'The camera failed to start. Please try again.',
		errorNetwork: 'Could not reach the scanner. Check your connection and try again.',
		errorGeneric: 'Failed to analyze the workout. Please try again.',
		errorIncomplete: 'The scan didn\'t finish. Please try again — your photos are still here.',
		errorApply: 'Something went wrong while adding the scanned workout. Some of it may have been added — check your workouts below, or cancel Edit Workouts to discard the changes.',
		errorBadImage: 'Image was rejected. Make sure photos are clear and not too large.',
		noWorkoutDetected: 'No workout found in the images. Try clearer photos or screenshots.',
		noWorkoutBlurry: 'Images are too blurry or dark to read. Try retaking with better lighting.',
		quotaTitle: 'Scan Limit Reached',
		quotaAccountLocked: 'Workout scanning is temporarily disabled for your account.',
		quotaRateLimited: 'Too many scans in a short time. Please wait a moment.',
		quotaDailyCooldown: 'Too many unsuccessful scans today. Please try again tomorrow.',
		detectedSummary: 'Detected {days} day(s) with {exercises} exercise(s).',
		willCreateCustomSummary: '{count} exercise(s) will be created as new custom exercises when you apply.',
		willCreateCustom: 'This exercise is not in the app\'s exercise database. It will be created as a new custom exercise with the scanned name.',
		willCreateCustomSimilar: 'This exercise is not in the app\'s exercise database. It will be created as a new custom exercise with the scanned name, set up as similar to {name}.',
		useScannedButton: 'Keep Scanned Name',
		useSuggestionButton: 'Use Suggestion',
		changeButton: 'Change',
		resolveButton: 'Resolve',
		scannedAs: 'Scanned as "{name}"',
		suggestedAs: 'Suggested "{name}"',
		customBadge: 'Custom',
		matchedBadge: 'Matched',
		matchedBadgeTooltip: 'This is the same exercise as the scanned one, just possibly under a different name in the app\'s exercise database.',
		suggestedMatchBadge: 'Suggested',
		suggestedMatchTooltip: 'This is not the scanned exercise, but a similar one from the app\'s exercise database, suggested as a replacement.',
		alreadyInPlan: 'Already in your plan — applying will update its sets and reps everywhere it is used.',
		setsLabel: 'Sets',
		targetRepsLabel: 'Target Reps',
		applyModeHeader: 'How to apply',
		applyModeMessage: 'Append adds the scanned day(s) to your current workouts. Replace removes your current workouts first. Nothing is saved until you save the Edit Workouts page.',
		applyingLoading: 'Adding to your plan…',
		applyMode: {
			append: 'Append',
			replace: 'Replace',
		},
	},
	recipeScanner: {
		title: 'Scan Recipe',
		emptyRecipeHint: 'Have a recipe to hand? Scan a photo and we\'ll fill in the ingredients for you.',
		analyzeButton: 'Analyze',
		retakeButton: 'Retake',
		analyzing: 'Reading recipe...',
		retryingDifferentServer: 'Retrying with a different model...',
		photoHintCapture: 'Snap a recipe card, cookbook page, screen, or note.',
		photoHintBatch: 'Up to 4 photos — one per page.',
		photoHintImage: 'A photo of the finished dish becomes the recipe image.',
		cameraPermissionDenied: 'Camera access is required to scan a recipe. You can also upload photos with the + button.',
		nameLabel: 'Recipe Name',
		namePlaceholder: 'Recipe name',
		imageLabel: 'Recipe Image',
		servingsLabel: 'Makes {count} serving(s)',
		instructionsLabel: 'Instructions',
		useRecipeButton: 'Use {count} Ingredient(s)',
		changeButton: 'Change',
		searchButton: 'Search',
		estimatedGrams: '{grams}g estimated',
		estimatedToMeasured: '{estimated}g estimated → {measured}g measured',
		errorTitle: 'Scan Failed',
		error: 'Failed to read the recipe. Please try again.',
		noRecipeDetected: 'No recipe found in these photos. Try clearer photos of the full recipe.',
		noRecipeBlurry: 'Photos are too blurry or dark to read. Try retaking with better lighting.',
		quotaTitle: 'Scan Limit Reached',
		quotaAccountLocked: 'Recipe scanning is temporarily disabled for your account.',
		quotaRateLimited: 'Too many scans in a short time. Please wait a moment.',
		quotaDailyCooldown: 'Too many unsuccessful scans today. Please try again tomorrow.',
	},
	cameraPermission: {
		title: 'How to Enable Camera',
		nativeAndroidStep1: 'Open your phone\'s Settings app',
		nativeAndroidStep2: 'Tap Apps, then MH Physique',
		nativeAndroidStep3: 'Tap Permissions, then Camera',
		nativeAndroidStep4: 'Choose "Allow only while using the app"',
		nativeAndroidStep5: 'Return here and open the scanner again',
		nativeIosStep1: 'Open the Settings app on your device',
		nativeIosStep2: 'Scroll down and tap MH Physique',
		nativeIosStep3: 'Turn on Camera',
		nativeIosStep4: 'Return here and open the scanner again',
		webStep1: 'Click on the lock icon next to the address bar',
		webStep2: 'Find "Camera"',
		webStep3: 'Change the permission to "Allow"',
		webStep4: 'Close and reopen this screen',
		webIosStep1: 'Open the Settings app on your device',
		webIosStep2: 'Scroll down and tap Safari',
		webIosStep3: 'Tap Camera (under "Settings for Websites")',
		webIosStep4: 'Select Ask or Allow option',
		webIosStep5: 'Return here and reload the page',
		webIosOtherBrowserStep1: 'Open the Settings app on your device',
		webIosOtherBrowserStep2: 'Scroll down and tap the browser you are using, such as Chrome or Firefox',
		webIosOtherBrowserStep3: 'Turn on Camera',
		webIosOtherBrowserStep4: 'Return here and reload the page',
		macSafariStep1: 'In the Safari menu, choose "Settings for This Website"',
		macSafariStep2: 'Set Camera to "Allow"',
		macSafariStep3: 'Reload this page',
		macSafariStep4: 'Still blocked? Open System Settings, then Privacy & Security, then Camera, and turn on Safari',
	},
	barcodeScanner: {
		title: 'Scan Barcode',
		tabs: {
			scan: 'Scan',
			enterManually: 'Enter Manually',
		},
		manualEntry: {
			title: 'Enter Barcode',
			findFood: 'Find Food',
		},
		messages: {
			cameraPermissionDenied: 'You will need to allow the app to use your camera for this feature to work properly.',
			initializingCamera: 'Initializing camera',
			cannotInitializeCamera: 'Cannot initialize camera.',
			noBarcodeFound: 'No barcode found? Try {remedies}.',
		},
		remedies: {
			betterLighting: 'better lighting',
			differentDistance: 'different distance',
			enablingFlash: 'enabling flash',
			zoomingInOut: 'zooming in/out',
			switchingCameras: 'switching cameras',
		},
	},
	helpCard: {
		title: 'Information About...',
	},
	changelog: {
		title: 'Changelog',
		comingSoon: 'Coming Soon',
	},
	sso: {
		loggingIntoCommunity: 'Signing in to community...',
		communityForSubscribersOnly: 'The Community is only for subscribers. Subscribe to join the discussion.',
	},
	nutrients: {
		names: {
			energy: 'Energy',
			protein: 'Protein',
			fat: 'Fat',
			carbohydrates: 'Carbohydrates',
			fiber: 'Fiber',
			cholesterol: 'Cholesterol',
			fatSaturated: 'Fat, saturated',
			fatMonounsaturated: 'Fat, monounsaturated',
			fatPolyunsaturated: 'Fat, polyunsaturated',
			omega3: 'Omega-3',
			omega6: 'Omega-6',
			omega9: 'Omega-9',
			transsaturatedFat: 'Transsaturated fat',
			carbohydratesTotal: 'Carbohydrates, Total',
			sugarsTotal: 'Sugars, total',
			sugarAdded: 'Sugar, added',
			starch: 'Starch',
			saltNacl: 'Salt (NaCl)',
			alcohol: 'Alcohol',
			water: 'Water',
			caffeine: 'Caffeine',
			theobromine: 'Theobromine',
			calciumCa: 'Calcium (Ca)',
			ironFe: 'Iron (Fe)',
			magnesiumMg: 'Magnesium (Mg)',
			phosphorusP: 'Phosphorus (P)',
			potassiumK: 'Potassium (K)',
			sodiumNa: 'Sodium (Na)',
			zincZn: 'Zinc (Zn)',
			copperCu: 'Copper (Cu)',
			fluorideF: 'Fluoride (F)',
			manganeseMn: 'Manganese (Mn)',
			seleniumSe: 'Selenium (Se)',
			iodineI: 'Iodine (I)',
			vitaminAIu: 'Vitamin A (IU)',
			retinol: 'Retinol',
			vitaminARae: 'Vitamin A RAE',
			caroteneBeta: 'Carotene, beta',
			caroteneAlpha: 'Carotene, alpha',
			vitaminEAlphaTocopherol: 'Vitamin E (alpha-tocopherol)',
			vitaminD: 'Vitamin D',
			vitaminD2Ergocalciferol: 'Vitamin D2 (ergocalciferol)',
			vitaminD3Cholecalciferol: 'Vitamin D3 (cholecalciferol)',
			vitaminCTotalAscorbicAcid: 'Vitamin C, total ascorbic acid',
			thiaminVitaminB1: 'Thiamin (vitamin B-1)',
			riboflavinVitaminB2: 'Riboflavin (vitamin B-2)',
			niacinVitaminB3: 'Niacin (vitamin B-3)',
			pantothenicAcid: 'Pantothenic acid',
			vitaminB6: 'Vitamin B-6',
			folateTotal: 'Folate, total',
			vitaminB12: 'Vitamin B-12',
			cholineTotal: 'Choline, total',
			vitaminKPhylloquinone: 'Vitamin K (phylloquinone)',
			folicAcid: 'Folic acid',
			ash: 'Ash',
			biotinVitaminH: 'Biotin (vitamin H)',
			chromiumCr: 'Chromium (Cr)',
			molybdenumMo: 'Molybdenum (Mo)',
			fattyAcidsLinoleic: 'Fatty acids, linoleic',
			fattyAcidsAlphaLinolenic: 'Fatty acids, alpha-linolenic',
			epa: 'EPA',
			dha: 'DHA',
			fattyAcidsArachidonic: 'Fatty acids, arachidonic',
			sorbitol: 'Sorbitol',
			maltitol: 'Maltitol',
			mannitol: 'Mannitol',
			isomalt: 'Isomalt',
			lactitol: 'Lactitol',
			xylitol: 'Xylitol',
			erythritol: 'Erythritol',
			polyolsSugarAlcoholsTotal: 'Polyols (sugar alcohols), total',
		},
		short: {
			energy: 'Cal',
			protein: 'Pro',
			fat: 'Fat',
			carbohydrates: 'Carb',
			fiber: 'Fib',
			cholesterol: 'Chol',
		},
		threeLetter: {
			energy: 'Cal',
			protein: 'Pro',
			fat: 'Fat',
			carbohydrates: 'Cho',
			fiber: 'Fib',
		},
	},
	authRoles: {
		unknown: {
			name: 'Free',
			userExplanation: 'Your subscription is not currently active. A premium subscription is required to access all training and meal planning features.',
		},
		paid: {
			name: 'Premium',
			userExplanation: 'As a Premium user, you have access to all the features, including automatic fitness program and meal planning.',
		},
		vip: {
			name: 'VIP',
			userExplanation: 'As a VIP user, you have access to all the Premium features, including automatic fitness program and meal planning. This is a complimentary recurrent subscription manually managed by the MH Physique staff.',
		},
		mod: {
			name: 'Moderator',
			userExplanation: 'As a Moderator, you have access to all Premium features, and can also moderate community contributions. This is a complimentary recurrent subscription manually managed by the MH Physique staff.',
		},
		dev: {
			name: 'Developer',
			userExplanation: 'This is a basic developer license, contact your production manager for details on what that entails.',
		},
		officer: {
			name: 'Officer',
			userExplanation: 'As an Officer, you can edit operating parameters of the system (workout and meal solvers, official exercises, etc.), moderate community contributions and also edit and delete users.',
		},
		serverdev: {
			name: 'Server Dev',
			userExplanation: 'As a Server Developer, you don\'t need an explanation of what this means.',
		},
	},
	exerciseStates: {
		active: {
			title: 'Active',
			description: 'This exercise is currently a part of your program.',
		},
		plateaued: {
			title: 'Plateaued',
			description: 'You\'ve reached a plateau (a temporary peak of performance) in this exercise. It will be used again at a later time, when your progress will demand it.',
		},
		unsuitable: {
			title: 'Unsuitable',
			description: 'We have found this exercise was suboptimal for you. It will be used again at a later time, when your progress will demand it.',
		},
		avoided: {
			title: 'Avoided',
			description: 'You requested to switch this exercise for an alternative, because of an injury or lack of equipment. We will recommend it again soon to see if the situation has changed.',
		},
		ready: {
			title: 'Ready',
			description: 'You have used or attempted this exercise before. It is now ready again and waiting to be used as soon as needed.',
		},
		unused: {
			title: 'Unused',
			description: 'We didn\'t suggest this exercise to you yet. We will do so when your progress will require it.',
		},
	},
	exerciseStats: {
		setNumber: '{number}',
		importantNotice: 'Important notice',
		exerciseProgress: 'Exercise Progress',
		log: 'Log',
		exportAsCsv: 'Export as CSV',
		buttons: {
			allow: 'Allow',
			shareLink: 'Share Link',
			stopSharing: 'Stop Sharing',
		},
		toasts: {
			linkCopied: 'Link Copied to Clipboard',
		},
		tabs: {
			log: 'History',
			instructions: 'Instructions',
			settings: 'Settings',
		},
		settings: {
			equipment: 'Equipment',
			notSelectedYet: 'Not selected yet',
			myoReps: 'Myo-Reps',
			myoRepsTooltip: 'Myo-Reps use one main set followed by short-rest mini-sets for faster, effective muscle growth.',
			myoRepsLearnMore: 'Learn More',
			useMyoReps: 'Use',
			useMyoRepsNote: 'Use Myo-Reps whenever possible to make workouts faster while still effective. If an exercise isn\'t a good fit, the app will default to straight sets.',
			dontUseMyoReps: 'Don\'t Use',
			dontUseMyoRepsNote: 'Never use Myo-Reps for this exercise. Training will rely on traditional straight sets.',
			autoMyoReps: 'Automatic',
			autoMyoRepsNote: 'Automatically determine whether to use Myo-Reps for this exercise based on the overall goal preferences and current progress.',
			rmTesting: 'Rep Max (RM) Testing',
			rmTestingTooltip: 'RM testing uses a heavy 2–5-rep set with good form to gauge strength. The app then estimates your 1RM and picks your starting weight.',
			rmTestingLearnMore: 'Learn more',
			useRMTesting: 'Use',
			useRMTestingNote: 'Always use RM testing for this exercise. You\'ll test your 2–5 rep max so the app can estimate your strength and set the right weights.',
			dontUseRMTesting: 'Don\'t Use',
			dontUseRMTestingNote: 'Never use RM testing for this exercise. The app will rely on your logged performance instead.',
			autoRMTesting: 'Auto',
			autoRMTestingNote: 'Automatically determine whether to use RM testing from your general setting.',
		},
		options: {
			exerciseStatus: 'Exercise Status',
		},
		alerts: {
			stopAvoidingTitle: 'Stop avoiding',
			stopAvoidingMessage: 'Did you change your mind? This will not immediately start using this exercise in your plan, but will make it available in further planning or switching.',
		},
		filters: {
			weight: 'Weight',
			oneRM: '1 RM',
			tonnage: 'Tonnage',
			includeRMTests: 'Include RM tests',
		},
		emptyState: {
			noProgressData: 'No Progress Data',
			noProgressDataSubtext: 'Your exercise data will display here once you have performed the movement.',
			noLogHistory: 'No Log History',
			noLogHistorySubtext: 'Your logged exercises and notes will display here once you have performed the movement.',
			noDataInRange: 'No Data in Range',
			noDataInRangeSubtext: 'No exercise data found for the selected date range. Try selecting a different time period.',
			loadFailed: 'Couldn\'t load exercise data',
			loadFailedSubtext: 'Something went wrong while loading. Please try again.',
		},
		logTable: {
			date: 'DATE',
			loggedSets: 'LOGGED SETS',
			notes: 'NOTES',
		},
		logDetail: {
			sets: 'Sets',
			notes: 'Notes',
		},
		chart: {
			weight: 'Weight',
			oneRMEstimate: '1RM estimate',
			tonnage: 'Tonnage',
			weightAxis: 'Weight ({unit})',
			oneRMAxis: '1RM ({unit})',
			tonnageAxis: 'Tonnage ({unit})',
		},
	},
	trendChart: {
		last7Days: 'Last 7 Days',
		seeAll: 'See All',
		noData: 'No Data',
	},
	markdownEditor: {
		buttons: {
			unlink: 'Unlink',
		},
		placeholders: {
			url: 'URL',
		},
	},
	planHandler: {
		macrosOnTarget: 'Macros on target',
	},
	programSettings: {
		title: 'Program Settings',
		tabs: {
			training: 'Training',
			nutrition: 'Nutrition',
		},
		settings: {
			title: 'Settings',
		},
		advancedTraining: {
			title: 'Advanced Training Methods',
			edit: 'Edit',
		},
		moreSettings: {
			toggle: 'More Settings',
			info: 'Changes here may suggest program optimizations. You can always keep your current workouts.',
		},
		schedule: {
			title: 'Schedule',
			frequency: 'Training Frequency',
			days: 'Training Days',
			daysPerWeek: 'days per week',
			hoursPerNight: 'hours per night',
			sleep: 'Sleep',
		},
		lifestyle: {
			title: 'Lifestyle',
			activityLevel: 'Activity Level',
			stressLevel: 'Stress Level',
		},
		volume: {
			title: 'Training Volume',
			priority: 'Physique Priority',
			setsPerWeek: 'Sets Per Week Per Muscle Group',
			setsPerWeekCurrentPlanTooltip: 'Your workouts are still built for {volume} sets per week. Use View & Optimize under Muscle Volume to move them toward your target.',
		},
		nutrition: {
			goal: 'Goal',
			primaryGoal: 'Primary Goal',
			schedule: 'Schedule',
			mealFrequency: 'Meal Frequency',
			mealsPerDay: '{count} meals per day',
			mealTimes: 'Meal Times',
			energyBalance: 'Energy Balance',
			calorieSurplus: 'Calorie Surplus',
			calorieDeficit: 'Calorie Deficit',
			energyBalanceCustomText: '{sign}{percent}% ({sign}{adjustment} kcal)',
		},
		buttons: {
			update: 'Update',
			updateProgram: 'Update Program',
			volumeDetails: 'View & Optimize',
		},
	},
	system: {
		messages: {
			recompiling: 'Angular: Recompiling...',
		},
	},
	workoutDetail: {
		volumeAutoregulation: {
			title: 'Volume Autoregulation',
			message: 'Based on your data, in particular your fatigue index{intensityInfo}, we recommend {direction} your set volume for {exerciseName} by 1 set.',
			intensityHighRep: ' in the high-rep session',
			intensityMediumRep: ' in the medium-rep session',
			intensityLowRep: ' in the low-rep session',
			directionIncreasing: 'increasing',
			directionDecreasing: 'decreasing',
			buttons: {
				accept: 'Accept',
				decline: 'Decline',
				decideLater: 'I will decide later',
			},
		},
	},
	cliAuth: {
		allowAccess: 'Allow Access?',
		description: 'MH Physique CLI (GitHub) is requesting permission to:',
		permissions: {
			downloadData: 'Download user database snapshots',
			actOnBehalf: 'Perform actions on your behalf',
		},
		confirmAccess: 'Allow access for {email}?',
		authorizing: 'Authorizing...',
		approved: 'Access Granted',
		approvedDetail: 'You can close this page and return to the terminal.',
		denied: 'Access Denied',
		deniedDetail: 'The request was denied. You can close this page.',
		buttons: {
			deny: 'Deny',
			allow: 'Allow',
		},
		errors: {
			noSession: 'No authorization session found. Please start the CLI tool again.',
			notSignedIn: 'Please sign in to the app first, then try again.',
		},
	},
	muscles: {
		bodyParts: {
			glutes: 'Glute Max',
			glutemed: 'Glute Med',
			quads: 'Quads',
			hams: 'Hamstrings',
			calves: 'Calves (gastrocs and soleus)',
			shoulders: 'Shoulders (all heads)',
			chest: 'Chest',
			lats: 'Lats',
			traps: 'Traps',
			triceps: 'Triceps',
			biceps: 'Biceps',
			abs: 'Abs',
		},
		volumeTable: {
			chest: 'Chest',
			ldelt: 'Side Delts',
			rdelt: 'Rear Delts',
			utraps: 'Upper Traps',
			lats: 'Lats',
			biceps: 'Biceps',
			triceps: 'Triceps',
			erectors: 'Lower Back',
			quads: 'Quads',
			hams: 'Hamstrings',
			glutes: 'Glutes',
			glutemed: 'Glute Med',
			gastrocnemius: 'Calves (Gastrocnemius)',
			soleus: 'Calves (Soleus)',
			abs: 'Abs',
		},
	},
	coach: {
		manageClientOffline: {
			title: 'You\'re Offline',
			message: 'Managing a client needs an internet connection. Connect to the internet and try again.',
		},
		formerCoachBadge: {
			label: 'Former Coach',
			tooltip: 'Authored by your former coach {coachName}. You can keep using it or pick a different version.',
			editPageBody: 'Authored by your former coach {coachName}.',
		},
		detachExerciseNotice: 'Your plan still uses your former coach\'s exercises. Open the picker to switch back to your own version if you\'d like.',
		title: 'Coaching',
		addClient: 'Add Client',
		unnamedClient: 'Unnamed Client',
		clientGoneMessage: 'This client is no longer in your list.',
		slotsRemaining: '{remaining} client slots remaining',
		perSeatSlots: 'Add clients anytime — billed per seat',
		checkout: {
			billingTitle: 'How coaching billing works',
			billingInfo: 'This is a monthly subscription billed per client seat. You start with one seat — your own, which also includes your Premium membership. Each client you coach adds one seat to your monthly bill, and removing a client removes their seat. Seat changes are prorated to your next invoice.',
		},
		emptyState: {
			title: 'No Clients Yet',
		},
		editProgram: 'Edit Program',
		manageClient: 'Manage Client',
		managingBannerLabel: 'Managing',
		backToMyAccount: 'Back to My Account',
		status: {
			pending: 'Pending',
			claimed: 'Active',
		},
		nicknamePrompt: {
			createTitle: 'New Client',
			renameTitle: 'Rename Client',
			message: 'Choose a nickname for your reference. Your client won\'t see this name.',
			placeholder: 'Client Nickname',
			create: 'Create',
			rename: 'Rename',
			cancel: 'Cancel',
		},
		rename: 'Rename Client',
		removeClient: {
			button: 'Remove Client',
			confirmTitle: 'Remove Client',
			confirmMessage: 'This will permanently remove this client slot.',
			confirmMessageClaimed: 'This will disconnect the client from your coaching. They will keep their current program.',
			confirm: 'Remove',
			cancel: 'Cancel',
			removing: 'Removing Client…',
		},
		intakeChoice: {
			title: 'How would you like to start?',
			subtext: 'You can fill out the intake yourself, or send a blank intake to the client and let them answer it on their own.',
			fillMyself: 'I\'ll Fill the Intake',
			fillMyselfSubtext: 'You answer every step and prepare the program. The client gets a link to claim the finished program.',
			sendBlank: 'Send Blank Intake to Client',
			sendBlankSubtext: 'The client claims the link, answers the intake themselves, and you review and finalize the program inside their account.',
		},
		clientFills: {
			invitedTitle: 'Waiting for Client',
			invitedSubtext: 'The invite has been sent. The client will fill the intake on their own account. You\'ll get a notification when they finish.',
			fillMyself: 'Fill the Intake Myself',
			submittedTitle: 'Intake Ready for Review',
			submittedSubtext: 'Your client has finished their intake. Review their answers, set Training Volume and Energy Balance, and generate the program.',
			reviewIntake: 'Review Intake',
		},
		awaiting: {
			headerTitle: 'Your Program',
			title: 'Your coach is finalizing your program',
			subtext: 'We\'ll notify you the moment your program is ready. You can close the app and come back later.',
			subtextWithCoach: '{coachName} is reviewing your intake and putting together your training and nutrition plan. We\'ll notify you the moment it\'s ready. You can close the app and come back later.',
		},
		invite: {
			title: 'Invite Client',
			share: 'Share',
			linkCopied: 'Link Copied',
			linkCopiedMessage: 'The invite link has been copied to your clipboard. Share it with your client to get them started.',
			ok: 'OK',
			description: 'Share this link with your client. They will be able to sign up and receive the program you prepared.',
			creatingLink: 'Creating the invite link. This can take a few seconds.',
			linkFailed: 'We could not create the invitation link.',
		},
		claim: {
			title: 'Coach Invitation',
			description: '{coachName} has prepared a training and nutrition program for you. By accepting, your coach will be able to view your training data and adjust your program.',
			descriptionBlankIntake: '{coachName} has invited you to train with them. Answer a few questions so they can build your training and nutrition program. By accepting, your coach will be able to view your training data and adjust your program.',
			accept: 'Accept',
			overwriteWarning: 'You already have an active program. Accepting will replace your current program. Your logs, foods, exercises, and equipment will be preserved.',
			connected: 'You are now connected to {coachName}.',
			invalidToken: 'This invitation link is invalid or has already been used.',
			couldNotCheckToken: 'We could not check your invitation link just now. Your link is still valid, so please check your connection and try again.',
			couldNotComplete: 'We could not finish setting up your program. Your invitation link is still valid, so please open it again. If it still does not work, contact your coach.',
			connectedSetupUnfinished: 'You are now connected to your coach, but we could not finish copying your program across. Please reopen the app, and contact your coach if your program is still missing.',
			settingUp: 'Setting up your program...',
			activationTimeout: 'Your coach access is taking longer than expected to activate. Please try again in a moment, or contact support if the problem persists.',
		},
		settingsSection: {
			title: 'Coaching',
			coachName: 'Your Coach',
			leaveCoaching: 'Leave Coaching',
		},
		selfDetach: {
			confirmTitle: 'Leave Coaching',
			confirmMessage: 'You will be disconnected from {coachName}. You will keep your training data, food logs, and exercises, but you will lose your subscription access. This cannot be undone.',
			confirmButton: 'Leave',
			successToast: 'You have been disconnected from {coachName}.',
			deleteAccountButton: 'Leave Coaching First',
		},
		deleteBlocked: {
			message: 'You are currently being coached by {coachName}. Please ask your coach to remove you, or contact support, before deleting your data.',
		},
		workoutViewOnly: 'You are viewing this workout as a coach. Workout execution is managed by the client.',
		weighInViewOnly: 'You are viewing this weigh-in as a coach. Weight data is managed by the client.',
		offlineOverlay: {
			title: 'Connection Lost',
			message: 'Waiting to reconnect. All changes will sync when the connection is restored.',
		},
		presence: {
			online: 'Online',
			lastSeen: 'Last seen {time} ago',
			coachViewing: 'Your coach is currently viewing your program',
		},
		checkup: {
			submitToCoach: 'Submit to Coach',
			submittedToast: 'You\'ll be notified when your coach completes the review.',
			willBeSentForReview: 'Your checkup will be sent to your coach for review. You\'ll see any updated targets after your coach reviews it.',
			awaitingReview: 'Waiting for coach review',
			reviewTitle: 'Review Checkup',
			currentTarget: 'Current Target',
			recommendedTarget: 'Recommended Target',
			accept: 'Accept',
			modify: 'Modify',
			decline: 'Decline',
			customCalories: 'Custom Calorie Target',
			reviewActionPrompt: 'Review your client\'s weekly checkup and decide how to adjust the calorie target.',
			kcalDelta: '{sign}{diff} kcal',
			tabDecision: 'Decision',
			tabBodyComposition: 'Body Composition',
			weightChange: 'Weight Change',
			kcalValue: '{value} kcal',
			bfPercent: '~{value}%',
			resolveError: 'Unable to resolve this checkup. Please try again.',
			decisionChoice: {
				accept: 'Accept the App\'s Target',
				acceptHelp: 'Your client\'s plan changes to the app\'s recommendation.',
				modify: 'Set a Different Target',
				modifyHelp: 'Choose the number yourself, in kcal or as a change in percent.',
				decline: 'Keep the Current Target',
				declineHelp: 'Your client\'s plan stays as it is this week.',
			},
		},
		dashboard: {
			refresh: 'Refresh',
			statusOnTrack: 'On Track',
			statusOffTrack: 'Off Track',
			statusUndetermined: 'Undetermined',
			statusUnknown: 'Unknown',
			itemsNone: '—',
			pendingSectionTitle: 'Pending invitations',
			actionItemsSectionTitle: 'Pending action items',
			actionItemCheckupTitle: 'Weekly checkup',
			actionItemStrengthTestTitle: 'Strength test',
			actionItemSubtext: 'Awaiting your review',
			actionItemTimeAgo: '{time} ago',
			handoverIncompleteBadge: 'Setup Incomplete',
			handoverIncompleteExplanation: 'This client accepted your invitation, but their account never finished receiving the program you set up. Open Manage Client to check what they actually have, or ask them to open their invitation link again.',
		},
		billing: {
			title: 'Billing',
			headerAffordance: 'Open billing dashboard',
			refresh: 'Refresh',
			activeSlotsLine: '{count} active client slots',
			runoutLine: 'At your current pace, your credits will run out around {date}.',
			balance: {
				remaining: '{amount} client-months remaining',
				dueWithBilling: '{amount} client-months due',
				locked: 'Account locked',
			},
			history: {
				sectionTitle: 'Statements',
				consumed: 'Consumed {amount} client-months',
				settled: 'Settled',
				unsettled: 'Unsettled — no payment method on file',
				empty: 'No statements yet. You\'ll see one row here on the 3rd of each month.',
			},
			loadError: {
				title: 'Could Not Load Billing',
				subtext: 'We could not load your billing dashboard. Check your connection and try again.',
				retry: 'Retry',
			},
			unmigrated: {
				title: 'Billing Coming Soon',
				subtext: 'Your account is using the previous slot-based grant. We\'ll let you know when the new billing system is activated for you.',
			},
			seat: {
				heading: 'Coaching {count} {{client|clients}}',
				billedMonthly: 'Billed monthly, prorated as you add or remove clients.',
				nextPayment: 'Next payment: {amount} on {date}',
				manageNote: 'Your coaching subscription includes your own plan plus one seat per client. To stop coaching, cancel your coaching subscription.',
			},
			recordedHistory: {
				chartTitle: 'Clients Over Time',
				range6: '6 Months',
				range12: '12 Months',
				rangeAria: 'Range',
				active: 'Active',
				asleep: 'Asleep',
				mostClientsNote: 'The most clients you had in each month.',
				capNote: 'Dashed line: your plan\'s limit each month (now {cap} {{client|clients}}).',
				chartAria: 'Clients per month, {from} to {to}: {values}',
				chartAriaWithCap: 'Clients per month, {from} to {to}: {values}; plan limit {cap}',
				paymentsTitle: 'Past Payments',
				invoice: 'Invoice',
				testBadge: 'Test',
				paddleLine: '{amount} · {seats} client {{seat|seats}}',
				amountOnly: '{amount}',
				storeLine: '{amount} · {cap}-client plan · {store}',
				storeLineNoPlan: '{amount} · {store}',
				appStore: 'App Store',
				googlePlay: 'Google Play',
				receiptsApple: 'Receipts are in your App Store purchase history.',
				receiptsGoogle: 'Receipts are in your Google Play purchase history.',
				emptyHistory: 'No history yet. Changes to your clients are recorded from now on.',
				emptyPayments: 'No payments yet.',
				loadError: 'Your client history couldn\'t be loaded.',
				retry: 'Retry',
				invoiceUnavailable: 'The invoice isn\'t available right now. Try again in a minute.',
				invoiceBlocked: 'Your browser blocked the invoice tab. Allow pop-ups for this site and tap Invoice again.',
			},
		},
		tier: {
			becomeCoach: 'Become a Coach',
			noTiersAvailable: 'Coaching plans aren\'t available right now. Please try again later.',
			tierOption: '{count} Clients · {price}',
			purchaseFailed: 'We couldn\'t complete your purchase. Please try again.',
			purchaseNotYetConfirmed: 'Your purchase went through, but we couldn\'t finish confirming it just now, so your coaching plan may not be active yet. Please don\'t buy again. It may finish activating by itself the next time you open the app. If it still isn\'t active after a couple of days, contact support.',
			crossProviderConflict: 'You already have an active membership on another payment channel. Please cancel it first, then set up coaching here.',
			purchaseSuccess: 'You\'re all set — your coaching plan is now active.',
			alreadyOwned: 'You\'re already on this plan. Use Change Plan to switch to a different one.',
			tierName: '{count} Clients',
			usageLine: 'Using {used} of {cap} client slots',
			changePlan: 'Change Plan',
			changePlanSubtext: 'Upgrade or downgrade how many clients you can coach.',
			changeFailed: 'We couldn\'t change your plan. Please try again.',
			changeNotYetConfirmed: 'Your plan change went through at the store, but we couldn\'t finish confirming it just now, so your new plan may not show yet. Please don\'t try again. It may finish applying by itself the next time you open the app. If your new plan still isn\'t showing after a couple of days, contact support.',
			changeScheduled: 'Your plan change is scheduled and will take effect at the end of your current billing period.',
			changeSuccess: 'Your plan has been updated.',
			pauseWarning: {
				title: 'Some Clients Will Be Paused',
				message: 'This plan covers {cap} {{client|clients}}, and you have more than that right now. If you continue, {count} {{client|clients}} will be paused: {names}. A paused client keeps their account, program and history, but stops getting the Premium access you provide, and you will not be able to open them. You can wake any of them again later, as long as you stay within your plan.',
				confirm: 'Continue',
			},
			pausedNowSuffix: '{count} {{client was|clients were}} paused: {names}. You can wake them from your client list whenever your plan has room.',
			pauseScheduledNotice: {
				title: 'Clients will be paused at renewal',
				body: 'Your plan changes to {cap} {{client|clients}} on {date}. At that point {count} {{client|clients}} will be paused: {names}. Nothing changes before then.',
				bodyNoDate: 'Your plan is changing to {cap} {{client|clients}} at your next renewal. At that point {count} {{client|clients}} will be paused: {names}. Nothing changes before then.',
			},
			crossChannel: {
				appBlock: 'Your membership is managed on the web. You can become a coach from our website.',
				webBlock: 'Your membership is on the {store}. To become a coach, you can buy a coaching plan in the app — or, for a lower price, cancel your store membership and switch to web billing.',
				webBlockPayPro: 'Your membership is still billed through our previous payment provider, so coaching cannot be set up on it yet. Your membership needs to move to our current provider first — please get in touch and we will take care of it for you, without any interruption to your membership.',
			},
		},
		branding: {
			title: 'Branding',
			headerAffordance: 'Customize your branding',
			logoLabel: 'Logo',
			logoHelp: 'Upload your logo. It appears on your clients\' app.',
			brandNameLabel: 'Brand Name',
			brandNamePlaceholder: 'Your business name',
			accentLabel: 'Accent Color',
			accentHelp: 'Pick a preset or enter your own hex color. We build a readable shade family from it automatically.',
			contrastWarning: 'Text may be hard to read on this color. We keep it legible automatically, but a stronger color reads better.',
			previewLabel: 'Preview',
			previewButton: 'Sample Button',
			previewLink: 'Sample link',
			save: 'Save Branding',
			saveConfirmTitle: 'Apply Branding?',
			saveConfirmMessage: 'This branding will be applied to all your clients the next time they open the app.',
			saveSuccess: 'Branding saved.',
			savePartial: 'Branding saved, but {count} client(s) could not be updated. Try saving again to retry them.',
			saveError: 'Could not save branding. Check your connection and try again.',
			reset: 'Reset to Default',
			resetConfirmTitle: 'Remove Branding?',
			resetConfirmMessage: 'This removes your branding for all your clients. Their app goes back to the default MH Physique look the next time they open it.',
			resetSuccess: 'Branding reset to default.',
			resetPartial: 'Branding reset, but {count} client(s) could not be updated. Try resetting again to retry them.',
			resetError: 'Could not reset branding. Check your connection and try again.',
			poweredByMh: 'Powered by MH Physique',
		},
		sleepClient: {
			button: 'Put Client to Sleep',
			confirmTitle: 'Put This Client to Sleep?',
			confirmMessage: 'They keep their account, program and history, and you can wake them at any time. While asleep they go back to the free experience, you cannot open their account, and they no longer take up a place on your plan.',
			confirm: 'Put to Sleep',
			cancel: 'Cancel',
			working: 'Putting client to sleep…',
		},
		wakeClient: {
			button: 'Wake Client',
			confirmTitle: 'Wake This Client?',
			confirmMessage: 'They go back to full access straight away and you can open their account again. They will take up a place on your plan once more.',
			confirm: 'Wake',
			cancel: 'Cancel',
			working: 'Waking client…',
		},
		clientAsleep: {
			badge: 'Asleep',
			badgeSince: 'Asleep for {days} {{day|days}}',
			badgeSinceToday: 'Asleep since today',
			refusedTitle: 'This Client Is Asleep',
			refusedMessage: 'You cannot open a sleeping client\'s account. Wake this client to continue.',
			becausePlanFull: 'Paused because your plan is full',
			becausePlanFullSince: 'Paused for {days} {{day|days}} because your plan is full',
			becausePlanFullToday: 'Paused today because your plan is full',
		},
	},
	pageTitles: {
		login: 'Login',
		fatalError: 'Fatal Error',
		subscribe: 'Subscribe',
		unsubscribe: 'Unsubscribe',
		changelog: 'What\'s new',
		home: 'Home',
		editMeal: 'Meal',
		workoutDetail: 'Workout',
		equipmentEdit: 'Edit Equipment',
		editWeighIn: 'Weigh-in',
		training: 'Training',
		editWorkouts: 'Edit Workouts',
		nutrition: 'Nutrition',
		programSettings: 'Program Settings',
		advancedTrainingMethods: 'Advanced Training Methods',
		nutritionOptions: 'Nutrition Options',
		scheduleTips: 'Schedule Tips',
		equipmentSearch: 'Equipment',
		foods: 'Foods',
		barcodeScanner: 'Scan barcode',
		labelScanner: 'Scan Label',
		mealPhotoScanner: 'Scan Meal',
		equipmentScanner: 'Scan Equipment',
		workoutPlanScanner: 'Scan Workout',
		recipeScanner: 'Scan Recipe',
		mealPlan: 'Meal Plan',
		editFood: 'Food',
		editNutritionPlan: 'Edit Daily Nutrition Goal',
		editIngredient: 'Edit Ingredient',
		exerciseSwitch: 'Exercise Switch',
		editExercise: 'Edit Exercise',
		viewExercise: 'Exercise',
		exerciseConfiguration: 'Exercise Configuration',
		exerciseDiff: 'Update Available',
		muscleVolume: 'Muscle Volume',
		editTrainingGoal: 'Edit Training Goal',
		updatePlanOptions: 'Update Your Program',
		weeklyCheckup: 'Weekly Checkup',
		exerciseStats: 'Exercise Stats',
		progress: 'Progress',
		userProfile: 'User Profile',
		settings: 'Settings',
		appSettings: 'App Settings',
		notificationSettings: 'Notification Settings',
		customizeHomepage: 'Customize Homepage',
		redeemCode: 'Redeem Code',
		referralInvite: 'Invitation',
		promo: 'Sale',
		referralHub: 'Invite a Friend',
		intakeForm: 'Intake',
		introCarousel: 'Intro Carousel',
		awaitingCoach: 'Awaiting Coach',
		helpCard: 'Help',
		mealSuggestions: 'Meal Suggestions',
		note: 'Note',
		purchaseSuccess: 'Subscription Active',
		nutritionInfo: 'Nutrition Info',
		taskCalendar: 'Task Calendar',
		inputCalendar: 'Calendar',
		changeWorkout: 'Change Workout',
		trainingOverview: 'Training Overview',
		sendFeedback: 'Send Feedback',
		sendCoachMessage: 'Message Coach',
		help: 'Help & FAQ',
		cliAuth: 'CLI Authorization',
		coachSettings: 'Coaching',
		coachClientDetail: 'Client Detail',
		coachBilling: 'Coach Billing',
		coachCheckout: 'Become a Coach',
		coachBranding: 'Branding',
		coachClaim: 'Coach Claim',
	},
	coachingTips: {
		scheduleItems: {
			training: 'Training',
			wakeUp: 'Wake up',
			goToSleep: 'Go to sleep',
			weeklyCheckup: 'Weekly checkup',
		},
	},
} satisfies BaseTranslation;

export default en;
